// src/merge.ts
import { deepEqual } from "fast-equals";
function valuesEqual(left, right) {
  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) {
      return false;
    }
    const sortedA = [...left].sort();
    const sortedB = [...right].sort();
    return sortedA.every(
      (value, index) => deepEqual(value, sortedB[index])
    );
  }
  return deepEqual(left, right);
}
function fieldLevelMerge(fields, base, local, remote) {
  const merged = { ...remote };
  const conflicts = [];
  for (const field of fields) {
    const localChanged = !valuesEqual(local[field], base[field]);
    const remoteChanged = !valuesEqual(remote[field], base[field]);
    if (localChanged && remoteChanged) {
      if (valuesEqual(local[field], remote[field])) {
        merged[field] = local[field];
      } else {
        conflicts.push({
          field,
          localValue: local[field],
          remoteValue: remote[field],
          baseValue: base[field]
        });
      }
    } else if (localChanged) {
      merged[field] = local[field];
    }
  }
  return { merged, conflicts };
}

// src/personName.ts
var INSEPARABLE_LETTERS = [
  [/ł/g, "l"],
  [/Ł/g, "L"],
  [/ø/g, "o"],
  [/Ø/g, "O"],
  [/đ/g, "d"],
  [/Đ/g, "D"],
  [/ß/g, "ss"],
  [/æ/g, "ae"],
  [/Æ/g, "Ae"],
  [/œ/g, "oe"],
  [/Œ/g, "Oe"]
];
function normalizePersonName(name) {
  let value = name || "";
  for (const [pattern, replacement] of INSEPARABLE_LETTERS) {
    value = value.replace(pattern, replacement);
  }
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}
function personNameWords(name) {
  const normalized = normalizePersonName(name);
  return normalized ? normalized.split(" ") : [];
}
function isShortFormOf(few, many) {
  if (!few.length || few.length > many.length) {
    return false;
  }
  const rest = [...many];
  for (const word of few) {
    const index = rest.findIndex(
      (candidate) => candidate === word || few.length > 1 && word.length === 1 && candidate.startsWith(word)
    );
    if (index < 0) {
      return false;
    }
    rest.splice(index, 1);
  }
  return true;
}
function namesAreCompatible(left, right) {
  const leftWords = personNameWords(left);
  const rightWords = personNameWords(right);
  return isShortFormOf(leftWords, rightWords) || isShortFormOf(rightWords, leftWords);
}

// src/accountMatching.ts
function accountLabel(username, customName) {
  return (customName ?? "").trim() || username;
}
function matchableAccount(account, profile) {
  return {
    id: account.id,
    name: accountLabel(account.username, profile?.customName),
    crewmeisterName: profile?.crewmeisterName || void 0
  };
}
function onlyOne(candidates) {
  return candidates.length === 1 ? candidates[0] : null;
}
function buildAccountResolver(accounts) {
  const normalized = accounts.map((account) => ({
    account,
    name: normalizePersonName(account.name),
    words: personNameWords(account.name),
    crewmeisterName: normalizePersonName(account.crewmeisterName ?? "")
  }));
  return (name) => {
    const wanted = normalizePersonName(name);
    if (!wanted) {
      return null;
    }
    const words = personNameWords(name);
    const sameName = normalized.filter((entry) => entry.name === wanted);
    if (sameName.length) {
      return onlyOne(sameName)?.account ?? null;
    }
    const sameCrewmeisterName = normalized.filter(
      (entry) => entry.crewmeisterName === wanted
    );
    if (sameCrewmeisterName.length) {
      return onlyOne(sameCrewmeisterName)?.account ?? null;
    }
    const shortForm = normalized.filter(
      (entry) => isShortFormOf(words, entry.words) || isShortFormOf(entry.words, words)
    );
    return onlyOne(shortForm)?.account ?? null;
  };
}
function buildTieredAccountResolver(preferred, fallback) {
  const fromPreferred = buildAccountResolver(preferred);
  const fromFallback = buildAccountResolver(fallback);
  return (name) => fromPreferred(name) ?? fromFallback(name);
}

// src/personFields.ts
var PERSON_FIELDS = {
  project: [
    {
      name: "scientificDirector",
      id: "scientificDirectorId",
      shape: "list"
    },
    { name: "technicalDirector", id: "technicalDirectorId", shape: "list" },
    { name: "technicalAuthor", id: "technicalAuthorId", shape: "list" },
    { name: "scientificAuthor", id: "scientificAuthorId", shape: "list" }
  ],
  diary: [
    { name: "author", id: "authorId", shape: "single" },
    { name: "excavationLeader", id: "excavationLeaderId", shape: "single" },
    { name: "technician", id: "technicianIds", shape: "joined" },
    { name: "staff", id: "staffIds", shape: "joined" }
  ],
  find: [{ name: "excavator", id: "excavatorId", shape: "single" }],
  sample: [{ name: "sampler", id: "samplerId", shape: "single" }],
  photo: [{ name: "photographer", id: "photographerId", shape: "single" }],
  drawingSheet: [{ name: "drafter", id: "drafterId", shape: "list" }],
  findingPlanum: [{ name: "editor", id: "editorId", shape: "single" }],
  findingProfile: [{ name: "editor", id: "editorId", shape: "single" }],
  overallDescription: [{ name: "editor", id: "editorId", shape: "single" }],
  areaDescription: [{ name: "editor", id: "editorId", shape: "single" }],
  // A Gerät row shares this field with a machine's name; it has no link.
  timeEntry: [
    {
      name: "workerName",
      id: "userId",
      shape: "single",
      appliesTo: (doc) => (doc.entryType ?? "worker") === "worker"
    }
  ]
};
function personFieldNames(shape, value) {
  if (shape === "list") {
    return Array.isArray(value) ? value.map((name) => String(name)) : [];
  }
  const text3 = typeof value === "string" ? value : "";
  if (shape === "joined") {
    return text3 ? text3.split(", ") : [];
  }
  return [text3];
}
function personFieldIds(shape, value) {
  if (shape === "single") {
    return [typeof value === "string" ? value : ""];
  }
  return Array.isArray(value) ? value.map((id) => String(id ?? "")) : [];
}
function personFieldValue(shape, names) {
  if (shape === "list") {
    return names;
  }
  if (shape === "joined") {
    return names.join(", ");
  }
  return names[0] ?? "";
}
function fitsShape(shape, name) {
  return shape !== "joined" || !name.includes(",");
}
function withPersonLabels(entityName, record, labelOf) {
  const fields = PERSON_FIELDS[entityName];
  if (!fields) {
    return record;
  }
  const source = record;
  let shown = null;
  for (const field of fields) {
    const names = personFieldNames(field.shape, source[field.name]);
    const ids = personFieldIds(field.shape, source[field.id]);
    let changed = false;
    const labelled = names.map((name, index) => {
      const id = (ids[index] ?? "").trim();
      const label = id ? labelOf(id).trim() : "";
      if (label && label !== name && fitsShape(field.shape, label)) {
        changed = true;
        return label;
      }
      return name;
    });
    if (changed) {
      shown ??= { ...source };
      shown[field.name] = personFieldValue(field.shape, labelled);
    }
  }
  return shown ?? record;
}

// src/personLinks.ts
function resolvePersonUserId(name, resolve) {
  return resolve(name)?.id ?? "";
}
function resolvePersonUserIds(names, resolve) {
  return names.map((name) => resolvePersonUserId(name, resolve));
}
function resolvePersonUserIdAsSelf(name, self, resolve) {
  const trimmed = (name || "").trim();
  if (!trimmed) {
    return "";
  }
  if (self && self.name && namesAreCompatible(self.name, trimmed)) {
    return self.id;
  }
  return resolvePersonUserId(trimmed, resolve);
}
function storedPersonLinks(links) {
  const byName = /* @__PURE__ */ new Map();
  for (const link of links) {
    const key = normalizePersonName(link.name);
    if (key && link.id && !byName.has(key)) {
      byName.set(key, link.id);
    }
  }
  return byName;
}
function compatibleStoredLink(kept, name, taken = /* @__PURE__ */ new Set()) {
  const key = normalizePersonName(name);
  if (!key) {
    return "";
  }
  const fitting = /* @__PURE__ */ new Set();
  for (const [storedName, id] of kept) {
    if (!taken.has(id) && namesAreCompatible(storedName, key)) {
      fitting.add(id);
    }
  }
  return fitting.size === 1 ? [...fitting][0] : "";
}
function resolveWithKept(names, kept, resolve, ruleFirst) {
  const ids = names.map(
    (name) => ruleFirst(name) ? resolve(name) : kept.get(normalizePersonName(name)) || resolve(name)
  );
  const taken = new Set(ids.filter(Boolean));
  return ids.map((id, index) => {
    if (id || ruleFirst(names[index])) {
      return id;
    }
    const fallback = compatibleStoredLink(kept, names[index], taken);
    if (fallback) {
      taken.add(fallback);
    }
    return fallback;
  });
}
function storedPersonLinkList(names, ids) {
  return names.map((name, index) => ({ name, id: ids?.[index] ?? "" }));
}
function resolveKeepingStored(names, stored, resolve, ruleFirst = () => false) {
  return resolveWithKept(
    names,
    storedPersonLinks(stored),
    resolve,
    ruleFirst
  );
}
function distinctPeople(names, ids) {
  const seenIds = /* @__PURE__ */ new Set();
  const seenNames = /* @__PURE__ */ new Set();
  const kept = { names: [], ids: [] };
  names.forEach((name, index) => {
    const id = (ids[index] ?? "").trim();
    const normalized = normalizePersonName(name);
    if (id && seenIds.has(id) || normalized && seenNames.has(normalized)) {
      return;
    }
    if (id) {
      seenIds.add(id);
    }
    if (normalized) {
      seenNames.add(normalized);
    }
    kept.names.push(name);
    kept.ids.push(ids[index] ?? "");
  });
  return kept;
}
function relinkPersonFields(fields, doc, stored, resolveName, app) {
  const relinked = { ...doc };
  for (const field of fields) {
    if (field.appliesTo && !field.appliesTo(doc)) {
      continue;
    }
    const names = personFieldNames(field.shape, doc[field.name]);
    const sentIds = doc[field.id] === void 0 ? [] : personFieldIds(field.shape, doc[field.id]);
    const storedNames = stored ? personFieldNames(field.shape, stored[field.name]) : [];
    const storedIds = stored ? personFieldIds(field.shape, stored[field.id]) : [];
    const storedPairs = storedPersonLinkList(storedNames, storedIds);
    const storedNameOf = new Map(
      storedPairs.filter((pair) => pair.id).map((pair) => [pair.id, normalizePersonName(pair.name)])
    );
    const keepSent = (pair) => {
      const owner = storedNameOf.get(pair.id);
      if (owner === void 0 || owner === normalizePersonName(pair.name)) {
        return true;
      }
      return app === "current" && resolveName(pair.name) === "";
    };
    const sentPairs = names.map((name, index) => ({ name, id: sentIds[index] ?? "" })).filter((pair) => pair.id !== "" && keepSent(pair));
    const leftEmpty = (index) => app === "current" && index < sentIds.length && sentIds[index] === "" && index < storedNames.length && normalizePersonName(storedNames[index]) === normalizePersonName(names[index]);
    const ids = resolveKeepingStored(
      names.map((name, index) => leftEmpty(index) ? "" : name),
      [...sentPairs, ...storedPairs],
      resolveName
    ).map((id, index) => leftEmpty(index) ? "" : id);
    if (field.shape === "single") {
      relinked[field.id] = ids[0] ?? "";
      continue;
    }
    const distinct = distinctPeople(names, ids);
    if (distinct.names.length < names.length) {
      relinked[field.name] = personFieldValue(
        field.shape,
        distinct.names
      );
    }
    relinked[field.id] = distinct.ids;
  }
  return relinked;
}

// src/auth/ability.ts
import {
  AbilityBuilder,
  createMongoAbility
} from "@casl/ability";
import { subject } from "@casl/ability";
var APP_ADMIN_ROLE = "Administrator";
var PRIVILEGED_ROLES = [
  APP_ADMIN_ROLE,
  "Projektmanagement",
  "Personalverwaltung",
  "\xDCbersetzungsverwaltung"
];
function isPrivilegedRole(role) {
  return PRIVILEGED_ROLES.includes(role);
}
function isDelegatableRoleChange(before, after) {
  const beforeSet = new Set(before);
  const afterSet = new Set(after);
  const changed = [
    ...after.filter((role) => !beforeSet.has(role)),
    ...before.filter((role) => !afterSet.has(role))
  ];
  return changed.every((role) => !isPrivilegedRole(role));
}
var SYSTEM_USER_ID = "system";
function isAppAdmin(roles) {
  return roles.includes(APP_ADMIN_ROLE);
}
function resolveAccessibleProjectIds(projects, groups, userId) {
  const memberGroupIds = new Set(
    groups.filter((group) => group.userIds.includes(userId)).map((group) => group.id)
  );
  return projects.filter(
    (project) => project.userIds.includes(userId) || project.groupIds.some((groupId) => memberGroupIds.has(groupId))
  ).map((project) => project.id);
}
var PROJECT_RECORDS = [
  "Finding",
  "FindingPlanum",
  "FindingProfile",
  "OverallDescription",
  "Find",
  "Sample",
  "Photo",
  "DrawingSheet",
  "Diary",
  "Media",
  "MediaLink",
  "MediaGroup"
];
var ROLE_PERMISSIONS = {
  // Runs the projects. Everything except the Synology account import and the
  // account itself, both of which stay with the Administrator.
  Projektmanagement: {
    manage: [
      "Project",
      "Parcel",
      ...PROJECT_RECORDS,
      "AreaDescription",
      "TimeEntry",
      "Group",
      "ProjectGroup",
      "GlossaryTerm",
      "BillingCatalog",
      "BillingStatement"
    ],
    grants: [
      ["refresh", "AdministrativeAreas"],
      // The Crewmeister-Abgleich is office work, and its name mapping
      // belongs on the person — see the action's note above.
      ["mapCrewmeister", "UserProfile"],
      ["markExcavationLead", "UserProfile"],
      ["recordIssuedItems", "UserProfile"],
      // The delegated user administration: hands out every role but the
      // privileged ones (see PRIVILEGED_ROLES).
      ["assignRoles", "UserProfile"],
      ["manageLogin", "User"],
      // Names a person no account knows — see the action's note above.
      ["nameFreely", "Person"],
      // A Befund turned baulicher Bestand or back, after it was created.
      ["update", "FindingType"],
      ["access", "Office"],
      ["access", "UserAdministration"],
      ["access", "ProjectMetadata"],
      ["access", "ProjectSettings"],
      ["access", "WorkerTimeTracking"],
      ["access", "EquipmentTimeTracking"],
      ["access", "ChangeLog"],
      ["access", "RecordHistory"],
      ["access", "CrewRecords"],
      // Nachweise: what is billed — not the Projektassistenz's.
      ["access", "Billing"]
    ]
  },
  // Projektmanagement without the rights that reach into other people's
  // accounts: no roles, no login carriers, no Zeitabgleich, no change log.
  Projektassistenz: {
    manage: [
      "Project",
      "Parcel",
      ...PROJECT_RECORDS,
      "AreaDescription",
      "TimeEntry",
      "Group",
      "ProjectGroup",
      "GlossaryTerm"
    ],
    grants: [
      ["refresh", "AdministrativeAreas"],
      ["nameFreely", "Person"],
      ["access", "Office"],
      ["access", "ProjectMetadata"],
      ["access", "ProjectSettings"],
      ["access", "WorkerTimeTracking"],
      ["access", "EquipmentTimeTracking"],
      // The version lists, but not the cross-project change log above:
      // this role keeps the projects, it does not watch the staff.
      ["access", "RecordHistory"],
      ["access", "CrewRecords"]
    ]
  },
  /**
   * Runs an excavation and answers for its crew's hours. Records everything
   * in its own projects; the project itself (metadata, phases, quotas) is
   * read-only to it, and documentation is not its to delete.
   */
  "Teamleitung (Senior)": {
    scoped: {
      create: [
        "Parcel",
        ...PROJECT_RECORDS,
        "AreaDescription",
        "TimeEntry"
      ],
      update: [
        "Parcel",
        ...PROJECT_RECORDS,
        "AreaDescription",
        "TimeEntry"
      ],
      /**
       * Deliberately narrow: whoever keeps their crew's hours has to be
       * able to clear a wrong entry away, but removing documentation (a
       * Befund, a Foto, a Tagebuch) stays with the office.
       */
      delete: ["Parcel", "Media", "MediaLink", "MediaGroup", "TimeEntry"]
    },
    grants: [
      ["access", "Office"],
      ["access", "ProjectMetadata"],
      ["access", "ProjectSettings"],
      ["access", "WorkerTimeTracking"],
      ["access", "EquipmentTimeTracking"],
      ["access", "CrewRecords"]
    ]
  },
  // Leads in the field only — no office at all, and no Flurstücke, Funde,
  // Fotos or Zeichenblätter. The excavation and its hours, and the Proben
  // taken from it, as the Teamleitung (Senior) does — but not deleting them,
  // nor linking them to Funde.
  "Teamleitung (Junior)": {
    scoped: {
      create: [
        "Finding",
        "FindingPlanum",
        "FindingProfile",
        "OverallDescription",
        "AreaDescription",
        "Sample",
        "Diary",
        "Media",
        "MediaLink",
        "MediaGroup",
        "TimeEntry"
      ],
      update: [
        "Finding",
        "FindingPlanum",
        "FindingProfile",
        "OverallDescription",
        "AreaDescription",
        "Sample",
        "Diary",
        "Media",
        "MediaLink",
        "MediaGroup",
        "TimeEntry"
      ],
      delete: ["MediaGroup", "TimeEntry"]
    }
  },
  /**
   * Documents in the office. The same records as a Teamleitung (Senior) and
   * more freedom to delete them, but no Zeiterfassung of its own. Sees the
   * project metadata to look things up; without `manage Project` the form is
   * read-only.
   */
  "Dokumentationsassistenz (Senior)": {
    scoped: {
      create: ["Parcel", ...PROJECT_RECORDS, "AreaDescription"],
      update: ["Parcel", ...PROJECT_RECORDS, "AreaDescription"],
      delete: [
        "Parcel",
        "OverallDescription",
        "AreaDescription",
        "Find",
        "Sample",
        "Photo",
        "DrawingSheet",
        "Media",
        "MediaLink",
        "MediaGroup"
      ],
      // Projektmanagement and -assistenz hold it through `manage Sample`.
      grants: [["linkToFind", "Sample"]]
    },
    grants: [
      ["access", "Office"],
      ["access", "ProjectMetadata"],
      ["access", "ProjectSettings"],
      // Sees the crew's hours, records none — the Zeiterfassung grids go
      // read-only by themselves, having no `update TimeEntry` to gate on.
      ["access", "WorkerTimeTracking"],
      ["access", "CrewRecords"]
    ]
  },
  // The same work without the deleting, without the Flurstücke and without
  // Phasen and Kontingente; the metadata only to look things up.
  "Dokumentationsassistenz (Junior)": {
    scoped: {
      create: [...PROJECT_RECORDS],
      update: [...PROJECT_RECORDS],
      delete: ["MediaGroup"]
    },
    grants: [
      ["access", "Office"],
      ["access", "ProjectMetadata"],
      ["access", "CrewRecords"]
    ]
  },
  /**
   * Additive: someone who also keeps the teams. Its own role because a team is
   * a group of PEOPLE and carries no project, so "only in my own projects" —
   * the bound every other Teamleitung right has — cannot be expressed for it.
   */
  Teamkoordination: {
    manage: ["Group"],
    // Teams are an office screen, so the role has to reach the office even
    // when it is the only one someone holds.
    grants: [["access", "Office"]]
  },
  /**
   * Additive like Teamkoordination: keeps the Übersetzung screen of the
   * administration — edits the DeepL glossary, hands it to DeepL and sees
   * what the contract has used. Nothing else beyond the global reads.
   */
  \u00DCbersetzungsverwaltung: {
    manage: ["GlossaryTerm"],
    grants: [
      ["access", "Office"],
      ["access", "TranslationSettings"]
    ]
  },
  /**
   * Additive like Teamkoordination: keeps the personnel side of the
   * Benutzerverwaltung — employment dates and type, and the other profile
   * fields the office keeps (Crewmeister name, Grabungsleiter mark, issued
   * e-mail and cards). Hands out no roles and no logins: that stays with
   * Projektmanagement and the Administrator.
   */
  Personalverwaltung: {
    grants: [
      ["access", "Office"],
      ["access", "UserAdministration"],
      ["recordEmployment", "UserProfile"],
      ["recordIssuedItems", "UserProfile"],
      ["mapCrewmeister", "UserProfile"],
      ["markExcavationLead", "UserProfile"]
    ]
  },
  /**
   * Field crew. Records the excavation in the projects they are on; the
   * Funde, Fotos and Zeichenblätter they enter are theirs to write down and
   * the office's to correct.
   */
  Mitarbeiter: {
    scoped: {
      create: [...PROJECT_RECORDS, "TimeEntry"],
      /**
       * No Fund, Foto, Zeichenblatt or Zeiterfassung: those are entered once
       * and put right by whoever answers for them — for the Zeiterfassung
       * the crew lead (see Teamleitung above). The grids gate their inline
       * editing on `update` and so go read-only by themselves.
       */
      update: [
        "Finding",
        "FindingPlanum",
        "FindingProfile",
        "OverallDescription",
        "Diary",
        "Media",
        "MediaLink",
        "MediaGroup"
      ],
      delete: ["MediaGroup"]
    }
  }
};
function defineAbilityFor(ctx) {
  const { can, build } = new AbilityBuilder(createMongoAbility);
  if (isAppAdmin(ctx.roles)) {
    can("manage", "all");
    return build();
  }
  can("read", "all");
  can(["create", "update", "delete"], "Translation");
  const scoped = {
    projectId: { $in: ctx.projectIds }
  };
  for (const role of ctx.roles) {
    const permissions = ROLE_PERMISSIONS[role];
    if (!permissions) {
      continue;
    }
    if (permissions.manage) {
      can("manage", permissions.manage);
    }
    const {
      create,
      update,
      delete: remove,
      grants: scopedGrants
    } = permissions.scoped ?? {};
    if (create) {
      can("create", create, scoped);
    }
    if (update) {
      can("update", update, scoped);
    }
    if (remove) {
      can("delete", remove, scoped);
    }
    for (const [action, grantSubject] of scopedGrants ?? []) {
      can(action, grantSubject, scoped);
    }
    for (const [action, grantSubject] of permissions.grants ?? []) {
      can(action, grantSubject);
    }
  }
  return build();
}

// src/entities/primitives.ts
var NAME_MAX = 255;
var DESCRIPTION_MAX = 2e4;
var TEXT_MAX = 1e6;
var ID_MAX = 36;
var NUMBER_MAX = 50;
var SPATIAL_MAX = 50;
var PERSON_MAX = 100;
var MEASURE_MAX = 30;
var SHORT_TEXT_MAX = 100;
var MEDIUM_TEXT_MAX = 255;
var DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
var MIN_PLAUSIBLE_YEAR = 1900;
var PLAUSIBLE_YEARS_AHEAD = 10;
function isPlausibleYear(year) {
  return Number.isInteger(year) && year >= MIN_PLAUSIBLE_YEAR && year <= (/* @__PURE__ */ new Date()).getUTCFullYear() + PLAUSIBLE_YEARS_AHEAD;
}
function isPlausibleDay(value) {
  const day = value.trim();
  if (day === "") {
    return true;
  }
  return DAY_PATTERN.test(day) && isPlausibleYear(Number(day.slice(0, 4)));
}
function isPlausibleDateTime(value) {
  const raw = value.trim();
  if (raw === "") {
    return true;
  }
  const ms = Date.parse(raw);
  return !Number.isNaN(ms) && isPlausibleYear(new Date(ms).getUTCFullYear());
}
function hasPlausibleYearIfDay(value) {
  return !DAY_PATTERN.test(value.trim()) || isPlausibleDay(value);
}
function hasPlausibleYearIfDateTime(value) {
  return Number.isNaN(Date.parse(value.trim())) || isPlausibleDateTime(value);
}
var MEASURE_VALUE_PATTERN = /^\d+([.,]\d+)?$/;
function isMeasureValue(value) {
  const trimmed = value.trim();
  return trimmed === "" || MEASURE_VALUE_PATTERN.test(trimmed);
}
function rule(code, params) {
  return params ? JSON.stringify({ code, ...params }) : code;
}
function clampString(value, max) {
  const str = typeof value === "string" ? value : value == null ? "" : String(value);
  return str.trim().slice(0, max);
}
function clampStringArray(value, opts = {}) {
  if (!Array.isArray(value)) {
    return [];
  }
  const allowed = opts.values ? new Set(opts.values) : null;
  const max = opts.itemMaxLength ?? Number.MAX_SAFE_INTEGER;
  const out = [];
  for (const item of value) {
    const str = clampString(item, max);
    if (str === "" || allowed && !allowed.has(str) || out.includes(str)) {
      continue;
    }
    out.push(str);
  }
  return out;
}
function clampStringList(value, opts = {}) {
  if (!Array.isArray(value)) {
    return [];
  }
  const max = opts.itemMaxLength ?? Number.MAX_SAFE_INTEGER;
  return value.map((item) => clampString(item, max));
}
function parseStringArray(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === "string");
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value || "[]");
      return Array.isArray(parsed) ? parsed.filter(
        (item) => typeof item === "string"
      ) : [];
    } catch {
      return [];
    }
  }
  return [];
}
function fmtDate(date) {
  return date.toISOString().slice(0, 10);
}
function parseDate(value) {
  return value ? new Date(value) : null;
}
function parseJsonArrayRaw(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

// src/entities/defineEntity.ts
function makeSanitizer(fields) {
  const specs = Object.entries(fields);
  return (doc) => {
    const out = {
      ...doc
    };
    for (const [field, spec] of specs) {
      const value = doc[field];
      if (spec.sanitizeValue) {
        out[field] = spec.sanitizeValue(value);
      } else if (spec.kind === "date") {
        const day = clampString(value, 10);
        out[field] = DAY_PATTERN.test(day) && isPlausibleDay(day) ? day : "";
      } else if (spec.kind === "datetime") {
        const raw = clampString(value, 30);
        const ms = raw ? Date.parse(raw) : NaN;
        out[field] = Number.isNaN(ms) || !isPlausibleDateTime(raw) ? "" : new Date(ms).toISOString();
      } else if (spec.kind === "array") {
        out[field] = clampStringArray(value, {
          itemMaxLength: spec.itemMaxLength,
          values: spec.values
        });
      } else if (spec.kind === "list") {
        out[field] = clampStringList(value, {
          itemMaxLength: spec.itemMaxLength
        });
      } else if (spec.kind === "boolean") {
        out[field] = Boolean(value);
      } else if (spec.kind === "number") {
        const num = Number(value);
        out[field] = Number.isFinite(num) ? num : 0;
      } else {
        out[field] = clampString(
          value,
          spec.maxLength ?? Number.MAX_SAFE_INTEGER
        );
      }
    }
    return out;
  };
}
function migrationProblems(entity) {
  const version = entity.rxSchema.version;
  if (typeof version !== "number" || !Number.isInteger(version)) {
    return [`${entity.name}: rxSchema has no whole-number version`];
  }
  const problems = [];
  for (let step = 1; step <= version; step++) {
    if (!entity.migrations[step]) {
      problems.push(`${entity.name}: no migration step to v${step}`);
    }
  }
  for (const step of Object.keys(entity.migrations).map(Number)) {
    if (!Number.isInteger(step) || step < 1 || step > version) {
      problems.push(
        `${entity.name}: migration step ${step} outside v1..v${version}`
      );
    }
  }
  return problems;
}
function defineEntity(input) {
  const migrations = input.migrations ?? {};
  const declared = input.rxSchema.version;
  const schemaVersion = typeof declared === "number" && Number.isInteger(declared) ? declared : 0;
  const specs = Object.entries(input.fields);
  const fieldNames = (filter) => specs.filter(([, spec]) => filter(spec)).map(([field]) => field);
  const contentFields = fieldNames(() => true);
  const mergeFields = fieldNames((spec) => !!spec.merge);
  const richTextFields = fieldNames((spec) => !!spec.richText);
  const historyFields = input.historyAllFields ? contentFields : fieldNames((spec) => !!spec.history);
  const uniqueFields = fieldNames((spec) => !!spec.unique);
  const conflictFields = [...contentFields, "_deleted"];
  return {
    ...input,
    contentFields,
    mergeFields,
    richTextFields,
    historyFields,
    uniqueFields,
    conflictFields,
    schemaVersion,
    migrations,
    sanitize: input.sanitize ?? makeSanitizer(input.fields)
  };
}

// src/entities/project.ts
import { z as z3 } from "zod";

// src/entities/phase.ts
import { z } from "zod";
var BASE_MEASURE_NUMBER_MAX = 32;
var SUB_MEASURE_MAX = 8;
var PHASE_NUMBER_MAX = 8;
var PHASE_NAME_MAX = 100;
var PHASE_SHORT_TITLE_MAX = 200;
var BASE_MEASURE_NUMBER_PATTERN = /^M-\d{4}-\d{1,4}$/;
var PHASE_NUMBER_MAX_VALUE = 99;
var DIGITS_PATTERN = /^\d+$/;
function isPhaseNumberInRange(value) {
  return DIGITS_PATTERN.test(value) && Number(value) <= PHASE_NUMBER_MAX_VALUE;
}
var PHASE_PRESETS = [
  { number: "1", name: "Oberbodenabtrag" },
  { number: "2", name: "Grabung" },
  { number: "3", name: "Baubegleitung" },
  { number: "4", name: "Prospektion" },
  { number: "5", name: "Dokumentation" }
];
function sanitizePhases(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  const out = [];
  for (const item of value) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const record = item;
    const number = clampString(record.number, PHASE_NUMBER_MAX);
    const name = clampString(record.name, PHASE_NAME_MAX);
    const shortTitle = clampString(
      record.shortTitle,
      PHASE_SHORT_TITLE_MAX
    );
    const rawStart = clampString(record.startDate, 10);
    const startDate = DAY_PATTERN.test(rawStart) ? rawStart : "";
    if (number === "" && name === "") {
      continue;
    }
    out.push({ number, name, startDate, shortTitle });
  }
  return out;
}
function parsePhaseArray(value) {
  if (typeof value === "string") {
    try {
      return sanitizePhases(JSON.parse(value || "[]"));
    } catch {
      return [];
    }
  }
  return sanitizePhases(value);
}
function phaseForDate(phases, recordedAt) {
  if (!phases.length) {
    return null;
  }
  const sorted = [...phases].sort(
    (firstPhase, secondPhase) => firstPhase.startDate.localeCompare(secondPhase.startDate)
  );
  const day = (recordedAt || "").slice(0, 10);
  if (day) {
    let match = null;
    for (const phase of sorted) {
      if (phase.startDate && phase.startDate <= day) {
        match = phase;
      }
    }
    return match ?? sorted[0];
  }
  return sorted[sorted.length - 1];
}
function effectivePhaseNumber(phases, recordedAt, override) {
  if (override) {
    return override;
  }
  return phaseForDate(phases, recordedAt)?.number ?? "";
}
function recordMeasureNumber(project, record) {
  return composeMeasureNumber(
    project.baseMeasureNumber,
    effectivePhaseNumber(
      project.phases,
      record.recordedAt,
      record.phaseNumber
    ),
    project.subMeasure
  );
}
function composeMeasureNumber(base, phaseNumber, sub) {
  if (!base) {
    return "";
  }
  let out = base;
  if (phaseNumber) {
    out += `-${phaseNumber}`;
  }
  if (sub) {
    out += `_${sub}`;
  }
  return out;
}
function fullMeasureNumber(base, phaseNumber, sub) {
  if (!base) {
    return "";
  }
  let out = `${base}-${phaseNumber || "X"}`;
  if (sub) {
    out += `_${sub}`;
  }
  return out;
}
function recordMeasureParts(project, record) {
  const number = effectivePhaseNumber(
    project.phases,
    record.recordedAt,
    record.phaseNumber ?? ""
  );
  const phase = project.phases.find((candidate) => candidate.number === number) ?? null;
  return {
    measureNumber: fullMeasureNumber(
      project.baseMeasureNumber,
      number,
      project.subMeasure
    ),
    shortTitle: phase?.shortTitle || project.shortTitle,
    measureName: phase?.name ?? ""
  };
}
function projectMeasureParts(project) {
  return recordMeasureParts(project, { recordedAt: "" });
}
function splitMeasureNumber(measureNumber) {
  const value = (measureNumber || "").trim();
  const match = /^(M-\d{4}-\d{1,4})(?:-([^_]+))?(?:_(\d{1,3}))?$/.exec(value);
  if (!match) {
    return { base: "", phaseNumber: "", sub: "" };
  }
  return {
    base: match[1],
    phaseNumber: match[2] ?? "",
    sub: match[3] ?? ""
  };
}
var phaseEntrySchema = z.object({
  // 0…99, digits only. Two checks rather than one pattern, so the message
  // names the actual problem: 'A' is not digits, '100' is out of range.
  number: z.string().trim().max(PHASE_NUMBER_MAX, rule("maxLength", { max: PHASE_NUMBER_MAX })).refine(
    (value) => value === "" || DIGITS_PATTERN.test(value),
    rule("digits")
  ).refine(
    (value) => value === "" || Number(value) <= PHASE_NUMBER_MAX_VALUE,
    rule("maxValue", { max: PHASE_NUMBER_MAX_VALUE })
  ).default(""),
  name: z.string().trim().max(PHASE_NAME_MAX, rule("maxLength", { max: PHASE_NAME_MAX })).default(""),
  startDate: z.string().trim().refine(
    (value) => value === "" || DAY_PATTERN.test(value),
    rule("dateFormat")
  ).refine(hasPlausibleYearIfDay, rule("implausibleYear")).default(""),
  shortTitle: z.string().trim().max(
    PHASE_SHORT_TITLE_MAX,
    rule("maxLength", { max: PHASE_SHORT_TITLE_MAX })
  ).default("")
});

// src/entities/crew.ts
var CREW_NAME_MAX = 150;
var CREW_ROLE_MAX = 100;
var CREW_KEY_MAX = 160;
var CREW_ROLES_MAX = 20;
var CREW_ROLE_PRESETS = [
  "Wissenschaftliche Grabungsleitung",
  "Technische Grabungsleitung",
  "Hilfsarbeiter",
  "Vermessung",
  "Grabungsfacharbeiter",
  "Dokumentationsassistenz",
  "Anthropologe",
  "Geoarch\xE4ologe",
  "Arch\xE4ozoologe"
];
function crewPersonKey(name) {
  const normalized = (name || "").trim().replace(/\s+/g, " ").toLowerCase();
  return normalized ? `n:${normalized}` : "";
}
function sanitizeCrew(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const item of value) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const record = item;
    const name = clampString(record.name, CREW_NAME_MAX);
    const rawKey = clampString(record.personKey, CREW_KEY_MAX);
    const personKey = rawKey || crewPersonKey(name);
    if (!personKey || seen.has(personKey)) {
      continue;
    }
    const roles = Array.isArray(record.roles) ? [
      ...new Set(
        record.roles.map((role) => clampString(role, CREW_ROLE_MAX)).filter((role) => role !== "")
      )
    ].slice(0, CREW_ROLES_MAX) : [];
    seen.add(personKey);
    out.push({ personKey, name, roles });
  }
  return out;
}
function parseCrewArray(value) {
  if (typeof value === "string") {
    try {
      return sanitizeCrew(JSON.parse(value || "[]"));
    } catch {
      return [];
    }
  }
  return sanitizeCrew(value);
}

// src/entities/numberQuota.ts
import { z as z2 } from "zod";
var QUOTA_LABEL_MAX = 100;
var QUOTA_NUMBER_MAX = 9999999;
var RECORD_NUMBER_PATTERN = /^\d+$/;
function parseRecordNumber(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!RECORD_NUMBER_PATTERN.test(raw)) {
    return null;
  }
  const parsed = Number(raw);
  if (!Number.isSafeInteger(parsed) || parsed > QUOTA_NUMBER_MAX) {
    return null;
  }
  return parsed;
}
function parseEndpoint(value) {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    return null;
  }
  return parsed > QUOTA_NUMBER_MAX ? null : parsed;
}
function sanitizeQuotas(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  const out = [];
  for (const item of value) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const record = item;
    const from = parseEndpoint(record.from);
    const to = parseEndpoint(record.to);
    if (from === null || to === null || from > to) {
      continue;
    }
    out.push({
      from,
      to,
      label: clampString(record.label, QUOTA_LABEL_MAX)
    });
  }
  return out;
}
function parseQuotaArray(value) {
  if (typeof value === "string") {
    try {
      return sanitizeQuotas(JSON.parse(value || "[]"));
    } catch {
      return [];
    }
  }
  return sanitizeQuotas(value);
}
function quotaCovers(quotas, value) {
  return quotas.some((quota) => value >= quota.from && value <= quota.to);
}
function isRecordNumberAllowed(quotas, value) {
  if (!quotas.length) {
    return true;
  }
  const parsed = parseRecordNumber(value);
  return parsed !== null && quotaCovers(quotas, parsed);
}
function quotasOverlap(left, right) {
  return left.from <= right.to && right.from <= left.to;
}
function overlapsOtherQuota(quotas, candidate, ignoreIndex) {
  return quotas.some(
    (quota, index) => index !== ignoreIndex && quotasOverlap(quota, candidate)
  );
}
function uncoveredNumbers(quotas, numbers) {
  if (!quotas.length) {
    return [];
  }
  const out = [];
  for (const number of numbers) {
    const trimmed = (number || "").trim();
    if (!trimmed || out.includes(trimmed)) {
      continue;
    }
    if (!isRecordNumberAllowed(quotas, trimmed)) {
      out.push(trimmed);
    }
  }
  return out;
}
function validateQuotaEndpoint(value) {
  if (value === "" || value === null || value === void 0) {
    return rule("required");
  }
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return rule("integer");
  }
  if (parsed > QUOTA_NUMBER_MAX) {
    return rule("maxValue", { max: QUOTA_NUMBER_MAX });
  }
  return null;
}
function formatQuotaRange(quota) {
  return quota.from === quota.to ? String(quota.from) : `${quota.from}\u2013${quota.to}`;
}
function formatQuotaRanges(quotas) {
  return [...quotas].sort((firstQuota, secondQuota) => firstQuota.from - secondQuota.from).map(formatQuotaRange).join(", ");
}
var endpointField = () => z2.number({ message: rule("integer") }).int(rule("integer")).min(0, rule("integer")).max(QUOTA_NUMBER_MAX, rule("maxValue", { max: QUOTA_NUMBER_MAX }));
var numberQuotaSchema = z2.object({
  from: endpointField(),
  to: endpointField(),
  label: z2.string().trim().max(QUOTA_LABEL_MAX, rule("maxLength", { max: QUOTA_LABEL_MAX })).default("")
}).refine((quota) => quota.from <= quota.to, rule("quotaRange"));

// src/migrations/shared.ts
function fillMissing(oldDoc, defaults) {
  const filled = { ...oldDoc };
  for (const [field, value] of Object.entries(defaults)) {
    if (filled[field] === void 0) {
      filled[field] = value;
    }
  }
  return filled;
}
function toFlatStringArray(value) {
  if (value == null || value === "") {
    return [];
  }
  const items = Array.isArray(value) ? value : [value];
  return items.flat(Infinity).map(
    (item) => typeof item === "string" ? item : String(item ?? "")
  );
}
function withoutBlankNames(names, ids) {
  const kept = names.map((name, index) => ({ name, index })).filter(({ name }) => name.trim() !== "");
  return {
    names: kept.map(({ name }) => name),
    ids: ids ? kept.filter(({ index }) => index < ids.length).map(({ index }) => ids[index]) : void 0
  };
}
function cleanPersonList(doc, nameField, idField) {
  const hasIds = doc[idField] !== void 0;
  const { names, ids } = withoutBlankNames(
    toFlatStringArray(doc[nameField]),
    hasIds ? toFlatStringArray(doc[idField]) : void 0
  );
  return hasIds ? { ...doc, [nameField]: names, [idField]: ids } : { ...doc, [nameField]: names };
}
function withEntry(list, entry, same) {
  const current = Array.isArray(list) ? list : [];
  return current.some((existing) => same(existing, entry)) ? current : [...current, entry];
}
function foldDayIntoRecordedAt(doc, day) {
  if (typeof day !== "string" || !isPlausibleDay(day)) {
    return doc;
  }
  const recordedAt = typeof doc.recordedAt === "string" ? doc.recordedAt : "";
  if (daysOf(recordedAt).includes(day)) {
    return doc;
  }
  return { ...doc, recordedAt: `${day}T00:00:00.000Z` };
}
var GERMAN_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Berlin",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});
function daysOf(iso) {
  const moment = new Date(iso);
  return Number.isNaN(moment.getTime()) ? [iso.slice(0, 10)] : [iso.slice(0, 10), GERMAN_DAY.format(moment)];
}
function addRecordedAt(oldDoc) {
  return fillMissing(oldDoc, {
    recordedAt: oldDoc.createdAt || ""
  });
}
function dropDescriptionDate(oldDoc) {
  const { date, ...rest } = oldDoc;
  return foldDayIntoRecordedAt(rest, date);
}

// src/migrations/projects.ts
function timestampsToIso(oldDoc) {
  if (typeof oldDoc.updatedAt === "number") {
    oldDoc.updatedAt = new Date(oldDoc.updatedAt).toISOString();
  }
  if (typeof oldDoc.createdAt === "number") {
    oldDoc.createdAt = new Date(oldDoc.createdAt).toISOString();
  }
  return oldDoc;
}
function projectV1ToV2(oldDoc) {
  const { name, ...rest } = oldDoc;
  const typed = typeof name === "string" ? name : "";
  return fillMissing(typed ? { ...rest, shortTitle: typed } : rest, {
    shortTitle: "",
    measureNumber: "",
    company: "",
    scientificDirector: "",
    technicalDirector: "",
    startDate: "",
    endDate: "",
    authority: "",
    fileReference: "",
    permitDate: "",
    findsRecipient: "",
    findsDeliveryDate: "",
    client: ""
  });
}
function projectV2ToV3(oldDoc) {
  return fillMissing(oldDoc, { measureName: "" });
}
function projectV3ToV4(oldDoc) {
  return fillMissing(oldDoc, { status: "ongoing" });
}
var STATUS_LABEL_TO_KEY = {
  Laufend: "ongoing",
  "Au\xDFenarbeit abgeschlossen": "fieldworkCompleted",
  "Nacharbeit/Dokumentationskontrolle abgeschlossen": "postProcessingCompleted",
  "Dokumentation beim Amt abgegeben": "documentationSubmitted",
  "Funde beim Amt abgegeben": "findsSubmitted",
  "Dokumentationspr\xFCfung erhalten": "documentationReviewReceived"
};
function projectV4ToV5(oldDoc) {
  const status = oldDoc.status;
  return { ...oldDoc, status: STATUS_LABEL_TO_KEY[status] ?? status };
}
function projectV6ToV7(oldDoc) {
  const measureNumber = oldDoc.measureNumber || "";
  const { base, phaseNumber, sub } = splitMeasureNumber(measureNumber);
  const measureName = oldDoc.measureName || "";
  const startDate = oldDoc.startDate || "";
  return fillMissing(oldDoc, {
    baseMeasureNumber: base,
    subMeasure: sub,
    familyId: "",
    phases: measureNumber ? [
      {
        number: phaseNumber,
        name: measureName,
        startDate,
        shortTitle: ""
      }
    ] : []
  });
}
function projectV7ToV8(oldDoc) {
  const phases = Array.isArray(oldDoc.phases) ? oldDoc.phases : [];
  return {
    ...oldDoc,
    phases: phases.map((phase) => ({
      shortTitle: "",
      ...phase
    }))
  };
}
function projectV8ToV9(oldDoc) {
  return fillMissing(oldDoc, {
    technicalAuthor: "",
    scientificAuthor: "",
    reviewedBy: "",
    onlinePublication: true
  });
}
function projectV16ToV17(oldDoc) {
  const names = toFlatStringArray(oldDoc.scientificDirector);
  const storedId = oldDoc.scientificDirectorId;
  const ids = Array.isArray(storedId) ? storedId.flat(Infinity).map(
    (item) => typeof item === "string" ? item : ""
  ) : names.map(
    (_name, index) => index === 0 && typeof storedId === "string" ? storedId : ""
  );
  const clean = withoutBlankNames(names, ids);
  return {
    ...oldDoc,
    scientificDirector: clean.names,
    scientificDirectorId: clean.ids
  };
}
var PROJECT_MIGRATIONS = {
  1: timestampsToIso,
  2: projectV1ToV2,
  3: projectV2ToV3,
  4: projectV3ToV4,
  5: projectV4ToV5,
  // v5 -> v6: embedded userIds + groupIds (were the project_users /
  // project_groups joins); local docs start empty and resync.
  6: (oldDoc) => fillMissing(oldDoc, { userIds: [], groupIds: [] }),
  7: projectV6ToV7,
  8: projectV7ToV8,
  9: projectV8ToV9,
  // v10: embedded crew (Grabungsmannschaft); local docs start empty
  // and re-derive/re-sync.
  10: (oldDoc) => fillMissing(oldDoc, { crew: [] }),
  /**
   * v11: technicalDirector became a string[]. Through the
   * flatten-normalizer, so a doc that already pulled the server's
   * array form is not wrapped a second time (what v14 had to repair
   * for the other two person fields).
   */
  11: (oldDoc) => cleanPersonList(oldDoc, "technicalDirector", "technicalDirectorId"),
  /**
   * v12: embedded Kontingente. Empty = unconstrained, which every
   * pre-v12 project was; matches the server backfill.
   */
  12: (oldDoc) => fillMissing(oldDoc, { findingQuotas: [], profileQuotas: [] }),
  /**
   * v13: technicalAuthor/scientificAuthor became string[]. The
   * flatten-normalizer wraps a single name and leaves an
   * already-array alone.
   */
  13: (oldDoc) => cleanPersonList(
    cleanPersonList(oldDoc, "technicalAuthor", "technicalAuthorId"),
    "scientificAuthor",
    "scientificAuthorId"
  ),
  /**
   * v14: repair docs a BUGGY v13 (deployed briefly) double-wrapped
   * into [["x"]]. A no-op on already-clean docs.
   */
  14: (oldDoc) => [
    ["technicalDirector", "technicalDirectorId"],
    ["technicalAuthor", "technicalAuthorId"],
    ["scientificAuthor", "scientificAuthorId"]
  ].reduce(
    (doc, [nameField, idField]) => cleanPersonList(doc, nameField, idField),
    oldDoc
  ),
  // v15: added the app-user id links for Grabungsleitung + Autoren,
  // empty until the metadata form is re-saved (which resolves them).
  15: (oldDoc) => fillMissing(oldDoc, {
    scientificDirectorId: "",
    technicalDirectorId: [],
    technicalAuthorId: [],
    scientificAuthorId: []
  }),
  /** v16: the Fundzettelnummer Kontingent. Empty = unconstrained. */
  16: (oldDoc) => fillMissing(oldDoc, { findSlipQuotas: [] }),
  17: projectV16ToV17
};
var GROUP_MIGRATIONS = {
  // v0 -> v1: embedded userIds (was the group_members join); local
  // docs start empty and re-sync.
  1: (oldDoc) => fillMissing(oldDoc, { userIds: [] })
};

// src/entities/project.ts
var TEXT_FIELD_MAX = 255;
var MEASURE_NUMBER_MAX = 32;
var MEASURE_NUMBER_PATTERN = /^M-\d{4}-\d{1,4}-\d{1,2}(_\d{1,3})?$/;
var PROJECT_STATUSES = [
  "ongoing",
  "fieldworkCompleted",
  "postProcessingCompleted",
  "documentationSubmitted",
  "findsSubmitted",
  "documentationReviewReceived"
];
var DEFAULT_PROJECT_STATUS = "ongoing";
var STATUS_MAX = 80;
function personListArray(value) {
  const items = value == null ? [] : Array.isArray(value) ? value : [value];
  return items.flat(Infinity).map((item) => typeof item === "string" ? item : String(item ?? "")).map((item) => item.trim()).filter((item) => item !== "");
}
function scalarOrList(itemMaxLength) {
  return (value) => typeof value === "string" ? value ? [clampString(value, itemMaxLength)] : [] : clampStringList(value, { itemMaxLength });
}
var PROJECT_SCHEMA_VERSION = 17;
var projectSchema = {
  version: PROJECT_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    measureNumber: { type: "string", maxLength: MEASURE_NUMBER_MAX },
    measureName: { type: "string" },
    // Split axes of the Maßnahmennummer (see the v7 note above).
    baseMeasureNumber: { type: "string", maxLength: MEASURE_NUMBER_MAX },
    subMeasure: { type: "string", maxLength: SUB_MEASURE_MAX },
    familyId: { type: "string", maxLength: 36 },
    phases: {
      type: "array",
      items: {
        type: "object",
        properties: {
          number: { type: "string", maxLength: PHASE_NUMBER_MAX },
          name: { type: "string", maxLength: PHASE_NAME_MAX },
          startDate: { type: "string", maxLength: 10 },
          shortTitle: {
            type: "string",
            maxLength: PHASE_SHORT_TITLE_MAX
          }
        },
        required: ["number", "name", "startDate"]
      }
    },
    crew: {
      type: "array",
      items: {
        type: "object",
        properties: {
          personKey: { type: "string", maxLength: CREW_KEY_MAX },
          name: { type: "string", maxLength: CREW_NAME_MAX },
          roles: {
            type: "array",
            items: { type: "string", maxLength: CREW_ROLE_MAX }
          }
        },
        required: ["personKey", "name", "roles"]
      }
    },
    // Kontingente — allocated number ranges (see the v12 note above).
    findingQuotas: {
      type: "array",
      items: {
        type: "object",
        properties: {
          from: { type: "number" },
          to: { type: "number" },
          label: { type: "string", maxLength: QUOTA_LABEL_MAX }
        },
        required: ["from", "to"]
      }
    },
    profileQuotas: {
      type: "array",
      items: {
        type: "object",
        properties: {
          from: { type: "number" },
          to: { type: "number" },
          label: { type: "string", maxLength: QUOTA_LABEL_MAX }
        },
        required: ["from", "to"]
      }
    },
    findSlipQuotas: {
      type: "array",
      items: {
        type: "object",
        properties: {
          from: { type: "number" },
          to: { type: "number" },
          label: { type: "string", maxLength: QUOTA_LABEL_MAX }
        },
        required: ["from", "to"]
      }
    },
    shortTitle: { type: "string" },
    status: { type: "string", maxLength: STATUS_MAX },
    company: { type: "string" },
    scientificDirector: {
      type: "array",
      items: { type: "string", maxLength: TEXT_FIELD_MAX }
    },
    // App-user ids, POSITIONAL to scientificDirector ('' where free text).
    scientificDirectorId: {
      type: "array",
      ref: "users",
      items: { type: "string", maxLength: 36 }
    },
    technicalDirector: {
      type: "array",
      items: { type: "string", maxLength: TEXT_FIELD_MAX }
    },
    // App-user ids, POSITIONAL to technicalDirector ('' where free text).
    technicalDirectorId: {
      type: "array",
      ref: "users",
      items: { type: "string", maxLength: 36 }
    },
    startDate: { type: "string" },
    endDate: { type: "string" },
    authority: { type: "string" },
    fileReference: { type: "string" },
    permitDate: { type: "string" },
    findsRecipient: { type: "string" },
    findsDeliveryDate: { type: "string" },
    client: { type: "string" },
    description: { type: "string" },
    technicalAuthor: {
      type: "array",
      items: { type: "string", maxLength: TEXT_FIELD_MAX }
    },
    // App-user ids, POSITIONAL to technicalAuthor ('' where free text).
    technicalAuthorId: {
      type: "array",
      ref: "users",
      items: { type: "string", maxLength: 36 }
    },
    scientificAuthor: {
      type: "array",
      items: { type: "string", maxLength: TEXT_FIELD_MAX }
    },
    // App-user ids, POSITIONAL to scientificAuthor ('' where free text).
    scientificAuthorId: {
      type: "array",
      ref: "users",
      items: { type: "string", maxLength: 36 }
    },
    reviewedBy: { type: "string" },
    onlinePublication: { type: "boolean", default: true },
    // Direct user assignments (ref -> users) + assigned groups (ref -> groups).
    userIds: {
      type: "array",
      ref: "users",
      items: { type: "string", maxLength: 36 }
    },
    groupIds: {
      type: "array",
      ref: "groups",
      items: { type: "string", maxLength: 36 }
    },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "measureNumber",
    "measureName",
    "baseMeasureNumber",
    "subMeasure",
    "familyId",
    "phases",
    "crew",
    "findingQuotas",
    "profileQuotas",
    "findSlipQuotas",
    "shortTitle",
    "status",
    "company",
    "scientificDirector",
    "scientificDirectorId",
    "technicalDirector",
    "technicalDirectorId",
    "startDate",
    "endDate",
    "authority",
    "fileReference",
    "permitDate",
    "findsRecipient",
    "findsDeliveryDate",
    "client",
    "description",
    "technicalAuthor",
    "technicalAuthorId",
    "scientificAuthor",
    "scientificAuthorId",
    "reviewedBy",
    "onlinePublication",
    "userIds",
    "groupIds",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"]]
};
var textField = () => z3.string().trim().max(TEXT_FIELD_MAX, rule("maxLength", { max: TEXT_FIELD_MAX })).default("");
var dateField = () => z3.string().trim().refine(
  (value) => value === "" || DAY_PATTERN.test(value),
  rule("dateFormat")
).refine(hasPlausibleYearIfDay, rule("implausibleYear")).default("");
var projectInputSchema = z3.object({
  measureNumber: z3.string().trim().min(1, rule("required")).regex(MEASURE_NUMBER_PATTERN, rule("measureFormat")),
  measureName: textField(),
  shortTitle: z3.string().trim().min(1, rule("required")).max(NAME_MAX, rule("maxLength", { max: NAME_MAX })),
  status: z3.enum(PROJECT_STATUSES).default(DEFAULT_PROJECT_STATUS),
  company: textField(),
  scientificDirector: z3.array(z3.string().max(TEXT_FIELD_MAX)).default([]),
  scientificDirectorId: z3.array(z3.string().max(ID_MAX)).default([]),
  technicalDirector: z3.array(z3.string().max(TEXT_FIELD_MAX)).default([]),
  technicalDirectorId: z3.array(z3.string().max(ID_MAX)).default([]),
  startDate: dateField(),
  endDate: dateField(),
  authority: textField(),
  fileReference: textField(),
  permitDate: dateField(),
  findsRecipient: textField(),
  findsDeliveryDate: dateField(),
  client: textField(),
  description: z3.string().trim().max(DESCRIPTION_MAX, rule("maxLength", { max: DESCRIPTION_MAX })).default(""),
  technicalAuthor: z3.array(z3.string().max(TEXT_FIELD_MAX)).default([]),
  technicalAuthorId: z3.array(z3.string().max(ID_MAX)).default([]),
  scientificAuthor: z3.array(z3.string().max(TEXT_FIELD_MAX)).default([]),
  scientificAuthorId: z3.array(z3.string().max(ID_MAX)).default([]),
  reviewedBy: textField(),
  onlinePublication: z3.boolean().default(true),
  userIds: z3.array(z3.string().max(ID_MAX)).default([]),
  groupIds: z3.array(z3.string().max(ID_MAX)).default([])
});
var projectEntity = defineEntity({
  name: "project",
  apiBase: "/api/projects",
  collection: "projects",
  rxSchema: projectSchema,
  migrations: PROJECT_MIGRATIONS,
  fields: {
    measureNumber: {
      maxLength: MEASURE_NUMBER_MAX,
      required: true,
      merge: true,
      history: true,
      unique: true
    },
    measureName: { maxLength: TEXT_FIELD_MAX, merge: true, history: true },
    baseMeasureNumber: {
      maxLength: MEASURE_NUMBER_MAX,
      merge: true,
      history: true
    },
    subMeasure: { maxLength: SUB_MEASURE_MAX, merge: true, history: true },
    familyId: { maxLength: ID_MAX, merge: true, history: true },
    /**
     * Ordered phase list — office-edited only, never concurrently in the
     * field, so last-write-wins and NOT a merge field. `sanitizeValue`
     * clamps the embedded objects.
     */
    phases: { history: true, sanitizeValue: sanitizePhases },
    // Grabungsmannschaft — last-write-wins like `phases`.
    crew: { history: true, sanitizeValue: sanitizeCrew },
    // Kontingente — last-write-wins like `phases`, and mirrored across the
    // family in one pass.
    findingQuotas: { history: true, sanitizeValue: sanitizeQuotas },
    profileQuotas: { history: true, sanitizeValue: sanitizeQuotas },
    findSlipQuotas: { history: true, sanitizeValue: sanitizeQuotas },
    shortTitle: {
      maxLength: NAME_MAX,
      required: true,
      merge: true,
      history: true
    },
    status: { maxLength: STATUS_MAX, merge: true, history: true },
    company: { maxLength: TEXT_FIELD_MAX, merge: true, history: true },
    /**
     * Both 'list', not 'array': the Grabungsleitung's names and the
     * account of each, positional. Dropping a blank or a repeat on one
     * side alone files everybody after it under the wrong person.
     */
    scientificDirector: {
      kind: "list",
      itemMaxLength: TEXT_FIELD_MAX,
      sanitizeValue: scalarOrList(TEXT_FIELD_MAX),
      merge: true,
      history: true
    },
    scientificDirectorId: {
      kind: "list",
      itemMaxLength: ID_MAX,
      sanitizeValue: scalarOrList(ID_MAX),
      merge: true,
      history: true
    },
    technicalDirector: {
      kind: "list",
      itemMaxLength: TEXT_FIELD_MAX,
      merge: true,
      history: true
    },
    technicalDirectorId: {
      kind: "list",
      itemMaxLength: ID_MAX,
      merge: true,
      history: true
    },
    startDate: { kind: "date", merge: true, history: true },
    endDate: { kind: "date", merge: true, history: true },
    authority: { maxLength: TEXT_FIELD_MAX, merge: true, history: true },
    fileReference: {
      maxLength: TEXT_FIELD_MAX,
      merge: true,
      history: true
    },
    permitDate: { kind: "date", merge: true, history: true },
    findsRecipient: {
      maxLength: TEXT_FIELD_MAX,
      merge: true,
      history: true
    },
    findsDeliveryDate: { kind: "date", merge: true, history: true },
    client: { maxLength: TEXT_FIELD_MAX, merge: true, history: true },
    description: {
      kind: "text",
      maxLength: DESCRIPTION_MAX,
      merge: true,
      history: true
    },
    technicalAuthor: {
      kind: "list",
      itemMaxLength: TEXT_FIELD_MAX,
      merge: true,
      history: true
    },
    technicalAuthorId: {
      kind: "list",
      itemMaxLength: ID_MAX,
      merge: true,
      history: true
    },
    scientificAuthor: {
      kind: "list",
      itemMaxLength: TEXT_FIELD_MAX,
      merge: true,
      history: true
    },
    scientificAuthorId: {
      kind: "list",
      itemMaxLength: ID_MAX,
      merge: true,
      history: true
    },
    reviewedBy: { maxLength: TEXT_FIELD_MAX, merge: true, history: true },
    onlinePublication: { kind: "boolean", merge: true, history: true },
    // Membership id sets — string-array merge (set-union/dedupe).
    userIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true,
      history: true
    },
    groupIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true,
      history: true
    }
  },
  inputSchema: projectInputSchema,
  toWire(row) {
    return {
      id: row.id,
      measureNumber: row.measureNumber || "",
      measureName: row.measureName || "",
      baseMeasureNumber: row.baseMeasureNumber || "",
      subMeasure: row.subMeasure || "",
      familyId: row.familyId || "",
      phases: parsePhaseArray(row.phases),
      crew: parseCrewArray(row.crew),
      findingQuotas: parseQuotaArray(row.findingQuotas),
      profileQuotas: parseQuotaArray(row.profileQuotas),
      findSlipQuotas: parseQuotaArray(row.findSlipQuotas),
      shortTitle: row.shortTitle,
      status: row.status || DEFAULT_PROJECT_STATUS,
      company: row.company || "",
      scientificDirector: parseStringArray(row.scientificDirector),
      scientificDirectorId: parseStringArray(row.scientificDirectorId),
      technicalDirector: parseStringArray(row.technicalDirector),
      technicalDirectorId: parseStringArray(row.technicalDirectorId),
      startDate: row.startDate ? fmtDate(row.startDate) : "",
      endDate: row.endDate ? fmtDate(row.endDate) : "",
      authority: row.authority || "",
      fileReference: row.fileReference || "",
      permitDate: row.permitDate ? fmtDate(row.permitDate) : "",
      findsRecipient: row.findsRecipient || "",
      findsDeliveryDate: row.findsDeliveryDate ? fmtDate(row.findsDeliveryDate) : "",
      client: row.client || "",
      description: row.description,
      technicalAuthor: parseStringArray(row.technicalAuthor),
      technicalAuthorId: parseStringArray(row.technicalAuthorId),
      scientificAuthor: parseStringArray(row.scientificAuthor),
      scientificAuthorId: parseStringArray(row.scientificAuthorId),
      reviewedBy: row.reviewedBy || "",
      onlinePublication: row.onlinePublication == null ? true : Boolean(row.onlinePublication),
      userIds: parseStringArray(row.userIds),
      groupIds: parseStringArray(row.groupIds),
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      measureNumber: doc.measureNumber,
      measureName: doc.measureName,
      baseMeasureNumber: doc.baseMeasureNumber,
      subMeasure: doc.subMeasure,
      familyId: doc.familyId,
      // Every embedded list persists as a JSON-string TEXT column.
      phases: JSON.stringify(doc.phases || []),
      crew: JSON.stringify(doc.crew || []),
      findingQuotas: JSON.stringify(doc.findingQuotas || []),
      profileQuotas: JSON.stringify(doc.profileQuotas || []),
      findSlipQuotas: JSON.stringify(doc.findSlipQuotas || []),
      shortTitle: doc.shortTitle,
      status: doc.status,
      company: doc.company,
      scientificDirector: JSON.stringify(
        personListArray(doc.scientificDirector)
      ),
      // The four *Id arrays are POSITIONAL to their name arrays: keep
      // blanks, or the alignment the crew reads by index would shift.
      scientificDirectorId: JSON.stringify(
        parseStringArray(doc.scientificDirectorId)
      ),
      technicalDirector: JSON.stringify(
        personListArray(doc.technicalDirector)
      ),
      technicalDirectorId: JSON.stringify(
        parseStringArray(doc.technicalDirectorId)
      ),
      startDate: parseDate(doc.startDate),
      endDate: parseDate(doc.endDate),
      authority: doc.authority,
      fileReference: doc.fileReference,
      permitDate: parseDate(doc.permitDate),
      findsRecipient: doc.findsRecipient,
      findsDeliveryDate: parseDate(doc.findsDeliveryDate),
      client: doc.client,
      description: doc.description,
      technicalAuthor: JSON.stringify(
        personListArray(doc.technicalAuthor)
      ),
      technicalAuthorId: JSON.stringify(
        parseStringArray(doc.technicalAuthorId)
      ),
      scientificAuthor: JSON.stringify(
        personListArray(doc.scientificAuthor)
      ),
      scientificAuthorId: JSON.stringify(
        parseStringArray(doc.scientificAuthorId)
      ),
      reviewedBy: doc.reviewedBy,
      onlinePublication: doc.onlinePublication,
      userIds: JSON.stringify(doc.userIds || []),
      groupIds: JSON.stringify(doc.groupIds || [])
    };
  }
});

// src/entities/parcel.ts
import { z as z4 } from "zod";
var PARCEL_FIELD_MAX = 255;
var PARCEL_SCHEMA_VERSION = 0;
var parcelSchema = {
  version: PARCEL_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    country: { type: "string" },
    state: { type: "string" },
    administrativeRegion: { type: "string" },
    district: { type: "string" },
    municipality: { type: "string" },
    cadastralDistrict: { type: "string" },
    parcelNumber: { type: "string" },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "country",
    "state",
    "administrativeRegion",
    "district",
    "municipality",
    "cadastralDistrict",
    "parcelNumber",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId"]
};
var hierarchyField = () => z4.string().trim().max(PARCEL_FIELD_MAX, rule("maxLength", { max: PARCEL_FIELD_MAX })).default("");
var parcelInputSchema = z4.object({
  country: hierarchyField(),
  state: hierarchyField(),
  administrativeRegion: hierarchyField(),
  district: hierarchyField(),
  municipality: hierarchyField(),
  cadastralDistrict: hierarchyField(),
  parcelNumber: hierarchyField(),
  projectId: z4.string().max(ID_MAX).default("")
});
var parcelEntity = defineEntity({
  name: "parcel",
  apiBase: "/api/parcels",
  collection: "parcels",
  rxSchema: parcelSchema,
  fields: {
    country: { maxLength: PARCEL_FIELD_MAX, merge: true, history: true },
    state: { maxLength: PARCEL_FIELD_MAX, merge: true, history: true },
    administrativeRegion: {
      maxLength: PARCEL_FIELD_MAX,
      merge: true,
      history: true
    },
    district: { maxLength: PARCEL_FIELD_MAX, merge: true, history: true },
    municipality: {
      maxLength: PARCEL_FIELD_MAX,
      merge: true,
      history: true
    },
    cadastralDistrict: {
      maxLength: PARCEL_FIELD_MAX,
      merge: true,
      history: true
    },
    parcelNumber: {
      maxLength: PARCEL_FIELD_MAX,
      merge: true,
      history: true
    },
    projectId: { kind: "ref", maxLength: ID_MAX }
  },
  inputSchema: parcelInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      country: row.country || "",
      state: row.state || "",
      administrativeRegion: row.administrativeRegion || "",
      district: row.district || "",
      municipality: row.municipality || "",
      cadastralDistrict: row.cadastralDistrict || "",
      parcelNumber: row.parcelNumber || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      country: doc.country,
      state: doc.state,
      administrativeRegion: doc.administrativeRegion,
      district: doc.district,
      municipality: doc.municipality,
      cadastralDistrict: doc.cadastralDistrict,
      parcelNumber: doc.parcelNumber
    };
  }
});

// src/entities/user.ts
import { z as z5 } from "zod";

// src/migrations/people.ts
var USER_MIGRATIONS = {
  1: (oldDoc) => oldDoc
};
var USER_PROFILE_MIGRATIONS = {
  /**
   * v0 -> v1: the name Crewmeister books this person under, for the
   * Zeitabgleich. Empty means "spelled the same or not mapped yet", which is
   * what every existing profile was; matches the server backfill (the column
   * defaults to '').
   */
  1: (oldDoc) => fillMissing(oldDoc, { crewmeisterName: "" }),
  /**
   * v1 -> v2: may this person be NAMED as Grabungsleiter. It is `required`
   * in the schema, so a stored profile needs the value rather than a
   * missing key; false is what every existing one was, and matches the
   * server backfill (the column defaults to 0).
   */
  2: (oldDoc) => fillMissing(oldDoc, { canLeadExcavation: false }),
  /**
   * v2 -> v3: company email address and business cards issued. Unmarked,
   * as on the server (both columns default to 0).
   */
  3: (oldDoc) => fillMissing(oldDoc, { hasCompanyEmail: false, hasBusinessCard: false }),
  // v3 -> v4: company and private email address, empty as on the server.
  4: (oldDoc) => fillMissing(oldDoc, { companyEmail: "", privateEmail: "" }),
  /**
   * v4 -> v5: employment dates and type. Open-ended and an ordinary employee,
   * as on the server (NULL dates, type defaults to 'employee') — so nobody
   * drops out of a picker by the update alone.
   */
  5: (oldDoc) => fillMissing(oldDoc, {
    employmentStart: "",
    employmentEnd: "",
    employeeType: "employee"
  })
};

// src/entities/user.ts
var USER_SCHEMA_VERSION = 1;
var EMAIL_MAX = 255;
var userSchema = {
  version: USER_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    // The column is VarChar(255).
    username: { type: "string", maxLength: 255 },
    displayName: { type: "string" },
    email: { type: "string" },
    disabled: { type: "boolean" },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "username",
    "displayName",
    "email",
    "disabled",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "username"]
};
var userInputSchema = z5.object({
  username: z5.string().trim().min(1).max(NAME_MAX),
  displayName: z5.string().max(NAME_MAX).default(""),
  email: z5.string().max(EMAIL_MAX).default(""),
  disabled: z5.boolean().default(false)
});
var userEntity = defineEntity({
  name: "user",
  apiBase: "/api/users",
  collection: "users",
  rxSchema: userSchema,
  migrations: USER_MIGRATIONS,
  readOnly: true,
  /**
   * Versioned so the administration can see what an account looked like
   * before: the Synology refresh renames it, changes the mail address or
   * expires it, and an admin toggles `disabled` by hand. Read-only history —
   * restoring a row the NAS owns would only be undone at the next refresh
   * (see routes/users.history.ts).
   */
  historyAllFields: true,
  fields: {
    username: { maxLength: NAME_MAX },
    displayName: { maxLength: NAME_MAX },
    email: { maxLength: EMAIL_MAX },
    /**
     * Mutable server-side (Synology refresh toggles it), so it must be a
     * content field — otherwise the conflict handler's equality check can't
     * see it change and clients never pull the new status.
     */
    disabled: { kind: "boolean" }
  },
  inputSchema: userInputSchema,
  toWire(row) {
    return {
      id: row.id,
      username: row.username,
      displayName: row.displayName || "",
      email: row.email || "",
      disabled: Boolean(row.disabled),
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      username: doc.username,
      displayName: doc.displayName,
      email: doc.email,
      disabled: doc.disabled
    };
  }
});

// src/entities/group.ts
import { z as z6 } from "zod";
var GROUP_SCHEMA_VERSION = 1;
var groupSchema = {
  version: GROUP_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    name: { type: "string", maxLength: NAME_MAX },
    description: { type: "string" },
    // Member user ids (ref -> users).
    userIds: {
      type: "array",
      ref: "users",
      items: { type: "string", maxLength: 36 }
    },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "name",
    "description",
    "userIds",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"]]
};
var groupInputSchema = z6.object({
  name: z6.string().trim().min(1, rule("required")).max(NAME_MAX, rule("maxLength", { max: NAME_MAX })),
  description: z6.string().max(DESCRIPTION_MAX, rule("maxLength", { max: DESCRIPTION_MAX })).default(""),
  userIds: z6.array(z6.string().max(ID_MAX)).default([])
});
var groupEntity = defineEntity({
  name: "group",
  apiBase: "/api/groups",
  collection: "groups",
  rxSchema: groupSchema,
  migrations: GROUP_MIGRATIONS,
  // Versioned so name/description and membership (userIds) changes are
  // auditable — diff the snapshot between versions.
  historyAllFields: true,
  fields: {
    name: {
      maxLength: NAME_MAX,
      required: true,
      merge: true,
      unique: true
    },
    description: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    // Member id set — string-array merge (set-union/dedupe).
    userIds: { kind: "array", itemMaxLength: ID_MAX, merge: true }
  },
  inputSchema: groupInputSchema,
  toWire(row) {
    return {
      id: row.id,
      name: row.name || "",
      description: row.description || "",
      userIds: parseStringArray(row.userIds),
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      name: doc.name,
      description: doc.description,
      // Member id set persists as a JSON-string TEXT column.
      userIds: JSON.stringify(doc.userIds || [])
    };
  }
});

// src/entities/projectGroup.ts
import { z as z7 } from "zod";
var PROJECT_GROUP_SCHEMA_VERSION = 0;
var projectGroupSchema = {
  version: PROJECT_GROUP_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    name: { type: "string", maxLength: NAME_MAX },
    description: { type: "string" },
    // Member project ids (ref -> projects).
    projectIds: {
      type: "array",
      ref: "projects",
      items: { type: "string", maxLength: 36 }
    },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "name",
    "description",
    "projectIds",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"]]
};
var projectGroupInputSchema = z7.object({
  name: z7.string().trim().min(1, rule("required")).max(NAME_MAX, rule("maxLength", { max: NAME_MAX })),
  description: z7.string().max(DESCRIPTION_MAX, rule("maxLength", { max: DESCRIPTION_MAX })).default(""),
  projectIds: z7.array(z7.string().max(ID_MAX)).default([])
});
var projectGroupEntity = defineEntity({
  name: "projectGroup",
  apiBase: "/api/project-groups",
  collection: "project_groups",
  rxSchema: projectGroupSchema,
  // Versioned so name/description and membership (projectIds) changes are
  // auditable — diff the snapshot between versions.
  historyAllFields: true,
  fields: {
    name: {
      maxLength: NAME_MAX,
      required: true,
      merge: true,
      unique: true
    },
    description: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    // Member id set — string-array merge (set-union/dedupe).
    projectIds: { kind: "array", itemMaxLength: ID_MAX, merge: true }
  },
  inputSchema: projectGroupInputSchema,
  toWire(row) {
    return {
      id: row.id,
      name: row.name || "",
      description: row.description || "",
      projectIds: parseStringArray(row.projectIds),
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      name: doc.name,
      description: doc.description,
      // Member id set persists as a JSON-string TEXT column.
      projectIds: JSON.stringify(doc.projectIds || [])
    };
  }
});

// src/entities/userProfile.ts
import { z as z8 } from "zod";
var EMAIL_MAX2 = 255;
var EMPLOYEE_TYPES = ["employee", "trial"];
var DEFAULT_EMPLOYEE_TYPE = "employee";
var EMPLOYEE_TYPE_MAX = 20;
function clampEmployeeType(value) {
  const text3 = typeof value === "string" ? value.trim() : "";
  return EMPLOYEE_TYPES.includes(text3) ? text3 : DEFAULT_EMPLOYEE_TYPE;
}
var APP_ROLES = [
  "Administrator",
  "Projektmanagement",
  "Projektassistenz",
  "Teamleitung (Senior)",
  "Teamleitung (Junior)",
  "Dokumentationsassistenz (Senior)",
  "Dokumentationsassistenz (Junior)",
  // Additive to any of the above: whoever also keeps the Teams. See the
  // role table in auth/ability.ts for why it is a role of its own.
  "Teamkoordination",
  // Additive as well: keeps the personnel side of the Benutzerverwaltung —
  // employment dates, employee type and the other profile fields, but no
  // roles. See the role table in auth/ability.ts.
  "Personalverwaltung",
  // Additive as well: keeps the translation side of the administration —
  // the DeepL glossary and the contract's usage. See auth/ability.ts.
  "\xDCbersetzungsverwaltung",
  "Mitarbeiter"
];
var USER_PROFILE_SCHEMA_VERSION = 5;
var userProfileSchema = {
  version: USER_PROFILE_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    customName: { type: "string", maxLength: NAME_MAX },
    roles: { type: "array", items: { type: "string" } },
    /**
     * How Crewmeister writes this person, when it differs from what we call
     * them. Lives here rather than in the Zeitabgleich's own storage so the
     * office maps a name ONCE, not once per workstation.
     */
    crewmeisterName: { type: "string", maxLength: NAME_MAX },
    // May be named as Grabungsleiter — the leader fields offer these
    // accounts as a group of their own, ahead of everyone else.
    canLeadExcavation: { type: "boolean" },
    // Issued a company email address / business cards.
    hasCompanyEmail: { type: "boolean" },
    hasBusinessCard: { type: "boolean" },
    companyEmail: { type: "string", maxLength: EMAIL_MAX2 },
    privateEmail: { type: "string", maxLength: EMAIL_MAX2 },
    // First and last day employed, inclusive; '' = not limited that way.
    employmentStart: { type: "string", maxLength: 10 },
    employmentEnd: { type: "string", maxLength: 10 },
    employeeType: { type: "string", maxLength: EMPLOYEE_TYPE_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "customName",
    "roles",
    "crewmeisterName",
    "canLeadExcavation",
    "hasCompanyEmail",
    "hasBusinessCard",
    "companyEmail",
    "privateEmail",
    "employmentStart",
    "employmentEnd",
    "employeeType",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"]]
};
var employmentDay = () => z8.string().trim().refine(
  (value) => value === "" || DAY_PATTERN.test(value),
  rule("dateFormat")
).refine(hasPlausibleYearIfDay, rule("implausibleYear")).default("");
var userProfileInputSchema = z8.object({
  customName: z8.string().max(NAME_MAX).default(""),
  roles: z8.array(z8.string()).default([]),
  crewmeisterName: z8.string().max(NAME_MAX).default(""),
  canLeadExcavation: z8.boolean().default(false),
  hasCompanyEmail: z8.boolean().default(false),
  hasBusinessCard: z8.boolean().default(false),
  companyEmail: z8.string().max(EMAIL_MAX2).default(""),
  privateEmail: z8.string().max(EMAIL_MAX2).default(""),
  employmentStart: employmentDay(),
  employmentEnd: employmentDay(),
  employeeType: z8.enum(EMPLOYEE_TYPES).default(DEFAULT_EMPLOYEE_TYPE)
});
var userProfileEntity = defineEntity({
  name: "userProfile",
  apiBase: "/api/user-profiles",
  collection: "user_profiles",
  rxSchema: userProfileSchema,
  migrations: USER_PROFILE_MIGRATIONS,
  // The roles live here, so this row IS the authorization state — who granted
  // whom what, and when, is the one change log an administration really needs.
  historyAllFields: true,
  fields: {
    customName: { maxLength: NAME_MAX, merge: true },
    /**
     * An array (set) field: the generic sanitizer dedupes and filters to the
     * known roles, and it flows into mergeFields/conflictFields like any
     * other content field (compared via the array-aware `valuesEqual`).
     */
    roles: { kind: "array", values: APP_ROLES, merge: true },
    crewmeisterName: { maxLength: NAME_MAX, merge: true },
    canLeadExcavation: { kind: "boolean", merge: true },
    hasCompanyEmail: { kind: "boolean", merge: true },
    hasBusinessCard: { kind: "boolean", merge: true },
    companyEmail: { maxLength: EMAIL_MAX2, merge: true },
    privateEmail: { maxLength: EMAIL_MAX2, merge: true },
    employmentStart: { kind: "date", merge: true },
    employmentEnd: { kind: "date", merge: true },
    employeeType: { sanitizeValue: clampEmployeeType, merge: true }
  },
  inputSchema: userProfileInputSchema,
  toWire(row) {
    let roles;
    try {
      roles = JSON.parse(row.roles || "[]");
    } catch {
      roles = [];
    }
    return {
      id: row.id,
      customName: row.customName || "",
      roles,
      crewmeisterName: row.crewmeisterName || "",
      canLeadExcavation: Boolean(row.canLeadExcavation),
      hasCompanyEmail: Boolean(row.hasCompanyEmail),
      hasBusinessCard: Boolean(row.hasBusinessCard),
      companyEmail: row.companyEmail || "",
      privateEmail: row.privateEmail || "",
      employmentStart: row.employmentStart ? fmtDate(row.employmentStart) : "",
      employmentEnd: row.employmentEnd ? fmtDate(row.employmentEnd) : "",
      employeeType: clampEmployeeType(row.employeeType),
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      customName: doc.customName || "",
      roles: JSON.stringify(doc.roles || []),
      crewmeisterName: doc.crewmeisterName || "",
      canLeadExcavation: Boolean(doc.canLeadExcavation),
      hasCompanyEmail: Boolean(doc.hasCompanyEmail),
      hasBusinessCard: Boolean(doc.hasBusinessCard),
      companyEmail: doc.companyEmail || "",
      privateEmail: doc.privateEmail || "",
      employmentStart: parseDate(doc.employmentStart),
      employmentEnd: parseDate(doc.employmentEnd),
      employeeType: clampEmployeeType(doc.employeeType)
    };
  }
});

// src/entities/finding.ts
import { z as z10 } from "zod";
import { VORLAEUFIGE_DATIERUNG } from "ast-excabook-sheets/thesaurus";

// src/entities/recordedAt.ts
import { z as z9 } from "zod";
var RECORDED_AT_MAX = 30;
function recordedAtField(options) {
  const base = z9.string().trim();
  if (options?.required) {
    return base.min(1, rule("required")).refine(
      (value) => value === "" || !Number.isNaN(Date.parse(value)),
      rule("dateTimeFormat")
    ).refine(hasPlausibleYearIfDateTime, rule("implausibleYear"));
  }
  return base.refine(
    (value) => value === "" || !Number.isNaN(Date.parse(value)),
    rule("dateTimeFormat")
  ).refine(hasPlausibleYearIfDateTime, rule("implausibleYear")).default("");
}

// src/entities/findingType.ts
var FINDING_TYPE_OPTIONS = ["description", "structure"];
var DEFAULT_FINDING_TYPE = "description";
function isStructureFinding(type) {
  return (type ?? "").trim() === "structure";
}
function clampFindingType(value) {
  const text3 = typeof value === "string" ? value.trim() : "";
  return FINDING_TYPE_OPTIONS.includes(text3) ? text3 : DEFAULT_FINDING_TYPE;
}
function findingTypeChanged(incoming, stored) {
  return !!stored && clampFindingType(incoming.findingType) !== clampFindingType(stored.findingType);
}

// src/migrations/findings.ts
function findingV0ToV1(oldDoc) {
  const rest = { ...oldDoc };
  delete rest.trench;
  return rest;
}
function findingV1ToV2(oldDoc) {
  return fillMissing(oldDoc, { partOf: "", inProfileWith: "" });
}
function findingV3ToV4(oldDoc) {
  const rest = { ...oldDoc };
  delete rest.parcelId;
  delete rest.area;
  delete rest.sondage;
  delete rest.overliesOrCuts;
  delete rest.underliesOrIsCut;
  delete rest.contemporaryWith;
  delete rest.identicalWith;
  delete rest.partOf;
  delete rest.inProfileWith;
  return rest;
}
function findingPlanumV0ToV1(oldDoc) {
  return fillMissing(oldDoc, {
    boundaryClarity: "",
    boundaryBasis: "",
    contour: "",
    humusContent: ""
  });
}
function findingPlanumV1ToV2(oldDoc) {
  return fillMissing(oldDoc, {
    northArrow: false,
    northAngle: 0,
    northX: 88,
    northY: 12
  });
}
function identicalWithToRefs(oldDoc) {
  const rest = { ...oldDoc };
  delete rest.identicalWith;
  return fillMissing(rest, {
    identicalWithPlanumIds: [],
    identicalWithProfileIds: []
  });
}
function mergeNumberLists(left, right) {
  const merged = [];
  for (const part of `${left ?? ""},${right ?? ""}`.split(/[;,]/)) {
    const trimmed = part.trim();
    if (trimmed && !merged.some(
      (entry) => entry.toLowerCase() === trimmed.toLowerCase()
    )) {
      merged.push(trimmed);
    }
  }
  return merged.join(", ");
}
function mergeLegacyRelations(oldDoc) {
  const { partOf, inProfileWith, ...rest } = oldDoc;
  if (!String(partOf ?? "").trim() && !String(inProfileWith ?? "").trim()) {
    return rest;
  }
  rest.identicalWith = mergeNumberLists(rest.identicalWith, partOf);
  rest.contemporaryWith = mergeNumberLists(
    rest.contemporaryWith,
    inProfileWith
  );
  return rest;
}
function splitOutPartOfAndInProfile(oldDoc) {
  return fillMissing(oldDoc, {
    partOfPlanumIds: [],
    partOfProfileIds: [],
    inProfileWithPlanumIds: [],
    inProfileWithProfileIds: []
  });
}
function addSpatialStratigraphy(oldDoc) {
  return fillMissing(oldDoc, {
    parcelId: "",
    area: "",
    sondage: "",
    overliesOrCuts: "",
    underliesOrIsCut: "",
    contemporaryWith: "",
    identicalWith: "",
    partOf: "",
    inProfileWith: ""
  });
}
var FINDING_MIGRATIONS = {
  1: findingV0ToV1,
  2: findingV1ToV2,
  // v3: added recordedAt (Aufnahmedatum), backfilled from createdAt.
  3: addRecordedAt,
  // v4: moved parcelId/area/sondage + stratigraphy to Planum/Profil.
  4: findingV3ToV4,
  // v5: added onlyVisibleInProfile, false for every existing Befund.
  5: (oldDoc) => fillMissing(oldDoc, { onlyVisibleInProfile: false }),
  // v6: added findingType — everything so far is the default.
  6: (oldDoc) => fillMissing(oldDoc, { findingType: DEFAULT_FINDING_TYPE })
};
var addStructureFields = (oldDoc) => fillMissing(oldDoc, {
  courses: [],
  excavationMethod: "",
  fills: "",
  filledBy: "",
  interpretation: ""
});
var addStrata = (oldDoc) => fillMissing(oldDoc, { strata: [] });
var keepStrata = (oldDoc) => oldDoc;
var addPartLabel = (oldDoc) => fillMissing(oldDoc, { partLabel: "" });
var PLANUM_MIGRATIONS = {
  1: findingPlanumV0ToV1,
  2: findingPlanumV1ToV2,
  // v3: added recordedAt (Aufnahmedatum), backfilled from createdAt.
  3: addRecordedAt,
  // v4: added phaseNumber (per-record phase override), empty = auto.
  4: (oldDoc) => fillMissing(oldDoc, { phaseNumber: "" }),
  // v5: took over the spatial + stratigraphy fields from the Befund.
  5: addSpatialStratigraphy,
  // v6: dropped `date` — recordedAt is the single date field.
  6: dropDescriptionDate,
  // v7: added depth (Tiefe), empty for existing rows.
  7: (oldDoc) => fillMissing(oldDoc, { depth: "" }),
  // v8: partOf/inProfileWith merged into identical/contemporary.
  8: mergeLegacyRelations,
  // v9: identicalWith string -> description-ref arrays.
  9: identicalWithToRefs,
  // v10: added editorId (app-user link), empty until re-saved.
  10: (oldDoc) => fillMissing(oldDoc, { editorId: "" }),
  // v11: color + munsellCode scalars -> colors list; the old pair
  // becomes an entry, or none if both were empty. Added to a list the
  // document already pulled, not dropped: typed into an older app, it is
  // the newest value there is (see fillMissing).
  11: (oldDoc) => {
    const color = String(oldDoc.color ?? "");
    const munsellCode = String(oldDoc.munsellCode ?? "");
    const { color: _c, munsellCode: _m, ...rest } = oldDoc;
    if (!color && !munsellCode) {
      return fillMissing(rest, { colors: [] });
    }
    return {
      ...rest,
      colors: withEntry(
        rest.colors,
        { color, munsellCode },
        (left, right) => left.color === right.color && left.munsellCode === right.munsellCode
      )
    };
  },
  // v12: added differenceToPreviousPlanum (Abstich in cm), empty.
  12: (oldDoc) => fillMissing(oldDoc, { differenceToPreviousPlanum: "" }),
  // v13: `inclusions` string -> {inclusion, percentage}[]; split a
  // legacy comma-joined value into one entry per name (empty share).
  13: (oldDoc) => Array.isArray(oldDoc.inclusions) ? (
    // Already the new shape: this doc pulled the server's
    // migrated row while still on the old schema version.
    oldDoc
  ) : {
    ...oldDoc,
    inclusions: String(oldDoc.inclusions ?? "").split(",").map((name) => name.trim()).filter(Boolean).map((inclusion) => ({ inclusion, percentage: "" }))
  },
  /**
   * v14: "Teil von" and "Im Profil mit" split back out into their
   * own reference pairs; nothing to move.
   */
  14: splitOutPartOfAndInProfile,
  /** v15: added `strata` — the Schichten with their own measures. */
  15: addStrata,
  /** v16: a seeded Schicht row remembers its mark and name. */
  16: keepStrata,
  /**
   * v17: added `partLabel` — the Teil mark that tells two
   * descriptions of the same Planum apart (see descriptionPart).
   */
  17: addPartLabel,
  // v18: the fields a baulicher Bestand records.
  18: addStructureFields,
  // v19: longer Farbe/Munsell entries. Only the cap moved.
  19: (oldDoc) => oldDoc,
  // v20: a Schicht may carry a Durchmesser; optional, nothing to move.
  20: (oldDoc) => oldDoc
};
var PROFILE_MIGRATIONS = {
  // v1: added recordedAt (Aufnahmedatum), backfilled from createdAt.
  1: addRecordedAt,
  // v2: added phaseNumber (per-record phase override), empty = auto.
  2: (oldDoc) => fillMissing(oldDoc, { phaseNumber: "" }),
  // v3: took over the spatial + stratigraphy fields from the Befund.
  3: addSpatialStratigraphy,
  // v4: dropped `date` — recordedAt is the single date field.
  4: dropDescriptionDate,
  // v5: partOf/inProfileWith merged into identical/contemporary.
  5: mergeLegacyRelations,
  // v6: identicalWith string -> description-ref arrays.
  6: identicalWithToRefs,
  // v7: added editorId (app-user link), empty until re-saved.
  7: (oldDoc) => fillMissing(oldDoc, { editorId: "" }),
  // v8: see the Planum's v14 — the two relations split back out.
  8: splitOutPartOfAndInProfile,
  /**
   * v9: took over the Bodenmerkmale for the Befund that is only
   * visible in section; the Planum kept them until now.
   */
  9: (oldDoc) => fillMissing(oldDoc, {
    colors: [],
    soilType: "",
    inclusions: [],
    consistency: "",
    boundaryClarity: "",
    boundaryBasis: "",
    contour: "",
    humusContent: ""
  }),
  // v10: added `strata` — see the Planum's v15.
  10: addStrata,
  // v11: a seeded Schicht row remembers its mark — see Planum v16.
  11: keepStrata,
  // v12: added `partLabel` — see the Planum's v17.
  12: addPartLabel,
  // v13: baulicher Bestand fields — see the Planum's v18.
  13: addStructureFields,
  // v14: longer Farbe/Munsell entries — see the Planum's v19.
  14: (oldDoc) => oldDoc
};

// src/entities/finding.ts
var FINDING_NUMBER_MAX = 50;
var SHORT_DESCRIPTION_MAX = 100;
var DATING_MAX = 100;
var TEXT_FIELD_MAX2 = 255;
var FINDING_TYPE_MAX = 20;
var SHORT_DESCRIPTION_OPTIONS = [
  "Auff\xFCllung",
  "Brandgrab",
  "Brandsch\xFCttungsgrab",
  "Estrich",
  "Fundament",
  "Geologie",
  "Graben",
  "Grube",
  "Grubenhaus",
  "Grubenkomplex",
  "K\xF6rpergrab",
  "Mauer",
  "Ofen",
  "Pflaster",
  "Pfostengrube",
  "Planierschicht",
  "Rollierung",
  "Siedlungsgrube",
  "Steinpackung",
  "Urnengrab",
  "Verf\xFCllung",
  "Wandgr\xE4bchen"
];
var INTERPRETATION_OPTIONS = [
  "Grube",
  "Abfallgrube",
  "Vorratsgrube",
  "Speichergrube",
  "Siedlungsgrube",
  "Materialentnahmegrube",
  "Lehmentnahmegrube",
  "Sandentnahmegrube",
  "Schlackengrube",
  "Grube (Funktion unklar)",
  "Pfostengrube",
  "Pfostenstandspur",
  "Pfostengrube mit Standspuren",
  "Graben",
  "Sohlgraben",
  "Spitzgraben",
  "Randgraben",
  "Entw\xE4sserungsgraben",
  "Befestigungsgraben",
  "Drainage",
  "Grab",
  "K\xF6rpergrab",
  "Brandgrab",
  "Brandbestattung",
  "Urnenbestattung",
  "Kindergrab",
  "Grabgrube",
  "Hausgrundriss",
  "Geb\xE4udegrundriss",
  "Grubenhaus",
  "Keller",
  "Mauerrest",
  "Fundament",
  "Steinpflaster",
  "Herd",
  "Feuerstelle",
  "Backofen",
  "Ofen",
  "Brennofen",
  "Brunnen",
  "Zisterne",
  "Wasserleitung",
  "Kulturschicht",
  "Planierschicht",
  "Ascheschicht",
  "Brandschicht",
  "Bodenverf\xE4rbung",
  "Verf\xFCllung",
  "Werkplatz",
  "Meiler",
  "Hortfund",
  "Tiergang",
  "Baumwurf",
  "nat\xFCrliche Struktur",
  "St\xF6rung",
  "neuzeitliche St\xF6rung",
  "Befund unklar"
];
var PRELIMINARY_DATING_OPTIONS = VORLAEUFIGE_DATIERUNG;
var DATING_BASIS_OPTIONS = ["Fund", "Stratigraphie"];
var FINDING_SCHEMA_VERSION = 6;
var findingSchema = {
  version: FINDING_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    findingNumber: { type: "string", maxLength: FINDING_NUMBER_MAX },
    // Befundbeschreibung or baulicher Bestand — see FINDING_TYPE_OPTIONS.
    findingType: {
      type: "string",
      maxLength: FINDING_TYPE_MAX,
      default: DEFAULT_FINDING_TYPE
    },
    shortDescription: { type: "string", maxLength: SHORT_DESCRIPTION_MAX },
    interpretation: { type: "string", maxLength: TEXT_FIELD_MAX2 },
    datingBasis: { type: "string", maxLength: TEXT_FIELD_MAX2 },
    preliminaryDating: { type: "string", maxLength: DATING_MAX },
    remark: { type: "string" },
    /**
     * Some Befunde never show in plan, only in section. The flag documents
     * WHY there is no Planumsblatt, and turns on the Bodenmerkmale in the
     * Profil form — the fields the Planum would otherwise have carried.
     */
    onlyVisibleInProfile: { type: "boolean", default: false },
    recordedAt: { type: "string", maxLength: RECORDED_AT_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "findingNumber",
    "findingType",
    "shortDescription",
    "interpretation",
    "datingBasis",
    "preliminaryDating",
    "remark",
    "onlyVisibleInProfile",
    "recordedAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId"]
};
var findingInputSchema = z10.object({
  findingNumber: z10.string().trim().min(1, rule("required")).max(
    FINDING_NUMBER_MAX,
    rule("maxLength", { max: FINDING_NUMBER_MAX })
  ),
  findingType: z10.enum(FINDING_TYPE_OPTIONS).default(DEFAULT_FINDING_TYPE),
  shortDescription: z10.string().max(SHORT_DESCRIPTION_MAX).default(""),
  interpretation: z10.string().max(TEXT_FIELD_MAX2).default(""),
  datingBasis: z10.string().max(TEXT_FIELD_MAX2).default(""),
  preliminaryDating: z10.string().max(DATING_MAX).default(""),
  remark: z10.string().max(DESCRIPTION_MAX).default(""),
  onlyVisibleInProfile: z10.boolean().default(false),
  recordedAt: recordedAtField()
});
var findingEntity = defineEntity({
  name: "finding",
  apiBase: "/api/findings",
  collection: "findings",
  rxSchema: findingSchema,
  migrations: FINDING_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    findingNumber: { maxLength: FINDING_NUMBER_MAX, merge: true },
    findingType: { maxLength: FINDING_TYPE_MAX, merge: true },
    shortDescription: { maxLength: SHORT_DESCRIPTION_MAX, merge: true },
    interpretation: { maxLength: TEXT_FIELD_MAX2, merge: true },
    datingBasis: { maxLength: TEXT_FIELD_MAX2, merge: true },
    preliminaryDating: { maxLength: DATING_MAX, merge: true },
    remark: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    onlyVisibleInProfile: { kind: "boolean", merge: true },
    recordedAt: { kind: "datetime", merge: true }
  },
  inputSchema: findingInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      findingNumber: row.findingNumber || "",
      findingType: clampFindingType(row.findingType),
      shortDescription: row.shortDescription || "",
      interpretation: row.interpretation || "",
      datingBasis: row.datingBasis || "",
      preliminaryDating: row.preliminaryDating || "",
      remark: row.remark || "",
      onlyVisibleInProfile: Boolean(row.onlyVisibleInProfile),
      recordedAt: row.recordedAt || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      findingNumber: doc.findingNumber,
      findingType: clampFindingType(doc.findingType),
      shortDescription: doc.shortDescription || "",
      interpretation: doc.interpretation || "",
      datingBasis: doc.datingBasis || "",
      preliminaryDating: doc.preliminaryDating || "",
      remark: doc.remark || "",
      onlyVisibleInProfile: Boolean(doc.onlyVisibleInProfile),
      recordedAt: doc.recordedAt || ""
    };
  }
});

// src/entities/findingPlanum.ts
import { z as z12 } from "zod";

// src/entities/descriptionPart.ts
import { z as z11 } from "zod";
var PART_LABEL_MAX = 30;
var FORBIDDEN_IN_PART_LABEL = /[\s,;]/;
function isValidPartLabel(value) {
  return !FORBIDDEN_IN_PART_LABEL.test(value);
}
function partLabelField() {
  return z11.string().trim().max(PART_LABEL_MAX, rule("maxLength", { max: PART_LABEL_MAX })).refine(isValidPartLabel, rule("partLabelFormat")).default("");
}
var PLANUM_PART_OPTIONS = Array.from(
  { length: 26 },
  (_, index) => String.fromCharCode(65 + index)
);
var PROFILE_PART_OPTIONS = Array.from(
  { length: 12 },
  (_, index) => String(index + 1)
);

// src/entities/munsellSoilColors.ts
var SOIL_CHART_LABELS = {
  // 5R
  "5R 2.5/1": "r\xF6tlichschwarz",
  "5R 2.5/2": "sehr tr\xFCbrot",
  "5R 2.5/3": "sehr tr\xFCbrot",
  "5R 2.5/4": "sehr tr\xFCbrot",
  "5R 2.5/6": "dunkelrot",
  "5R 3/1": "dunkelr\xF6tlichgrau",
  "5R 3/2": "tr\xFCbrot",
  "5R 3/3": "tr\xFCbrot",
  "5R 3/4": "tr\xFCbrot",
  "5R 3/6": "dunkelrot",
  "5R 3/8": "dunkelrot",
  "5R 4/1": "dunkelr\xF6tlichgrau",
  "5R 4/2": "mattrot",
  "5R 4/3": "mattrot",
  "5R 4/4": "mattrot",
  "5R 4/6": "rot",
  "5R 4/8": "rot",
  "5R 5/1": "r\xF6tlichgrau",
  "5R 5/2": "mattrot",
  "5R 5/3": "mattrot",
  "5R 5/4": "mattrot",
  "5R 5/6": "rot",
  "5R 5/8": "rot",
  "5R 6/1": "r\xF6tlichgrau",
  "5R 6/2": "blassrot",
  "5R 6/3": "blassrot",
  "5R 6/4": "blassrot",
  "5R 6/6": "hellrot",
  "5R 6/8": "hellrot",
  "5R 7/1": "rosagrau",
  "5R 7/2": "blassrot",
  "5R 7/3": "blassrot",
  "5R 7/4": "blassrot",
  "5R 7/6": "hellrot",
  "5R 7/8": "hellrot",
  "5R 8/1": "wei\xDF",
  "5R 8/2": "hellrosa",
  "5R 8/3": "hellrosa",
  "5R 8/4": "hellrosa",
  // 7.5R
  "7.5R 2.5/1": "r\xF6tlichschwarz",
  "7.5R 2.5/2": "sehr tr\xFCbrot",
  "7.5R 2.5/3": "sehr tr\xFCbrot",
  "7.5R 2.5/4": "sehr tr\xFCbrot",
  "7.5R 3/1": "dunkelr\xF6tlichgrau",
  "7.5R 3/2": "tr\xFCbrot",
  "7.5R 3/3": "tr\xFCbrot",
  "7.5R 3/4": "tr\xFCbrot",
  "7.5R 3/6": "dunkelrot",
  "7.5R 3/8": "dunkelrot",
  "7.5R 4/1": "dunkelr\xF6tlichgrau",
  "7.5R 4/2": "mattrot",
  "7.5R 4/3": "mattrot",
  "7.5R 4/4": "mattrot",
  "7.5R 4/6": "rot",
  "7.5R 4/8": "rot",
  "7.5R 5/1": "r\xF6tlichgrau",
  "7.5R 5/2": "mattrot",
  "7.5R 5/3": "mattrot",
  "7.5R 5/4": "mattrot",
  "7.5R 5/6": "rot",
  "7.5R 5/8": "rot",
  "7.5R 6/1": "r\xF6tlichgrau",
  "7.5R 6/2": "blassrot",
  "7.5R 6/3": "blassrot",
  "7.5R 6/4": "blassrot",
  "7.5R 6/6": "hellrot",
  "7.5R 6/8": "hellrot",
  "7.5R 7/1": "rosagrau",
  "7.5R 7/2": "blassrot",
  "7.5R 7/3": "blassrot",
  "7.5R 7/4": "blassrot",
  "7.5R 7/6": "hellrot",
  "7.5R 7/8": "hellrot",
  "7.5R 8/1": "wei\xDF",
  "7.5R 8/2": "hellrosa",
  "7.5R 8/3": "hellrosa",
  "7.5R 8/4": "hellrosa",
  // 10R
  "10R 2.5/1": "r\xF6tlichschwarz",
  "10R 2.5/2": "sehr tr\xFCbrot",
  "10R 3/1": "dunkelr\xF6tlichgrau",
  "10R 3/2": "tr\xFCbrot",
  "10R 3/3": "tr\xFCbrot",
  "10R 3/4": "tr\xFCbrot",
  "10R 3/6": "dunkelrot",
  "10R 4/1": "dunkelr\xF6tlichgrau",
  "10R 4/2": "mattrot",
  "10R 4/3": "mattrot",
  "10R 4/4": "mattrot",
  "10R 4/6": "rot",
  "10R 4/8": "rot",
  "10R 5/1": "r\xF6tlichgrau",
  "10R 5/2": "mattrot",
  "10R 5/3": "mattrot",
  "10R 5/4": "mattrot",
  "10R 5/6": "rot",
  "10R 5/8": "rot",
  "10R 6/1": "r\xF6tlichgrau",
  "10R 6/2": "blassrot",
  "10R 6/3": "blassrot",
  "10R 6/4": "blassrot",
  "10R 6/6": "hellrot",
  "10R 6/8": "hellrot",
  "10R 7/1": "hellgrau",
  "10R 7/2": "blassrot",
  "10R 7/3": "blassrot",
  "10R 7/4": "blassrot",
  "10R 7/6": "hellrot",
  "10R 7/8": "hellrot",
  "10R 8/1": "wei\xDF",
  "10R 8/2": "rosawei\xDF",
  "10R 8/3": "rosa",
  "10R 8/4": "rosa",
  // 2.5YR
  "2.5YR 2.5/1": "r\xF6tlichschwarz",
  "2.5YR 2.5/2": "sehr tr\xFCbrot",
  "2.5YR 2.5/3": "dunkelr\xF6tlichbraun",
  "2.5YR 2.5/4": "dunkelr\xF6tlichbraun",
  "2.5YR 3/1": "dunkelr\xF6tlichgrau",
  "2.5YR 3/2": "tr\xFCbrot",
  "2.5YR 3/3": "dunkelr\xF6tlichbraun",
  "2.5YR 3/4": "dunkelr\xF6tlichbraun",
  "2.5YR 3/6": "dunkelrot",
  "2.5YR 4/1": "dunkelr\xF6tlichgrau",
  "2.5YR 4/2": "mattrot",
  "2.5YR 4/3": "r\xF6tlichbraun",
  "2.5YR 4/4": "r\xF6tlichbraun",
  "2.5YR 4/6": "rot",
  "2.5YR 4/8": "rot",
  "2.5YR 5/1": "r\xF6tlichgrau",
  "2.5YR 5/2": "mattrot",
  "2.5YR 5/3": "r\xF6tlichbraun",
  "2.5YR 5/4": "r\xF6tlichbraun",
  "2.5YR 5/6": "rot",
  "2.5YR 5/8": "rot",
  "2.5YR 6/1": "r\xF6tlichgrau",
  "2.5YR 6/2": "blassrot",
  "2.5YR 6/3": "hellr\xF6tlichbraun",
  "2.5YR 6/4": "hellr\xF6tlichbraun",
  "2.5YR 6/6": "hellrot",
  "2.5YR 6/8": "hellrot",
  "2.5YR 7/1": "hellr\xF6tlichgrau",
  "2.5YR 7/2": "blassrot",
  "2.5YR 7/3": "hellr\xF6tlichbraun",
  "2.5YR 7/4": "hellr\xF6tlichbraun",
  "2.5YR 7/6": "hellrot",
  "2.5YR 7/8": "hellrot",
  "2.5YR 8/1": "wei\xDF",
  "2.5YR 8/2": "rosawei\xDF",
  "2.5YR 8/3": "rosa",
  "2.5YR 8/4": "rosa",
  // 5YR
  "5YR 2.5/1": "schwarz",
  "5YR 2.5/2": "dunkelr\xF6tlichbraun",
  "5YR 3/1": "sehr dunkelgrau",
  "5YR 3/2": "dunkelr\xF6tlichbraun",
  "5YR 3/3": "dunkelr\xF6tlichbraun",
  "5YR 3/4": "dunkelr\xF6tlichbraun",
  "5YR 4/1": "dunkelgrau",
  "5YR 4/2": "dunkelr\xF6tlichgrau",
  "5YR 4/3": "r\xF6tlichbraun",
  "5YR 4/4": "r\xF6tlichbraun",
  "5YR 4/6": "gelblichrot",
  "5YR 5/1": "grau",
  "5YR 5/2": "r\xF6tlichgrau",
  "5YR 5/3": "r\xF6tlichbraun",
  "5YR 5/4": "r\xF6tlichbraun",
  "5YR 5/6": "gelblichrot",
  "5YR 5/8": "gelblichrot",
  "5YR 6/1": "grau",
  "5YR 6/2": "rosagrau",
  "5YR 6/3": "hellr\xF6tlichbraun",
  "5YR 6/4": "hellr\xF6tlichbraun",
  "5YR 6/6": "r\xF6tlichgelb",
  "5YR 6/8": "r\xF6tlichgelb",
  "5YR 7/1": "hellgrau",
  "5YR 7/2": "rosagrau",
  "5YR 7/3": "rosa",
  "5YR 7/4": "rosa",
  "5YR 7/6": "r\xF6tlichgelb",
  "5YR 7/8": "r\xF6tlichgelb",
  "5YR 8/1": "wei\xDF",
  "5YR 8/2": "rosawei\xDF",
  "5YR 8/3": "rosa",
  "5YR 8/4": "rosa",
  // 7.5YR
  "7.5YR 2.5/1": "schwarz",
  "7.5YR 2.5/2": "sehr dunkelbraun",
  "7.5YR 2.5/3": "sehr dunkelbraun",
  "7.5YR 3/1": "sehr dunkelgrau",
  "7.5YR 3/2": "dunkelbraun",
  "7.5YR 3/3": "dunkelbraun",
  "7.5YR 3/4": "dunkelbraun",
  "7.5YR 4/1": "dunkelgrau",
  "7.5YR 4/2": "braun",
  "7.5YR 4/3": "braun",
  "7.5YR 4/4": "braun",
  "7.5YR 4/6": "kr\xE4ftig braun",
  "7.5YR 5/1": "grau",
  "7.5YR 5/2": "braun",
  "7.5YR 5/3": "braun",
  "7.5YR 5/4": "braun",
  "7.5YR 5/6": "kr\xE4ftig braun",
  "7.5YR 5/8": "kr\xE4ftig braun",
  "7.5YR 6/1": "grau",
  "7.5YR 6/2": "rosagrau",
  "7.5YR 6/3": "hellbraun",
  "7.5YR 6/4": "hellbraun",
  "7.5YR 6/6": "r\xF6tlichgelb",
  "7.5YR 6/8": "r\xF6tlichgelb",
  "7.5YR 7/1": "hellgrau",
  "7.5YR 7/2": "rosagrau",
  "7.5YR 7/3": "rosa",
  "7.5YR 7/4": "rosa",
  "7.5YR 7/6": "r\xF6tlichgelb",
  "7.5YR 7/8": "r\xF6tlichgelb",
  "7.5YR 8/1": "wei\xDF",
  "7.5YR 8/2": "rosawei\xDF",
  "7.5YR 8/3": "rosa",
  "7.5YR 8/4": "rosa",
  "7.5YR 8/6": "r\xF6tlichgelb",
  "7.5YR 8.5/1": "wei\xDF",
  "7.5YR 8.5/2": "rosawei\xDF",
  "7.5YR 9/1": "wei\xDF",
  "7.5YR 9/2": "blassgelblichrosa",
  "7.5YR 9.5/1": "wei\xDF",
  "7.5YR 9.5/2": "blassgelblichrosa",
  // 10YR
  "10YR 2/1": "schwarz",
  "10YR 2/2": "sehr dunkelbraun",
  "10YR 3/1": "sehr dunkelgrau",
  "10YR 3/2": "sehr dunkelgraubraun",
  "10YR 3/3": "dunkelbraun",
  "10YR 3/4": "dunkelgelblichbraun",
  "10YR 3/6": "dunkelgelblichbraun",
  "10YR 4/1": "dunkelgrau",
  "10YR 4/2": "dunkelgraubraun",
  "10YR 4/3": "braun",
  "10YR 4/4": "dunkelgelblichbraun",
  "10YR 4/6": "dunkelgelblichbraun",
  "10YR 5/1": "grau",
  "10YR 5/2": "graubraun",
  "10YR 5/3": "braun",
  "10YR 5/4": "gelblichbraun",
  "10YR 5/6": "gelblichbraun",
  "10YR 5/8": "gelblichbraun",
  "10YR 6/1": "grau",
  "10YR 6/2": "hellbr\xE4unlichgrau",
  "10YR 6/3": "blassbraun",
  "10YR 6/4": "hellgelblichbraun",
  "10YR 6/6": "br\xE4unlichgelb",
  "10YR 6/8": "br\xE4unlichgelb",
  "10YR 7/1": "hellgrau",
  "10YR 7/2": "hellgrau",
  "10YR 7/3": "sehr blassbraun",
  "10YR 7/4": "sehr blassbraun",
  "10YR 7/6": "gelb",
  "10YR 7/8": "gelb",
  "10YR 8/1": "wei\xDF",
  "10YR 8/2": "sehr blassbraun",
  "10YR 8/3": "sehr blassbraun",
  "10YR 8/4": "sehr blassbraun",
  "10YR 8/6": "gelb",
  "10YR 8/8": "gelb",
  "10YR 8.5/1": "wei\xDF",
  "10YR 8.5/2": "sehr blassbraun",
  "10YR 9/1": "wei\xDF",
  "10YR 9/2": "blassorangegelb",
  "10YR 9.5/1": "wei\xDF",
  "10YR 9.5/2": "blassorangegelb",
  // 2.5Y
  "2.5Y 2.5/1": "schwarz",
  "2.5Y 3/1": "sehr dunkelgrau",
  "2.5Y 3/2": "sehr dunkelgraubraun",
  "2.5Y 3/3": "dunkelolivbraun",
  "2.5Y 4/1": "dunkelgrau",
  "2.5Y 4/2": "dunkelgraubraun",
  "2.5Y 4/3": "olivbraun",
  "2.5Y 4/4": "olivbraun",
  "2.5Y 4/6": "olivbraun",
  "2.5Y 5/1": "grau",
  "2.5Y 5/2": "graubraun",
  "2.5Y 5/3": "hellolivbraun",
  "2.5Y 5/4": "hellolivbraun",
  "2.5Y 5/6": "hellolivbraun",
  "2.5Y 6/1": "grau",
  "2.5Y 6/2": "hellbr\xE4unlichgrau",
  "2.5Y 6/3": "hellgelblichbraun",
  "2.5Y 6/4": "hellgelblichbraun",
  "2.5Y 6/6": "olivgelb",
  "2.5Y 6/8": "olivgelb",
  "2.5Y 7/1": "hellgrau",
  "2.5Y 7/2": "hellgrau",
  "2.5Y 7/3": "blassbraun",
  "2.5Y 7/4": "blassbraun",
  "2.5Y 7/6": "gelb",
  "2.5Y 7/8": "gelb",
  "2.5Y 8/1": "wei\xDF",
  "2.5Y 8/2": "blassbraun",
  "2.5Y 8/3": "blassbraun",
  "2.5Y 8/4": "blassbraun",
  "2.5Y 8/6": "gelb",
  "2.5Y 8/8": "gelb",
  "2.5Y 8.5/1": "wei\xDF",
  "2.5Y 8.5/2": "blassgelb",
  "2.5Y 9/1": "wei\xDF",
  "2.5Y 9/2": "sehr blassgelb",
  "2.5Y 9.5/1": "wei\xDF",
  "2.5Y 9.5/2": "sehr blassgelb",
  // 5Y
  "5Y 2.5/1": "schwarz",
  "5Y 2.5/2": "schwarz",
  "5Y 3/1": "sehr dunkelgrau",
  "5Y 3/2": "dunkelolivgrau",
  "5Y 4/1": "dunkelgrau",
  "5Y 4/2": "olivgrau",
  "5Y 4/3": "oliv",
  "5Y 4/4": "oliv",
  "5Y 5/1": "grau",
  "5Y 5/2": "olivgrau",
  "5Y 5/3": "oliv",
  "5Y 5/4": "oliv",
  "5Y 5/6": "oliv",
  "5Y 6/1": "grau",
  "5Y 6/2": "hellolivgrau",
  "5Y 6/3": "blassoliv",
  "5Y 6/4": "blassoliv",
  "5Y 6/6": "olivgelb",
  "5Y 6/8": "olivgelb",
  "5Y 7/1": "hellgrau",
  "5Y 7/2": "hellgrau",
  "5Y 7/3": "blassgelb",
  "5Y 7/4": "blassgelb",
  "5Y 7/6": "gelb",
  "5Y 7/8": "gelb",
  "5Y 8/1": "wei\xDF",
  "5Y 8/2": "blassgelb",
  "5Y 8/3": "blassgelb",
  "5Y 8/4": "blassgelb",
  "5Y 8/6": "gelb",
  "5Y 8/8": "gelb",
  // 10Y
  "10Y 2.5/1": "gr\xFCnlichschwarz",
  "10Y 3/1": "sehr dunkelgr\xFCnlichgrau",
  "10Y 3/2": "sehr dunkelgrauoliv",
  "10Y 3/4": "dunkeloliv",
  "10Y 4/1": "dunkelgr\xFCnlichgrau",
  "10Y 4/2": "dunkelgrauoliv",
  "10Y 4/4": "oliv",
  "10Y 5/1": "gr\xFCnlichgrau",
  "10Y 5/2": "grauoliv",
  "10Y 5/4": "helloliv",
  "10Y 6/1": "gr\xFCnlichgrau",
  "10Y 6/2": "hellgrauoliv",
  "10Y 6/4": "blassoliv",
  "10Y 7/1": "hellgr\xFCnlichgrau",
  "10Y 8/1": "hellgr\xFCnlichgrau",
  // 5GY
  "5GY 2.5/1": "gr\xFCnlichschwarz",
  "5GY 3/1": "sehr dunkelgr\xFCnlichgrau",
  "5GY 3/2": "sehr dunkelgraugr\xFCn",
  "5GY 3/4": "dunkelolivgr\xFCn",
  "5GY 4/1": "dunkelgr\xFCnlichgrau",
  "5GY 4/2": "dunkelgraugr\xFCn",
  "5GY 4/4": "olivgr\xFCn",
  "5GY 5/1": "gr\xFCnlichgrau",
  "5GY 5/2": "graugr\xFCn",
  "5GY 5/4": "hellolivgr\xFCn",
  "5GY 6/1": "gr\xFCnlichgrau",
  "5GY 6/2": "hellgraugr\xFCn",
  "5GY 6/4": "blassgelblichgr\xFCn",
  "5GY 7/1": "hellgr\xFCnlichgrau",
  "5GY 8/1": "hellgr\xFCnlichgrau",
  // 10GY
  "10GY 2.5/1": "gr\xFCnlichschwarz",
  "10GY 3/1": "sehr dunkelgr\xFCnlichgrau",
  "10GY 4/1": "dunkelgr\xFCnlichgrau",
  "10GY 5/1": "gr\xFCnlichgrau",
  "10GY 6/1": "gr\xFCnlichgrau",
  "10GY 7/1": "hellgr\xFCnlichgrau",
  "10GY 8/1": "hellgr\xFCnlichgrau",
  // 5G
  "5G 2.5/1": "gr\xFCnlichschwarz",
  "5G 2.5/2": "sehr dunkelgraugr\xFCn",
  "5G 3/1": "sehr dunkelgr\xFCnlichgrau",
  "5G 3/2": "sehr dunkelgraugr\xFCn",
  "5G 4/1": "dunkelgr\xFCnlichgrau",
  "5G 4/2": "graugr\xFCn",
  "5G 5/1": "gr\xFCnlichgrau",
  "5G 5/2": "graugr\xFCn",
  "5G 6/1": "gr\xFCnlichgrau",
  "5G 6/2": "blassgr\xFCn",
  "5G 7/1": "hellgr\xFCnlichgrau",
  "5G 7/2": "blassgr\xFCn",
  "5G 8/1": "hellgr\xFCnlichgrau",
  "5G 8/2": "blassgr\xFCn",
  // 10G
  "10G 2.5/1": "gr\xFCnlichschwarz",
  "10G 3/1": "sehr dunkelgr\xFCnlichgrau",
  "10G 4/1": "dunkelgr\xFCnlichgrau",
  "10G 5/1": "gr\xFCnlichgrau",
  "10G 6/1": "gr\xFCnlichgrau",
  "10G 7/1": "hellgr\xFCnlichgrau",
  "10G 8/1": "hellgr\xFCnlichgrau",
  // 5BG
  "5BG 2.5/1": "gr\xFCnlichschwarz",
  "5BG 3/1": "sehr dunkelgr\xFCnlichgrau",
  "5BG 4/1": "dunkelgr\xFCnlichgrau",
  "5BG 5/1": "gr\xFCnlichgrau",
  "5BG 6/1": "gr\xFCnlichgrau",
  "5BG 7/1": "hellgr\xFCnlichgrau",
  "5BG 8/1": "hellgr\xFCnlichgrau",
  // 10BG
  "10BG 2.5/1": "gr\xFCnlichschwarz",
  "10BG 3/1": "sehr dunkelgr\xFCnlichgrau",
  "10BG 4/1": "dunkelgr\xFCnlichgrau",
  "10BG 5/1": "gr\xFCnlichgrau",
  "10BG 6/1": "gr\xFCnlichgrau",
  "10BG 7/1": "hellgr\xFCnlichgrau",
  "10BG 8/1": "hellgr\xFCnlichgrau",
  // 5B
  "5B 2.5/1": "bl\xE4ulichschwarz",
  "5B 3/1": "sehr dunkelbl\xE4ulichgrau",
  "5B 4/1": "dunkelbl\xE4ulichgrau",
  "5B 5/1": "bl\xE4ulichgrau",
  "5B 6/1": "bl\xE4ulichgrau",
  "5B 7/1": "hellbl\xE4ulichgrau",
  "5B 8/1": "hellbl\xE4ulichgrau",
  // 10B
  "10B 2.5/1": "bl\xE4ulichschwarz",
  "10B 3/1": "sehr dunkelbl\xE4ulichgrau",
  "10B 4/1": "dunkelbl\xE4ulichgrau",
  "10B 5/1": "bl\xE4ulichgrau",
  "10B 6/1": "bl\xE4ulichgrau",
  "10B 7/1": "hellbl\xE4ulichgrau",
  "10B 8/1": "hellbl\xE4ulichgrau",
  // 5PB
  "5PB 2.5/1": "bl\xE4ulichschwarz",
  "5PB 3/1": "sehr dunkelbl\xE4ulichgrau",
  "5PB 4/1": "dunkelbl\xE4ulichgrau",
  "5PB 5/1": "bl\xE4ulichgrau",
  "5PB 6/1": "bl\xE4ulichgrau",
  "5PB 7/1": "hellbl\xE4ulichgrau",
  "5PB 8/1": "hellbl\xE4ulichgrau",
  // N
  "N 2.5/": "schwarz",
  "N 3/": "sehr dunkelgrau",
  "N 4/": "dunkelgrau",
  "N 5/": "grau",
  "N 6/": "grau",
  "N 7/": "hellgrau",
  "N 8/": "wei\xDF",
  "N 8.5/": "wei\xDF",
  "N 9/": "wei\xDF",
  "N 9.5/": "wei\xDF"
};
var LEGACY_LABELS = {
  "7.5YR 2/0": "schwarz",
  "N 2/0": "schwarz",
  "N 3/0": "sehr dunkelgrau",
  "N 4/0": "dunkelgrau",
  "N 5/0": "grau",
  "N 6/0": "grau",
  "N 7/0": "hellgrau",
  "N 8/0": "wei\xDF"
};
var MUNSELL_CODE_LABELS = {
  ...SOIL_CHART_LABELS,
  ...LEGACY_LABELS
};
var MUNSELL_CODE_OPTIONS = Object.keys(SOIL_CHART_LABELS);

// src/entities/findingPlanum.ts
var COLOR_OPTIONS = [
  "hellbraun",
  "mittelbraun",
  "dunkelbraun",
  "graubraun",
  "gelbbraun",
  "rotbraun",
  "schwarzbraun",
  "hellgrau",
  "dunkelgrau",
  "grau",
  "braun",
  "r\xF6tlich",
  "gelblich",
  "schw\xE4rzlich",
  "beige",
  "ocker"
];
var MAX_COLORS = 20;
function clampColors(value) {
  const out = [];
  for (const item of parseJsonArrayRaw(value)) {
    if (out.length >= MAX_COLORS) {
      break;
    }
    const obj = item ?? {};
    const color = clampString(obj.color, MEDIUM_TEXT_MAX);
    const munsellCode = clampString(obj.munsellCode, MEDIUM_TEXT_MAX);
    if (color === "" && munsellCode === "") {
      continue;
    }
    out.push({ color, munsellCode });
  }
  return out;
}
var MAX_INCLUSIONS = 20;
function clampInclusions(value) {
  const out = [];
  for (const item of parseJsonArrayRaw(value)) {
    if (out.length >= MAX_INCLUSIONS) {
      break;
    }
    const obj = item ?? {};
    const inclusion = clampString(obj.inclusion, SHORT_TEXT_MAX);
    const percentage = clampString(obj.percentage, MEASURE_MAX);
    if (inclusion === "" && percentage === "") {
      continue;
    }
    out.push({ inclusion, percentage });
  }
  return out;
}
function formatInclusionShare(share) {
  const text3 = String(share ?? "").trim();
  if (!text3 || text3.includes("%") || !/\d$/.test(text3)) {
    return text3;
  }
  return `${text3}%`;
}
function parseInclusionShare(share) {
  const text3 = String(share ?? "").trim();
  const bare = /^(.*\d)\s*%$/.exec(text3);
  return bare ? bare[1].trim() : text3;
}
function formatInclusions(list) {
  return (list ?? []).map((entry) => {
    const share = formatInclusionShare(entry.percentage);
    if (entry.inclusion && share) {
      return `${entry.inclusion} (${share})`;
    }
    if (share) {
      return `(${share})`;
    }
    return entry.inclusion;
  }).filter(Boolean).join("; ");
}
var TRAILING_SHARE = /^(.+?)\s+((?:ca\.?\s*)?[<>~≈≥≤]?\s*\d+(?:[.,]\d+)?\s*%)$/;
function parseInclusionEntry(part) {
  const text3 = part.trim();
  const paren = /^(.*?)\s*\(([^)]*)\)\s*$/.exec(text3);
  if (paren) {
    return {
      inclusion: paren[1].trim(),
      percentage: parseInclusionShare(paren[2])
    };
  }
  const trailing = TRAILING_SHARE.exec(text3);
  if (trailing) {
    return {
      inclusion: trailing[1].trim(),
      percentage: parseInclusionShare(trailing[2])
    };
  }
  return { inclusion: text3, percentage: "" };
}
function parseInclusions(raw) {
  const text3 = String(raw ?? "").trim();
  if (!text3) {
    return [];
  }
  const separator = text3.includes(";") || text3.includes("(") ? ";" : ",";
  return text3.split(separator).map(parseInclusionEntry).filter((entry) => entry.inclusion !== "" || entry.percentage !== "").slice(0, MAX_INCLUSIONS);
}
var MAX_STRATA = 40;
function clampStrata(value) {
  const out = [];
  for (const item of parseJsonArrayRaw(value)) {
    if (out.length >= MAX_STRATA) {
      break;
    }
    const obj = item ?? {};
    const entry = {
      name: clampString(obj.name, SHORT_TEXT_MAX),
      length: clampString(obj.length, MEASURE_MAX),
      width: clampString(obj.width, MEASURE_MAX),
      depth: clampString(obj.depth, MEASURE_MAX)
    };
    const diameter = clampString(obj.diameter, MEASURE_MAX);
    if (entry.name === "" && entry.length === "" && entry.width === "" && entry.depth === "" && diameter === "") {
      continue;
    }
    if (diameter !== "") {
      entry.diameter = diameter;
    }
    const sourceId = clampString(obj.sourceId, ID_MAX);
    if (sourceId !== "") {
      entry.sourceId = sourceId;
    }
    const seededName = clampString(obj.seededName, SHORT_TEXT_MAX);
    if (seededName !== "") {
      entry.seededName = seededName;
    }
    out.push(entry);
  }
  return out;
}
function formatStrata(list) {
  return (list ?? []).map((entry) => {
    const measures = [
      entry.length ? `L ${entry.length} cm` : "",
      entry.width ? `B ${entry.width} cm` : "",
      entry.depth ? `T ${entry.depth} cm` : "",
      entry.diameter ? `Dm ${entry.diameter} cm` : ""
    ].filter(Boolean).join(", ");
    const name = entry.name.trim();
    if (name && measures) {
      return `${name} (${measures})`;
    }
    return name || measures;
  }).filter(Boolean).join("; ");
}
var MAX_COURSES = 40;
var COURSE_MEASURE_FIELDS = [
  "length",
  "width",
  "depth",
  "diameter",
  "heightTop",
  "heightBottom"
];
function emptyCourse() {
  return {
    name: "",
    length: "",
    width: "",
    depth: "",
    diameter: "",
    heightTop: "",
    heightBottom: "",
    levelSheet: ""
  };
}
function clampCourses(value) {
  const out = [];
  for (const item of parseJsonArrayRaw(value)) {
    if (out.length >= MAX_COURSES) {
      break;
    }
    const obj = item ?? {};
    const entry = {
      name: clampString(obj.name, SHORT_TEXT_MAX),
      length: clampString(obj.length, MEASURE_MAX),
      width: clampString(obj.width, MEASURE_MAX),
      depth: clampString(obj.depth, MEASURE_MAX),
      diameter: clampString(obj.diameter, MEASURE_MAX),
      heightTop: clampString(obj.heightTop, MEASURE_MAX),
      heightBottom: clampString(obj.heightBottom, MEASURE_MAX),
      levelSheet: clampString(obj.levelSheet, SHORT_TEXT_MAX)
    };
    if (Object.values(entry).every((field) => field === "")) {
      continue;
    }
    out.push(entry);
  }
  return out;
}
function formatCourses(list) {
  return (list ?? []).map((entry) => {
    const measures = [
      entry.length ? `L ${entry.length} cm` : "",
      entry.width ? `B ${entry.width} cm` : "",
      entry.depth ? `T ${entry.depth} cm` : "",
      entry.diameter ? `D ${entry.diameter} cm` : "",
      entry.heightTop ? `OK ${entry.heightTop} m` : "",
      entry.heightBottom ? `UK ${entry.heightBottom} m` : "",
      entry.levelSheet ? `Niv. ${entry.levelSheet}` : ""
    ].filter(Boolean).join(", ");
    const name = entry.name.trim();
    if (name && measures) {
      return `${name} (${measures})`;
    }
    return name || measures;
  }).filter(Boolean).join("; ");
}
var EXCAVATION_METHOD_OPTIONS = [
  "k\xFCnstliche Schichten",
  "nat\xFCrliche Schichten"
];
var SOIL_TYPE_OPTIONS = [
  "Sand",
  "lehmiger Sand",
  "schluffiger Sand",
  "Lehm",
  "sandiger Lehm",
  "schluffiger Lehm",
  "toniger Lehm",
  "Schluff",
  "sandiger Schluff",
  "toniger Schluff",
  "Ton",
  "schluffiger Ton",
  "Kies",
  "Bauschutt",
  "Holzkohle"
];
var INCLUSION_OPTIONS = [
  "Holzkohle",
  "Holzkohleflitter",
  "gebrannter Lehm",
  "H\xFCttenlehm",
  "Ziegelbruch",
  "M\xF6rtel",
  "Kalk",
  "Steine",
  "Kies",
  "Keramik",
  "Knochen",
  "Schlacke",
  "Bauschutt",
  "Mangan",
  "Eisen"
];
var CONSISTENCY_OPTIONS = [
  "locker",
  "locker-plastisch",
  "plastisch",
  "plastisch-dicht",
  "dicht",
  "dicht-hart",
  "hart"
];
var FORM_OPTIONS = [
  "kreisf\xF6rmig",
  "oval",
  "langoval",
  "rechteckig",
  "quadratisch",
  "trapezf\xF6rmig",
  "dreieckig",
  "nierenf\xF6rmig",
  "halbmondf\xF6rmig",
  "polygonal",
  "linear",
  "formlos",
  "unregelm\xE4\xDFig"
];
var CIRCULAR_FORM = "kreisf\xF6rmig";
function isCircularForm(form) {
  return (form ?? "").trim().toLowerCase() === CIRCULAR_FORM;
}
var ORIENTATION_OPTIONS = [
  "N-S",
  "NNO-SSW",
  "NNW-SSO",
  "NO-SW",
  "NW-SO",
  "O-W",
  "ONO-WSW",
  "OSO-WNW",
  "S-N",
  "SO-NW",
  "SSO-NNW",
  "SSW-NNO",
  "SW-NO",
  "W-O",
  "WNW-OSO",
  "WSW-ONO"
];
var NO_ORIENTATION = "Keine";
var BOUNDARY_CLARITY_OPTIONS = [
  "gut",
  "mittel-gut",
  "mittel",
  "mittel-schlecht",
  "schlecht"
];
var BOUNDARY_BASIS_OPTIONS = ["Farbe", "Material"];
var CONTOUR_OPTIONS = [
  "deutlich",
  "deutlich-diffus",
  "diffus",
  "nicht erkennbar"
];
var HUMUS_CONTENT_OPTIONS = [
  "stark",
  "mittel",
  "wenig",
  "kein"
];
var FINDING_PLANUM_SCHEMA_VERSION = 20;
function clampAngle(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return 0;
  }
  return (num % 360 + 360) % 360;
}
function clampPercent(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return 0;
  }
  return Math.max(0, Math.min(100, num));
}
var findingPlanumSchema = {
  version: FINDING_PLANUM_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    findingId: { type: "string", maxLength: 36 },
    parcelId: { type: "string", maxLength: 36 },
    planumNumber: { type: "string", maxLength: NUMBER_MAX },
    partLabel: { type: "string", maxLength: PART_LABEL_MAX },
    trench: { type: "string", maxLength: SPATIAL_MAX },
    area: { type: "string", maxLength: SPATIAL_MAX },
    sondage: { type: "string", maxLength: SPATIAL_MAX },
    editor: { type: "string", maxLength: PERSON_MAX },
    editorId: { type: "string", maxLength: 36 },
    length: { type: "string", maxLength: MEASURE_MAX },
    width: { type: "string", maxLength: MEASURE_MAX },
    diameter: { type: "string", maxLength: MEASURE_MAX },
    depth: { type: "string", maxLength: MEASURE_MAX },
    differenceToPreviousPlanum: { type: "string", maxLength: MEASURE_MAX },
    heightTop: { type: "string", maxLength: MEASURE_MAX },
    heightBottom: { type: "string", maxLength: MEASURE_MAX },
    form: { type: "string", maxLength: SHORT_TEXT_MAX },
    orientation: { type: "string", maxLength: SHORT_TEXT_MAX },
    colors: {
      type: "array",
      items: {
        type: "object",
        properties: {
          color: { type: "string", maxLength: MEDIUM_TEXT_MAX },
          munsellCode: { type: "string", maxLength: MEDIUM_TEXT_MAX }
        },
        required: ["color", "munsellCode"]
      }
    },
    soilType: { type: "string", maxLength: SHORT_TEXT_MAX },
    inclusions: {
      type: "array",
      items: {
        type: "object",
        properties: {
          inclusion: { type: "string", maxLength: SHORT_TEXT_MAX },
          percentage: { type: "string", maxLength: MEASURE_MAX }
        },
        required: ["inclusion", "percentage"]
      }
    },
    strata: {
      type: "array",
      maxItems: MAX_STRATA,
      items: {
        type: "object",
        properties: {
          name: { type: "string", maxLength: SHORT_TEXT_MAX },
          length: { type: "string", maxLength: MEASURE_MAX },
          width: { type: "string", maxLength: MEASURE_MAX },
          depth: { type: "string", maxLength: MEASURE_MAX },
          diameter: { type: "string", maxLength: MEASURE_MAX },
          // The seeding's bookkeeping — see DescriptionStratum.
          sourceId: { type: "string", maxLength: ID_MAX },
          seededName: { type: "string", maxLength: SHORT_TEXT_MAX }
        },
        required: ["name", "length", "width", "depth"]
      }
    },
    courses: {
      type: "array",
      maxItems: MAX_COURSES,
      items: {
        type: "object",
        properties: {
          name: { type: "string", maxLength: SHORT_TEXT_MAX },
          length: { type: "string", maxLength: MEASURE_MAX },
          width: { type: "string", maxLength: MEASURE_MAX },
          depth: { type: "string", maxLength: MEASURE_MAX },
          diameter: { type: "string", maxLength: MEASURE_MAX },
          heightTop: { type: "string", maxLength: MEASURE_MAX },
          heightBottom: { type: "string", maxLength: MEASURE_MAX },
          levelSheet: { type: "string", maxLength: SHORT_TEXT_MAX }
        },
        required: [
          "name",
          "length",
          "width",
          "depth",
          "diameter",
          "heightTop",
          "heightBottom",
          "levelSheet"
        ]
      }
    },
    excavationMethod: { type: "string", maxLength: SHORT_TEXT_MAX },
    consistency: { type: "string", maxLength: SHORT_TEXT_MAX },
    boundaryClarity: { type: "string", maxLength: SHORT_TEXT_MAX },
    boundaryBasis: { type: "string", maxLength: SHORT_TEXT_MAX },
    contour: { type: "string", maxLength: SHORT_TEXT_MAX },
    humusContent: { type: "string", maxLength: SHORT_TEXT_MAX },
    overliesOrCuts: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    underliesOrIsCut: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    contemporaryWith: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    // „verfüllt" / „verfüllt durch" — the pair a baulicher Bestand adds,
    // stored and mirrored exactly like liegt über/liegt unter above.
    fills: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    filledBy: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    /**
     * The three identity relations — "identisch mit", "Teil von" and "Im
     * Profil mit" — each name specific descriptions (ref -> the
     * sub-entities), not free-text Befundnummern.
     */
    identicalWithPlanumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    identicalWithProfileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    partOfPlanumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    partOfProfileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    inProfileWithPlanumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    inProfileWithProfileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    description: { type: "string" },
    // Ansprache/Interpretation — the reading of what was described, in the
    // recorder's own prose. Shown on a baulicher Bestand only.
    interpretation: { type: "string" },
    northArrow: { type: "boolean", default: false },
    northAngle: { type: "number", default: 0 },
    northX: { type: "number", default: 88 },
    northY: { type: "number", default: 12 },
    recordedAt: { type: "string", maxLength: RECORDED_AT_MAX },
    phaseNumber: { type: "string", maxLength: PHASE_NUMBER_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "findingId",
    "parcelId",
    "planumNumber",
    "partLabel",
    "trench",
    "area",
    "sondage",
    "editor",
    "editorId",
    "length",
    "width",
    "diameter",
    "depth",
    "differenceToPreviousPlanum",
    "heightTop",
    "heightBottom",
    "form",
    "orientation",
    "colors",
    "soilType",
    "inclusions",
    "strata",
    "courses",
    "excavationMethod",
    "consistency",
    "boundaryClarity",
    "boundaryBasis",
    "contour",
    "humusContent",
    "overliesOrCuts",
    "underliesOrIsCut",
    "contemporaryWith",
    "fills",
    "filledBy",
    "identicalWithPlanumIds",
    "identicalWithProfileIds",
    "partOfPlanumIds",
    "partOfProfileIds",
    "inProfileWithPlanumIds",
    "inProfileWithProfileIds",
    "description",
    "interpretation",
    "recordedAt",
    "phaseNumber",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId", "findingId"]
};
function measureField() {
  return z12.string().trim().max(MEASURE_MAX).refine((value) => isMeasureValue(value), rule("numeric")).default("");
}
function coursesField() {
  return z12.array(
    z12.object({
      name: z12.string().max(SHORT_TEXT_MAX).default(""),
      length: measureField(),
      width: measureField(),
      depth: measureField(),
      diameter: measureField(),
      heightTop: z12.string().max(MEASURE_MAX).default(""),
      heightBottom: z12.string().max(MEASURE_MAX).default(""),
      levelSheet: z12.string().max(SHORT_TEXT_MAX).default("")
    })
  ).default([]);
}
var findingPlanumInputSchema = z12.object({
  findingId: z12.string().trim().min(1, rule("required")).max(ID_MAX),
  parcelId: z12.string().max(ID_MAX).default(""),
  planumNumber: z12.string().trim().min(1, rule("required")).max(NUMBER_MAX, rule("maxLength", { max: NUMBER_MAX })),
  partLabel: partLabelField(),
  trench: z12.string().max(SPATIAL_MAX).default(""),
  area: z12.string().trim().min(1, rule("required")).max(SPATIAL_MAX, rule("maxLength", { max: SPATIAL_MAX })),
  sondage: z12.string().max(SPATIAL_MAX).default(""),
  editor: z12.string().trim().min(1, rule("required")).max(PERSON_MAX, rule("maxLength", { max: PERSON_MAX })),
  editorId: z12.string().max(ID_MAX).default(""),
  length: measureField(),
  width: measureField(),
  diameter: measureField(),
  depth: measureField(),
  differenceToPreviousPlanum: measureField(),
  heightTop: z12.string().max(MEASURE_MAX).default(""),
  heightBottom: z12.string().max(MEASURE_MAX).default(""),
  form: z12.string().max(SHORT_TEXT_MAX).default(""),
  orientation: z12.string().max(SHORT_TEXT_MAX).default(""),
  colors: z12.array(
    z12.object({
      color: z12.string().max(MEDIUM_TEXT_MAX).default(""),
      munsellCode: z12.string().max(MEDIUM_TEXT_MAX).default("")
    })
  ).default([]),
  soilType: z12.string().max(SHORT_TEXT_MAX).default(""),
  inclusions: z12.array(
    z12.object({
      inclusion: z12.string().max(SHORT_TEXT_MAX).default(""),
      percentage: z12.string().max(MEASURE_MAX).default("")
    })
  ).default([]),
  strata: z12.array(
    z12.object({
      name: z12.string().max(SHORT_TEXT_MAX).default(""),
      // Measure fields, so a Schicht's Länge is held to the same
      // "number or nothing" rule as the description's own.
      length: measureField(),
      width: measureField(),
      depth: measureField(),
      // Optional here as in the type: a hand-typed row has neither,
      // and zod would drop what it does not know about.
      sourceId: z12.string().max(ID_MAX).optional(),
      seededName: z12.string().max(SHORT_TEXT_MAX).optional()
    })
  ).default([]),
  courses: coursesField(),
  /**
   * A closed list, so an unknown value is a bug rather than a new word — but
   * it is still only validated for LENGTH here: the picker is what enforces
   * the two answers, and an older record must not fail to save.
   */
  excavationMethod: z12.string().max(SHORT_TEXT_MAX).default(""),
  consistency: z12.string().max(SHORT_TEXT_MAX).default(""),
  boundaryClarity: z12.string().max(SHORT_TEXT_MAX).default(""),
  boundaryBasis: z12.string().max(SHORT_TEXT_MAX).default(""),
  contour: z12.string().max(SHORT_TEXT_MAX).default(""),
  humusContent: z12.string().max(SHORT_TEXT_MAX).default(""),
  overliesOrCuts: z12.string().max(MEDIUM_TEXT_MAX).default(""),
  underliesOrIsCut: z12.string().max(MEDIUM_TEXT_MAX).default(""),
  contemporaryWith: z12.string().max(MEDIUM_TEXT_MAX).default(""),
  fills: z12.string().max(MEDIUM_TEXT_MAX).default(""),
  filledBy: z12.string().max(MEDIUM_TEXT_MAX).default(""),
  identicalWithPlanumIds: z12.array(z12.string().max(ID_MAX)).default([]),
  identicalWithProfileIds: z12.array(z12.string().max(ID_MAX)).default([]),
  partOfPlanumIds: z12.array(z12.string().max(ID_MAX)).default([]),
  partOfProfileIds: z12.array(z12.string().max(ID_MAX)).default([]),
  inProfileWithPlanumIds: z12.array(z12.string().max(ID_MAX)).default([]),
  inProfileWithProfileIds: z12.array(z12.string().max(ID_MAX)).default([]),
  description: z12.string().max(DESCRIPTION_MAX).default(""),
  interpretation: z12.string().max(DESCRIPTION_MAX).default(""),
  // The Nordpfeil (drawn on the sketches): shown yes/no + rotation degrees.
  northArrow: z12.boolean().default(false),
  northAngle: z12.number().finite().transform((value) => clampAngle(value)).default(0),
  recordedAt: recordedAtField({ required: true }),
  phaseNumber: z12.string().max(PHASE_NUMBER_MAX).default("")
});
var findingPlanumEntity = defineEntity({
  name: "findingPlanum",
  apiBase: "/api/finding-planums",
  collection: "finding_planums",
  rxSchema: findingPlanumSchema,
  migrations: PLANUM_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    findingId: { kind: "ref", maxLength: ID_MAX },
    parcelId: { kind: "ref", maxLength: ID_MAX },
    planumNumber: { maxLength: NUMBER_MAX, merge: true },
    partLabel: { maxLength: PART_LABEL_MAX, merge: true },
    trench: { maxLength: SPATIAL_MAX, merge: true },
    area: { maxLength: SPATIAL_MAX, merge: true },
    sondage: { maxLength: SPATIAL_MAX, merge: true },
    editor: { maxLength: PERSON_MAX, merge: true },
    editorId: { kind: "ref", maxLength: ID_MAX, merge: true },
    length: { maxLength: MEASURE_MAX, merge: true },
    width: { maxLength: MEASURE_MAX, merge: true },
    diameter: { maxLength: MEASURE_MAX, merge: true },
    depth: { maxLength: MEASURE_MAX, merge: true },
    differenceToPreviousPlanum: { maxLength: MEASURE_MAX, merge: true },
    heightTop: { maxLength: MEASURE_MAX, merge: true },
    heightBottom: { maxLength: MEASURE_MAX, merge: true },
    form: { maxLength: SHORT_TEXT_MAX, merge: true },
    orientation: { maxLength: SHORT_TEXT_MAX, merge: true },
    colors: { sanitizeValue: clampColors },
    soilType: { maxLength: SHORT_TEXT_MAX, merge: true },
    inclusions: { sanitizeValue: clampInclusions },
    strata: { sanitizeValue: clampStrata },
    courses: { sanitizeValue: clampCourses },
    excavationMethod: { maxLength: SHORT_TEXT_MAX, merge: true },
    consistency: { maxLength: SHORT_TEXT_MAX, merge: true },
    boundaryClarity: { maxLength: SHORT_TEXT_MAX, merge: true },
    boundaryBasis: { maxLength: SHORT_TEXT_MAX, merge: true },
    contour: { maxLength: SHORT_TEXT_MAX, merge: true },
    humusContent: { maxLength: SHORT_TEXT_MAX, merge: true },
    overliesOrCuts: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    underliesOrIsCut: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    contemporaryWith: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    fills: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    filledBy: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    identicalWithPlanumIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    identicalWithProfileIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    partOfPlanumIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    partOfProfileIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    inProfileWithPlanumIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    inProfileWithProfileIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    description: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    interpretation: {
      kind: "text",
      maxLength: DESCRIPTION_MAX,
      merge: true
    },
    northArrow: { kind: "boolean", merge: true },
    northAngle: { kind: "number", merge: true, sanitizeValue: clampAngle },
    northX: { kind: "number", merge: true, sanitizeValue: clampPercent },
    northY: { kind: "number", merge: true, sanitizeValue: clampPercent },
    recordedAt: { kind: "datetime", merge: true },
    phaseNumber: { maxLength: PHASE_NUMBER_MAX, merge: true }
  },
  inputSchema: findingPlanumInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      findingId: row.findingId || "",
      parcelId: row.parcelId || "",
      planumNumber: row.planumNumber || "",
      partLabel: row.partLabel || "",
      trench: row.trench || "",
      area: row.area || "",
      sondage: row.sondage || "",
      editor: row.editor || "",
      editorId: row.editorId || "",
      length: row.length || "",
      width: row.width || "",
      diameter: row.diameter || "",
      depth: row.depth || "",
      differenceToPreviousPlanum: row.differenceToPreviousPlanum || "",
      heightTop: row.heightTop || "",
      heightBottom: row.heightBottom || "",
      form: row.form || "",
      orientation: row.orientation || "",
      colors: clampColors(row.colors),
      soilType: row.soilType || "",
      inclusions: clampInclusions(row.inclusions),
      strata: clampStrata(row.strata),
      courses: clampCourses(row.courses),
      excavationMethod: row.excavationMethod || "",
      consistency: row.consistency || "",
      boundaryClarity: row.boundaryClarity || "",
      boundaryBasis: row.boundaryBasis || "",
      contour: row.contour || "",
      humusContent: row.humusContent || "",
      overliesOrCuts: row.overliesOrCuts || "",
      underliesOrIsCut: row.underliesOrIsCut || "",
      contemporaryWith: row.contemporaryWith || "",
      fills: row.fills || "",
      filledBy: row.filledBy || "",
      identicalWithPlanumIds: parseStringArray(
        row.identicalWithPlanumIds
      ),
      identicalWithProfileIds: parseStringArray(
        row.identicalWithProfileIds
      ),
      partOfPlanumIds: parseStringArray(row.partOfPlanumIds),
      partOfProfileIds: parseStringArray(row.partOfProfileIds),
      inProfileWithPlanumIds: parseStringArray(
        row.inProfileWithPlanumIds
      ),
      inProfileWithProfileIds: parseStringArray(
        row.inProfileWithProfileIds
      ),
      description: row.description || "",
      interpretation: row.interpretation || "",
      northArrow: Boolean(row.northArrow),
      northAngle: clampAngle(row.northAngle),
      northX: clampPercent(row.northX ?? 88),
      northY: clampPercent(row.northY ?? 12),
      recordedAt: row.recordedAt ? row.recordedAt.toISOString() : "",
      phaseNumber: row.phaseNumber || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      findingId: doc.findingId || "",
      parcelId: doc.parcelId || "",
      planumNumber: doc.planumNumber || "",
      partLabel: doc.partLabel || "",
      trench: doc.trench || "",
      area: doc.area || "",
      sondage: doc.sondage || "",
      editor: doc.editor || "",
      editorId: doc.editorId || "",
      length: doc.length || "",
      width: doc.width || "",
      diameter: doc.diameter || "",
      depth: doc.depth || "",
      differenceToPreviousPlanum: doc.differenceToPreviousPlanum || "",
      heightTop: doc.heightTop || "",
      heightBottom: doc.heightBottom || "",
      form: doc.form || "",
      orientation: doc.orientation || "",
      colors: JSON.stringify(clampColors(doc.colors)),
      soilType: doc.soilType || "",
      inclusions: JSON.stringify(clampInclusions(doc.inclusions)),
      strata: JSON.stringify(clampStrata(doc.strata)),
      courses: JSON.stringify(clampCourses(doc.courses)),
      excavationMethod: doc.excavationMethod || "",
      consistency: doc.consistency || "",
      boundaryClarity: doc.boundaryClarity || "",
      boundaryBasis: doc.boundaryBasis || "",
      contour: doc.contour || "",
      humusContent: doc.humusContent || "",
      overliesOrCuts: doc.overliesOrCuts || "",
      underliesOrIsCut: doc.underliesOrIsCut || "",
      contemporaryWith: doc.contemporaryWith || "",
      fills: doc.fills || "",
      filledBy: doc.filledBy || "",
      identicalWithPlanumIds: JSON.stringify(
        doc.identicalWithPlanumIds || []
      ),
      identicalWithProfileIds: JSON.stringify(
        doc.identicalWithProfileIds || []
      ),
      partOfPlanumIds: JSON.stringify(doc.partOfPlanumIds || []),
      partOfProfileIds: JSON.stringify(doc.partOfProfileIds || []),
      inProfileWithPlanumIds: JSON.stringify(
        doc.inProfileWithPlanumIds || []
      ),
      inProfileWithProfileIds: JSON.stringify(
        doc.inProfileWithProfileIds || []
      ),
      description: doc.description || "",
      interpretation: doc.interpretation || "",
      northArrow: Boolean(doc.northArrow),
      northAngle: clampAngle(doc.northAngle),
      northX: clampPercent(doc.northX),
      northY: clampPercent(doc.northY),
      recordedAt: doc.recordedAt ? new Date(doc.recordedAt) : null,
      phaseNumber: doc.phaseNumber || ""
    };
  }
});

// src/entities/findingProfile.ts
import { z as z13 } from "zod";
var PROFILE_FORM_OPTIONS = [
  "rechteckig",
  "kesself\xF6rmig",
  "kastenf\xF6rmig",
  "muldenf\xF6rmig",
  "trichterf\xF6rmig",
  "trapezf\xF6rmig",
  "pyramidenstumpff\xF6rmig",
  "kegelstumpff\xF6rmig",
  "wannenf\xF6rmig",
  "unregelm\xE4\xDFig",
  "v-f\xF6rmig",
  "u-f\xF6rmig",
  "sanduhrf\xF6rmig"
];
var FINDING_PROFILE_SCHEMA_VERSION = 14;
var findingProfileSchema = {
  version: FINDING_PROFILE_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    findingId: { type: "string", maxLength: 36 },
    parcelId: { type: "string", maxLength: 36 },
    profileNumber: { type: "string", maxLength: NUMBER_MAX },
    partLabel: { type: "string", maxLength: PART_LABEL_MAX },
    trench: { type: "string", maxLength: SPATIAL_MAX },
    area: { type: "string", maxLength: SPATIAL_MAX },
    sondage: { type: "string", maxLength: SPATIAL_MAX },
    editor: { type: "string", maxLength: PERSON_MAX },
    editorId: { type: "string", maxLength: 36 },
    width: { type: "string", maxLength: MEASURE_MAX },
    depth: { type: "string", maxLength: MEASURE_MAX },
    heightTop: { type: "string", maxLength: MEASURE_MAX },
    heightBottom: { type: "string", maxLength: MEASURE_MAX },
    form: { type: "string", maxLength: SHORT_TEXT_MAX },
    orientation: { type: "string", maxLength: SHORT_TEXT_MAX },
    /**
     * Bodenmerkmale — normally the Planum's, but a Befund only visible in
     * section records them here (see `onlyVisibleInProfile`). Same shapes
     * as the Planum's, so the ExcaBook columns stay interchangeable.
     */
    colors: {
      type: "array",
      items: {
        type: "object",
        properties: {
          color: { type: "string", maxLength: MEDIUM_TEXT_MAX },
          munsellCode: { type: "string", maxLength: MEDIUM_TEXT_MAX }
        },
        required: ["color", "munsellCode"]
      }
    },
    soilType: { type: "string", maxLength: SHORT_TEXT_MAX },
    inclusions: {
      type: "array",
      items: {
        type: "object",
        properties: {
          inclusion: { type: "string", maxLength: SHORT_TEXT_MAX },
          percentage: { type: "string", maxLength: MEASURE_MAX }
        },
        required: ["inclusion", "percentage"]
      }
    },
    // The Schichten and their measures — a Profil shows Breite and Tiefe,
    // the Planum Länge and Breite; one shape carries both.
    strata: {
      type: "array",
      items: {
        type: "object",
        properties: {
          name: { type: "string", maxLength: SHORT_TEXT_MAX },
          length: { type: "string", maxLength: MEASURE_MAX },
          width: { type: "string", maxLength: MEASURE_MAX },
          depth: { type: "string", maxLength: MEASURE_MAX },
          // The seeding's bookkeeping — see DescriptionStratum.
          sourceId: { type: "string", maxLength: ID_MAX },
          seededName: { type: "string", maxLength: SHORT_TEXT_MAX }
        },
        required: ["name", "length", "width", "depth"]
      }
    },
    courses: {
      type: "array",
      maxItems: MAX_COURSES,
      items: {
        type: "object",
        properties: {
          name: { type: "string", maxLength: SHORT_TEXT_MAX },
          length: { type: "string", maxLength: MEASURE_MAX },
          width: { type: "string", maxLength: MEASURE_MAX },
          depth: { type: "string", maxLength: MEASURE_MAX },
          diameter: { type: "string", maxLength: MEASURE_MAX },
          heightTop: { type: "string", maxLength: MEASURE_MAX },
          heightBottom: { type: "string", maxLength: MEASURE_MAX },
          levelSheet: { type: "string", maxLength: SHORT_TEXT_MAX }
        },
        required: [
          "name",
          "length",
          "width",
          "depth",
          "diameter",
          "heightTop",
          "heightBottom",
          "levelSheet"
        ]
      }
    },
    excavationMethod: { type: "string", maxLength: SHORT_TEXT_MAX },
    consistency: { type: "string", maxLength: SHORT_TEXT_MAX },
    boundaryClarity: { type: "string", maxLength: SHORT_TEXT_MAX },
    boundaryBasis: { type: "string", maxLength: SHORT_TEXT_MAX },
    contour: { type: "string", maxLength: SHORT_TEXT_MAX },
    humusContent: { type: "string", maxLength: SHORT_TEXT_MAX },
    overliesOrCuts: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    underliesOrIsCut: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    contemporaryWith: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    // „verfüllt" / „verfüllt durch" — see the Planum's.
    fills: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    filledBy: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    /**
     * The three identity relations — "identisch mit", "Teil von", "Im
     * Profil mit" — name specific descriptions, not Befundnummern.
     */
    identicalWithPlanumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    identicalWithProfileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    partOfPlanumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    partOfProfileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    inProfileWithPlanumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    inProfileWithProfileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    description: { type: "string" },
    // Ansprache/Interpretation — see the Planum's.
    interpretation: { type: "string" },
    recordedAt: { type: "string", maxLength: RECORDED_AT_MAX },
    phaseNumber: { type: "string", maxLength: PHASE_NUMBER_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "findingId",
    "parcelId",
    "profileNumber",
    "partLabel",
    "trench",
    "area",
    "sondage",
    "editor",
    "editorId",
    "width",
    "depth",
    "heightTop",
    "heightBottom",
    "form",
    "orientation",
    "colors",
    "soilType",
    "inclusions",
    "strata",
    "courses",
    "excavationMethod",
    "consistency",
    "boundaryClarity",
    "boundaryBasis",
    "contour",
    "humusContent",
    "overliesOrCuts",
    "underliesOrIsCut",
    "contemporaryWith",
    "fills",
    "filledBy",
    "identicalWithPlanumIds",
    "identicalWithProfileIds",
    "partOfPlanumIds",
    "partOfProfileIds",
    "inProfileWithPlanumIds",
    "inProfileWithProfileIds",
    "description",
    "interpretation",
    "recordedAt",
    "phaseNumber",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId", "findingId"]
};
var findingProfileInputSchema = z13.object({
  findingId: z13.string().trim().min(1, rule("required")).max(ID_MAX),
  parcelId: z13.string().max(ID_MAX).default(""),
  profileNumber: z13.string().trim().min(1, rule("required")).max(NUMBER_MAX, rule("maxLength", { max: NUMBER_MAX })),
  partLabel: partLabelField(),
  trench: z13.string().max(SPATIAL_MAX).default(""),
  area: z13.string().trim().min(1, rule("required")).max(SPATIAL_MAX, rule("maxLength", { max: SPATIAL_MAX })),
  sondage: z13.string().max(SPATIAL_MAX).default(""),
  editor: z13.string().trim().min(1, rule("required")).max(PERSON_MAX, rule("maxLength", { max: PERSON_MAX })),
  editorId: z13.string().max(ID_MAX).default(""),
  width: measureField(),
  depth: measureField(),
  heightTop: z13.string().max(MEASURE_MAX).default(""),
  heightBottom: z13.string().max(MEASURE_MAX).default(""),
  form: z13.string().max(SHORT_TEXT_MAX).default(""),
  orientation: z13.string().max(SHORT_TEXT_MAX).default(""),
  colors: z13.array(
    z13.object({
      color: z13.string().max(MEDIUM_TEXT_MAX).default(""),
      munsellCode: z13.string().max(MEDIUM_TEXT_MAX).default("")
    })
  ).default([]),
  soilType: z13.string().max(SHORT_TEXT_MAX).default(""),
  inclusions: z13.array(
    z13.object({
      inclusion: z13.string().max(SHORT_TEXT_MAX).default(""),
      percentage: z13.string().max(MEASURE_MAX).default("")
    })
  ).default([]),
  strata: z13.array(
    z13.object({
      name: z13.string().max(SHORT_TEXT_MAX).default(""),
      length: measureField(),
      width: measureField(),
      depth: measureField(),
      sourceId: z13.string().max(ID_MAX).optional(),
      seededName: z13.string().max(SHORT_TEXT_MAX).optional()
    })
  ).default([]),
  courses: coursesField(),
  excavationMethod: z13.string().max(SHORT_TEXT_MAX).default(""),
  consistency: z13.string().max(SHORT_TEXT_MAX).default(""),
  boundaryClarity: z13.string().max(SHORT_TEXT_MAX).default(""),
  boundaryBasis: z13.string().max(SHORT_TEXT_MAX).default(""),
  contour: z13.string().max(SHORT_TEXT_MAX).default(""),
  humusContent: z13.string().max(SHORT_TEXT_MAX).default(""),
  overliesOrCuts: z13.string().max(MEDIUM_TEXT_MAX).default(""),
  underliesOrIsCut: z13.string().max(MEDIUM_TEXT_MAX).default(""),
  contemporaryWith: z13.string().max(MEDIUM_TEXT_MAX).default(""),
  fills: z13.string().max(MEDIUM_TEXT_MAX).default(""),
  filledBy: z13.string().max(MEDIUM_TEXT_MAX).default(""),
  identicalWithPlanumIds: z13.array(z13.string().max(ID_MAX)).default([]),
  identicalWithProfileIds: z13.array(z13.string().max(ID_MAX)).default([]),
  partOfPlanumIds: z13.array(z13.string().max(ID_MAX)).default([]),
  partOfProfileIds: z13.array(z13.string().max(ID_MAX)).default([]),
  inProfileWithPlanumIds: z13.array(z13.string().max(ID_MAX)).default([]),
  inProfileWithProfileIds: z13.array(z13.string().max(ID_MAX)).default([]),
  description: z13.string().max(DESCRIPTION_MAX).default(""),
  interpretation: z13.string().max(DESCRIPTION_MAX).default(""),
  recordedAt: recordedAtField({ required: true }),
  phaseNumber: z13.string().max(PHASE_NUMBER_MAX).default("")
});
var findingProfileEntity = defineEntity({
  name: "findingProfile",
  apiBase: "/api/finding-profiles",
  collection: "finding_profiles",
  rxSchema: findingProfileSchema,
  migrations: PROFILE_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    findingId: { kind: "ref", maxLength: ID_MAX },
    parcelId: { kind: "ref", maxLength: ID_MAX },
    profileNumber: { maxLength: NUMBER_MAX, merge: true },
    partLabel: { maxLength: PART_LABEL_MAX, merge: true },
    trench: { maxLength: SPATIAL_MAX, merge: true },
    area: { maxLength: SPATIAL_MAX, merge: true },
    sondage: { maxLength: SPATIAL_MAX, merge: true },
    editor: { maxLength: PERSON_MAX, merge: true },
    editorId: { kind: "ref", maxLength: ID_MAX, merge: true },
    width: { maxLength: MEASURE_MAX, merge: true },
    depth: { maxLength: MEASURE_MAX, merge: true },
    heightTop: { maxLength: MEASURE_MAX, merge: true },
    heightBottom: { maxLength: MEASURE_MAX, merge: true },
    form: { maxLength: SHORT_TEXT_MAX, merge: true },
    orientation: { maxLength: SHORT_TEXT_MAX, merge: true },
    colors: { sanitizeValue: clampColors },
    soilType: { maxLength: SHORT_TEXT_MAX, merge: true },
    inclusions: { sanitizeValue: clampInclusions },
    strata: { sanitizeValue: clampStrata },
    courses: { sanitizeValue: clampCourses },
    excavationMethod: { maxLength: SHORT_TEXT_MAX, merge: true },
    consistency: { maxLength: SHORT_TEXT_MAX, merge: true },
    boundaryClarity: { maxLength: SHORT_TEXT_MAX, merge: true },
    boundaryBasis: { maxLength: SHORT_TEXT_MAX, merge: true },
    contour: { maxLength: SHORT_TEXT_MAX, merge: true },
    humusContent: { maxLength: SHORT_TEXT_MAX, merge: true },
    overliesOrCuts: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    underliesOrIsCut: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    contemporaryWith: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    fills: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    filledBy: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    identicalWithPlanumIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    identicalWithProfileIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    partOfPlanumIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    partOfProfileIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    inProfileWithPlanumIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    inProfileWithProfileIds: {
      kind: "array",
      itemMaxLength: ID_MAX,
      merge: true
    },
    description: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    interpretation: {
      kind: "text",
      maxLength: DESCRIPTION_MAX,
      merge: true
    },
    recordedAt: { kind: "datetime", merge: true },
    phaseNumber: { maxLength: PHASE_NUMBER_MAX, merge: true }
  },
  inputSchema: findingProfileInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      findingId: row.findingId || "",
      parcelId: row.parcelId || "",
      profileNumber: row.profileNumber || "",
      partLabel: row.partLabel || "",
      trench: row.trench || "",
      area: row.area || "",
      sondage: row.sondage || "",
      editor: row.editor || "",
      editorId: row.editorId || "",
      width: row.width || "",
      depth: row.depth || "",
      heightTop: row.heightTop || "",
      heightBottom: row.heightBottom || "",
      form: row.form || "",
      orientation: row.orientation || "",
      colors: clampColors(row.colors),
      soilType: row.soilType || "",
      inclusions: clampInclusions(row.inclusions),
      strata: clampStrata(row.strata),
      courses: clampCourses(row.courses),
      excavationMethod: row.excavationMethod || "",
      consistency: row.consistency || "",
      boundaryClarity: row.boundaryClarity || "",
      boundaryBasis: row.boundaryBasis || "",
      contour: row.contour || "",
      humusContent: row.humusContent || "",
      overliesOrCuts: row.overliesOrCuts || "",
      underliesOrIsCut: row.underliesOrIsCut || "",
      contemporaryWith: row.contemporaryWith || "",
      fills: row.fills || "",
      filledBy: row.filledBy || "",
      identicalWithPlanumIds: parseStringArray(
        row.identicalWithPlanumIds
      ),
      identicalWithProfileIds: parseStringArray(
        row.identicalWithProfileIds
      ),
      partOfPlanumIds: parseStringArray(row.partOfPlanumIds),
      partOfProfileIds: parseStringArray(row.partOfProfileIds),
      inProfileWithPlanumIds: parseStringArray(
        row.inProfileWithPlanumIds
      ),
      inProfileWithProfileIds: parseStringArray(
        row.inProfileWithProfileIds
      ),
      description: row.description || "",
      interpretation: row.interpretation || "",
      recordedAt: row.recordedAt ? row.recordedAt.toISOString() : "",
      phaseNumber: row.phaseNumber || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      findingId: doc.findingId || "",
      parcelId: doc.parcelId || "",
      profileNumber: doc.profileNumber || "",
      partLabel: doc.partLabel || "",
      trench: doc.trench || "",
      area: doc.area || "",
      sondage: doc.sondage || "",
      editor: doc.editor || "",
      editorId: doc.editorId || "",
      width: doc.width || "",
      depth: doc.depth || "",
      heightTop: doc.heightTop || "",
      heightBottom: doc.heightBottom || "",
      form: doc.form || "",
      orientation: doc.orientation || "",
      colors: JSON.stringify(clampColors(doc.colors)),
      soilType: doc.soilType || "",
      inclusions: JSON.stringify(clampInclusions(doc.inclusions)),
      strata: JSON.stringify(clampStrata(doc.strata)),
      courses: JSON.stringify(clampCourses(doc.courses)),
      excavationMethod: doc.excavationMethod || "",
      consistency: doc.consistency || "",
      boundaryClarity: doc.boundaryClarity || "",
      boundaryBasis: doc.boundaryBasis || "",
      contour: doc.contour || "",
      humusContent: doc.humusContent || "",
      overliesOrCuts: doc.overliesOrCuts || "",
      underliesOrIsCut: doc.underliesOrIsCut || "",
      contemporaryWith: doc.contemporaryWith || "",
      fills: doc.fills || "",
      filledBy: doc.filledBy || "",
      identicalWithPlanumIds: JSON.stringify(
        doc.identicalWithPlanumIds || []
      ),
      identicalWithProfileIds: JSON.stringify(
        doc.identicalWithProfileIds || []
      ),
      partOfPlanumIds: JSON.stringify(doc.partOfPlanumIds || []),
      partOfProfileIds: JSON.stringify(doc.partOfProfileIds || []),
      inProfileWithPlanumIds: JSON.stringify(
        doc.inProfileWithPlanumIds || []
      ),
      inProfileWithProfileIds: JSON.stringify(
        doc.inProfileWithProfileIds || []
      ),
      description: doc.description || "",
      interpretation: doc.interpretation || "",
      recordedAt: doc.recordedAt ? new Date(doc.recordedAt) : null,
      phaseNumber: doc.phaseNumber || ""
    };
  }
});

// src/entities/media.ts
import { z as z14 } from "zod";

// src/entities/mediaGroupLayout.ts
var MEDIA_GROUP_MAX_PER_ROW = 4;
var MEDIA_GROUP_MAX_ROWS = 6;
function parseMediaGroupLayout(value) {
  let raw = value;
  if (typeof value === "string") {
    try {
      raw = JSON.parse(value || "[]");
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = /* @__PURE__ */ new Set();
  const rows = [];
  for (const row of raw) {
    if (!Array.isArray(row)) {
      continue;
    }
    const ids = row.filter(
      (id) => typeof id === "string" && id !== "" && id.length <= ID_MAX && !seen.has(id)
    ).slice(0, MEDIA_GROUP_MAX_PER_ROW);
    ids.forEach((id) => seen.add(id));
    if (ids.length > 0) {
      rows.push(ids);
    }
    if (rows.length === MEDIA_GROUP_MAX_ROWS) {
      break;
    }
  }
  return rows;
}
function serializeMediaGroupLayout(layout) {
  return JSON.stringify(parseMediaGroupLayout(layout));
}

// src/migrations/media.ts
function mediaGroupV0ToV1(oldDoc) {
  const { columns, rows: _rows, mediaIds, ...rest } = oldDoc;
  const pulled = typeof rest.layout === "string" ? parseMediaGroupLayout(rest.layout) : [];
  const placed = new Set(pulled.flat());
  const ids = (Array.isArray(mediaIds) ? mediaIds : []).filter(
    (id) => !placed.has(id)
  );
  if (typeof rest.layout === "string" && ids.length === 0) {
    return rest;
  }
  const width = Math.min(
    MEDIA_GROUP_MAX_PER_ROW,
    Math.max(1, Math.round(Number(columns)) || 1)
  );
  const layout = [...pulled];
  for (let i = 0; i < ids.length; i += width) {
    layout.push(ids.slice(i, i + width));
  }
  return { ...rest, layout: serializeMediaGroupLayout(layout) };
}
function mediaGroupV1ToV2(oldDoc) {
  return fillMissing(oldDoc, { sortOrder: 0 });
}
var MEDIA_GROUP_MIGRATIONS = {
  1: mediaGroupV0ToV1,
  2: mediaGroupV1ToV2
};
function mediaV0ToV1(oldDoc) {
  return fillMissing(oldDoc, { deviceName: "", deviceId: "" });
}
var MEDIA_MIGRATIONS = {
  1: mediaV0ToV1
};

// src/entities/media.ts
var OWNER_TYPE_MAX = 30;
var KIND_MAX = 20;
var CAPTION_MAX = 500;
var NAME_MAX2 = 255;
var MIME_MAX = 100;
var SIZE_MAX = 20;
var DEVICE_NAME_MAX = 100;
var DEVICE_ID_MAX = 64;
var SCENE_MAX = 1e6;
var MEDIA_KINDS = ["photo", "sketch"];
var DEFAULT_MEDIA_KIND = "photo";
var MEDIA_OWNER_TYPES = [
  "finding",
  "findingPlanum",
  "findingProfile",
  "overallDescription",
  "areaDescription",
  "sample",
  "diary"
];
var MEDIA_SCHEMA_VERSION = 1;
var mediaSchema = {
  version: MEDIA_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    ownerType: { type: "string", maxLength: OWNER_TYPE_MAX },
    ownerId: { type: "string", maxLength: 36 },
    kind: { type: "string", maxLength: KIND_MAX },
    caption: { type: "string", maxLength: CAPTION_MAX },
    originalName: { type: "string", maxLength: NAME_MAX2 },
    mimeType: { type: "string", maxLength: MIME_MAX },
    byteSize: { type: "string", maxLength: SIZE_MAX },
    deviceName: { type: "string", maxLength: DEVICE_NAME_MAX },
    deviceId: { type: "string", maxLength: DEVICE_ID_MAX },
    // JSON scene for re-editable sketches; '' for photos. Not indexed, so no
    // maxLength needed in the RxDB schema (clamped on push via the field spec).
    scene: { type: "string" },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "ownerType",
    "ownerId",
    "kind",
    "caption",
    "originalName",
    "mimeType",
    "byteSize",
    "deviceName",
    "deviceId",
    "scene",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId", "ownerId"]
};
var mediaInputSchema = z14.object({
  ownerType: z14.enum(MEDIA_OWNER_TYPES),
  ownerId: z14.string().trim().min(1, rule("required")).max(ID_MAX),
  kind: z14.string().max(KIND_MAX).default(DEFAULT_MEDIA_KIND),
  caption: z14.string().max(CAPTION_MAX, rule("maxLength", { max: CAPTION_MAX })).default(""),
  originalName: z14.string().max(NAME_MAX2).default(""),
  mimeType: z14.string().max(MIME_MAX).default(""),
  byteSize: z14.string().max(SIZE_MAX).default(""),
  deviceName: z14.string().max(DEVICE_NAME_MAX).default(""),
  deviceId: z14.string().max(DEVICE_ID_MAX).default(""),
  scene: z14.string().max(SCENE_MAX).default("")
});
var mediaEntity = defineEntity({
  name: "media",
  apiBase: "/api/media",
  collection: "media",
  rxSchema: mediaSchema,
  historyAllFields: true,
  migrations: MEDIA_MIGRATIONS,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    // Polymorphic owner — creation metadata (immutable once set).
    ownerType: { maxLength: OWNER_TYPE_MAX },
    ownerId: { kind: "ref", maxLength: ID_MAX },
    kind: { maxLength: KIND_MAX },
    caption: { maxLength: CAPTION_MAX, merge: true },
    originalName: { maxLength: NAME_MAX2 },
    mimeType: { maxLength: MIME_MAX },
    byteSize: { maxLength: SIZE_MAX },
    // Creation metadata like the name: set once, by the device itself.
    deviceName: { maxLength: DEVICE_NAME_MAX },
    deviceId: { maxLength: DEVICE_ID_MAX },
    // Mutable (changes each edit), but not a merge field — last write wins.
    scene: { maxLength: SCENE_MAX }
  },
  inputSchema: mediaInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      ownerType: row.ownerType || "",
      ownerId: row.ownerId || "",
      kind: row.kind || DEFAULT_MEDIA_KIND,
      caption: row.caption || "",
      originalName: row.originalName || "",
      mimeType: row.mimeType || "",
      byteSize: row.byteSize || "",
      deviceName: row.deviceName || "",
      deviceId: row.deviceId || "",
      scene: row.scene || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      ownerType: doc.ownerType || "",
      ownerId: doc.ownerId || "",
      kind: doc.kind || DEFAULT_MEDIA_KIND,
      caption: doc.caption || "",
      originalName: doc.originalName || "",
      mimeType: doc.mimeType || "",
      byteSize: doc.byteSize || "",
      deviceName: doc.deviceName || "",
      deviceId: doc.deviceId || "",
      scene: doc.scene || ""
    };
  }
});

// src/entities/mediaLink.ts
import { z as z15 } from "zod";
var OWNER_TYPE_MAX2 = 30;
var MEDIA_LINK_SCHEMA_VERSION = 0;
var mediaLinkSchema = {
  version: MEDIA_LINK_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    mediaId: { type: "string", maxLength: 36 },
    ownerType: { type: "string", maxLength: OWNER_TYPE_MAX2 },
    ownerId: { type: "string", maxLength: 36 },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "mediaId",
    "ownerType",
    "ownerId",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId", "mediaId", "ownerId"]
};
var mediaLinkInputSchema = z15.object({
  mediaId: z15.string().trim().min(1, rule("required")).max(ID_MAX),
  ownerType: z15.enum(MEDIA_OWNER_TYPES),
  ownerId: z15.string().trim().min(1, rule("required")).max(ID_MAX)
});
var mediaLinkEntity = defineEntity({
  name: "mediaLink",
  apiBase: "/api/media-links",
  collection: "media_links",
  rxSchema: mediaLinkSchema,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    mediaId: { kind: "ref", maxLength: ID_MAX },
    ownerType: { maxLength: OWNER_TYPE_MAX2 },
    ownerId: { kind: "ref", maxLength: ID_MAX }
  },
  inputSchema: mediaLinkInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      mediaId: row.mediaId || "",
      ownerType: row.ownerType || "",
      ownerId: row.ownerId || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      mediaId: doc.mediaId || "",
      ownerType: doc.ownerType || "",
      ownerId: doc.ownerId || ""
    };
  }
});

// src/entities/translation.ts
import { z as z16 } from "zod";
var TRANSLATION_LANGUAGES = ["de", "en"];
var TRANSLATION_INSTRUCTIONS_MAX = 10;
var TRANSLATION_INSTRUCTION_MAX_LENGTH = 300;
var VOICE_FALLBACK_LANGUAGES = TRANSLATION_LANGUAGES;
var TRANSLATION_ORIGINS = ["machine", "edited"];
var TRANSLATION_OWNER_SUBJECTS = {
  finding: "Finding",
  findingPlanum: "FindingPlanum",
  findingProfile: "FindingProfile",
  overallDescription: "OverallDescription",
  areaDescription: "AreaDescription",
  find: "Find",
  sample: "Sample",
  photo: "Photo",
  drawingSheet: "DrawingSheet",
  diary: "Diary",
  project: "Project"
};
var TRANSLATION_OWNER_TYPES = Object.keys(
  TRANSLATION_OWNER_SUBJECTS
);
var OWNER_TYPE_MAX3 = 30;
var FIELD_MAX = 60;
var LANG_MAX = 5;
var ORIGIN_MAX = 10;
var APPROVED_AT_MAX = 30;
var TRANSLATION_SCHEMA_VERSION = 0;
var translationSchema = {
  version: TRANSLATION_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    ownerType: { type: "string", maxLength: OWNER_TYPE_MAX3 },
    ownerId: { type: "string", maxLength: 36 },
    field: { type: "string", maxLength: FIELD_MAX },
    sourceLang: { type: "string", maxLength: LANG_MAX },
    targetLang: { type: "string", maxLength: LANG_MAX },
    sourceText: { type: "string" },
    text: { type: "string" },
    origin: { type: "string", maxLength: ORIGIN_MAX },
    approvedBy: { type: "string", maxLength: 36 },
    approvedAt: { type: "string", maxLength: APPROVED_AT_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "ownerType",
    "ownerId",
    "field",
    "sourceLang",
    "targetLang",
    "sourceText",
    "text",
    "origin",
    "approvedBy",
    "approvedAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId", "ownerId"]
};
var translationInputSchema = z16.object({
  ownerType: z16.enum(
    TRANSLATION_OWNER_TYPES
  ),
  ownerId: z16.string().trim().min(1, rule("required")).max(ID_MAX),
  field: z16.string().trim().min(1, rule("required")).max(FIELD_MAX),
  // Whatever DeepL took the original for ('pl', 'fr' …), not only ours.
  sourceLang: z16.string().regex(/^[a-z]{2,3}$/),
  targetLang: z16.enum(TRANSLATION_LANGUAGES),
  sourceText: z16.string().max(DESCRIPTION_MAX),
  text: z16.string().max(DESCRIPTION_MAX),
  origin: z16.enum(TRANSLATION_ORIGINS)
});
function isPlainMachineTranslation(state) {
  if (!state) {
    return true;
  }
  return !state._deleted && state.origin === "machine" && state.approvedBy === "";
}
function translationWriteNeedsOwnerRight(incoming, stored) {
  return !(isPlainMachineTranslation(stored) && isPlainMachineTranslation(incoming));
}
var translationEntity = defineEntity({
  name: "translation",
  apiBase: "/api/translations",
  collection: "translations",
  rxSchema: translationSchema,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    ownerType: { maxLength: OWNER_TYPE_MAX3 },
    ownerId: { kind: "ref", maxLength: ID_MAX },
    field: { maxLength: FIELD_MAX },
    sourceLang: { maxLength: LANG_MAX },
    targetLang: { maxLength: LANG_MAX },
    sourceText: { kind: "text", maxLength: DESCRIPTION_MAX },
    text: { kind: "text", maxLength: DESCRIPTION_MAX },
    origin: { maxLength: ORIGIN_MAX },
    approvedBy: { kind: "ref", maxLength: ID_MAX },
    approvedAt: { maxLength: APPROVED_AT_MAX }
  },
  inputSchema: translationInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      ownerType: row.ownerType || "",
      ownerId: row.ownerId || "",
      field: row.field || "",
      sourceLang: row.sourceLang || "",
      targetLang: row.targetLang || "",
      sourceText: row.sourceText || "",
      text: row.text || "",
      origin: row.origin || "machine",
      approvedBy: row.approvedBy || "",
      approvedAt: row.approvedAt || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      ownerType: doc.ownerType || "",
      ownerId: doc.ownerId || "",
      field: doc.field || "",
      sourceLang: doc.sourceLang || "",
      targetLang: doc.targetLang || "",
      sourceText: doc.sourceText || "",
      text: doc.text || "",
      origin: doc.origin || "machine",
      approvedBy: doc.approvedBy || "",
      approvedAt: doc.approvedAt || ""
    };
  }
});

// src/entities/glossaryTerm.ts
import { z as z17 } from "zod";

// src/migrations/translation.ts
function glossaryTermV0ToV1(oldDoc) {
  return fillMissing(oldDoc, { direction: "both" });
}
var GLOSSARY_TERM_MIGRATIONS = {
  1: glossaryTermV0ToV1
};

// src/entities/glossaryTerm.ts
var GLOSSARY_TERM_MAX = 200;
var GLOSSARY_REMARK_MAX = 500;
var GLOSSARY_DIRECTIONS = ["both", "deToEn", "enToDe"];
var DIRECTION_MAX = 10;
function glossaryDirection(value) {
  return GLOSSARY_DIRECTIONS.includes(value) ? value : "both";
}
function worksDeToEn(direction) {
  return glossaryDirection(direction) !== "enToDe";
}
function worksEnToDe(direction) {
  return glossaryDirection(direction) !== "deToEn";
}
var GLOSSARY_TERM_SCHEMA_VERSION = 1;
var glossaryTermSchema = {
  version: GLOSSARY_TERM_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    de: { type: "string", maxLength: GLOSSARY_TERM_MAX },
    en: { type: "string", maxLength: GLOSSARY_TERM_MAX },
    direction: { type: "string", maxLength: DIRECTION_MAX },
    remark: { type: "string", maxLength: GLOSSARY_REMARK_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "de",
    "en",
    "direction",
    "remark",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"]]
};
function normalizeGlossaryTerm(value) {
  return value.replace(/\s+/g, " ").trim();
}
var term = z17.string().transform(normalizeGlossaryTerm).pipe(
  z17.string().min(1, rule("required")).max(
    GLOSSARY_TERM_MAX,
    rule("maxLength", { max: GLOSSARY_TERM_MAX })
  )
);
var glossaryTermInputSchema = z17.object({
  de: term,
  en: term,
  direction: z17.enum(GLOSSARY_DIRECTIONS),
  remark: z17.string().trim().max(
    GLOSSARY_REMARK_MAX,
    rule("maxLength", { max: GLOSSARY_REMARK_MAX })
  )
});
var glossaryTermEntity = defineEntity({
  name: "glossaryTerm",
  apiBase: "/api/glossary-terms",
  collection: "glossary_terms",
  rxSchema: glossaryTermSchema,
  migrations: GLOSSARY_TERM_MIGRATIONS,
  historyAllFields: true,
  fields: {
    de: { maxLength: GLOSSARY_TERM_MAX },
    en: { maxLength: GLOSSARY_TERM_MAX },
    direction: { sanitizeValue: glossaryDirection },
    remark: { maxLength: GLOSSARY_REMARK_MAX }
  },
  inputSchema: glossaryTermInputSchema,
  toWire(row) {
    return {
      id: row.id,
      de: row.de || "",
      en: row.en || "",
      direction: glossaryDirection(row.direction),
      remark: row.remark || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      de: doc.de || "",
      en: doc.en || "",
      direction: glossaryDirection(doc.direction),
      remark: doc.remark || ""
    };
  }
});

// src/entities/billingCatalog.ts
import { z as z18 } from "zod";

// src/entities/billingFields.ts
var BILLING_FIELD_TYPES = [
  "text",
  "number",
  "person",
  "date"
];
var BILLING_FIELD_LABEL_MAX = 100;
var BILLING_FIELD_VALUE_MAX = 255;
var MAX_FIELDS = 30;
var FIELD_ID_MAX = 36;
var USER_ID_MAX = 36;
function billingFieldType(value) {
  return BILLING_FIELD_TYPES.includes(value) ? value : "text";
}
function clampFieldDefinitions(value) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const item of parseJsonArrayRaw(value)) {
    if (out.length >= MAX_FIELDS) {
      break;
    }
    const raw = item ?? {};
    const id = clampString(raw.id, FIELD_ID_MAX);
    const label = clampString(raw.label, BILLING_FIELD_LABEL_MAX).trim();
    if (!id || !label || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push({
      id,
      label,
      type: billingFieldType(raw.type),
      perLine: raw.perLine === true
    });
  }
  return out;
}
function normalizedValue(type, value) {
  const trimmed = value.trim();
  if (type === "date") {
    return DAY_PATTERN.test(trimmed) ? trimmed : "";
  }
  if (type === "number") {
    const number = Number(trimmed.replace(",", "."));
    return trimmed !== "" && Number.isFinite(number) ? String(number) : "";
  }
  return trimmed;
}
function clampFieldValues(value, definitions) {
  const typeById = new Map(
    (definitions ?? []).map((definition) => [
      definition.id,
      definition.type
    ])
  );
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const item of parseJsonArrayRaw(value)) {
    if (out.length >= MAX_FIELDS) {
      break;
    }
    const raw = item ?? {};
    const fieldId = clampString(raw.fieldId, FIELD_ID_MAX);
    if (!fieldId || seen.has(fieldId)) {
      continue;
    }
    const type = typeById.get(fieldId) ?? null;
    const text3 = normalizedValue(
      type,
      clampString(raw.value, BILLING_FIELD_VALUE_MAX)
    );
    if (!text3) {
      continue;
    }
    seen.add(fieldId);
    out.push({
      fieldId,
      value: text3,
      userId: type === null || type === "person" ? clampString(raw.userId, USER_ID_MAX) : ""
    });
  }
  return out;
}
function fieldValueOf(values, fieldId) {
  return values.find((entry) => entry.fieldId === fieldId) ?? null;
}
function withFieldValue(values, fieldId, next) {
  const rest = values.filter((entry) => entry.fieldId !== fieldId);
  if (!next || next.value.trim() === "") {
    return rest;
  }
  return [...rest, { fieldId, value: next.value, userId: next.userId }];
}

// src/entities/billingExportLayout.ts
var BILLING_EXPORT_TABLES = ["rows", "totals"];
var BILLING_EXPORT_KEY_MAX = 50;
var BILLING_EXPORT_COLUMNS_MAX = 80;
function emptyExportLayout() {
  return { rows: [], totals: [] };
}
function clampEntries(value) {
  const entries = [];
  const seen = /* @__PURE__ */ new Set();
  for (const item of parseJsonArrayRaw(value)) {
    if (entries.length >= BILLING_EXPORT_COLUMNS_MAX) {
      break;
    }
    const raw = item ?? {};
    const key = clampString(raw.key, BILLING_EXPORT_KEY_MAX);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    entries.push({ key, shown: raw.shown === true });
  }
  return entries;
}
function clampExportLayout(value) {
  let raw = value;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw || "{}");
    } catch {
      raw = null;
    }
  }
  const source = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  return {
    rows: clampEntries(source.rows),
    totals: clampEntries(source.totals)
  };
}

// src/migrations/billing.ts
function billingCatalogV0ToV1(oldDoc) {
  return fillMissing(oldDoc, { exportLayout: { rows: [], totals: [] } });
}
var BILLING_CATALOG_MIGRATIONS = {
  1: billingCatalogV0ToV1
};

// src/entities/billingCatalog.ts
var BILLING_CATALOG_SCHEMA_VERSION = 1;
var exportLayoutEntriesSchema = {
  type: "array",
  items: {
    type: "object",
    properties: {
      key: { type: "string", maxLength: BILLING_EXPORT_KEY_MAX },
      shown: { type: "boolean" }
    },
    required: ["key", "shown"]
  }
};
var billingCatalogSchema = {
  version: BILLING_CATALOG_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    name: { type: "string", maxLength: NAME_MAX },
    client: { type: "string", maxLength: NAME_MAX },
    remark: { type: "string" },
    fieldDefinitions: {
      type: "array",
      items: {
        type: "object",
        properties: {
          id: { type: "string", maxLength: 36 },
          label: {
            type: "string",
            maxLength: BILLING_FIELD_LABEL_MAX
          },
          type: { type: "string", maxLength: 10 },
          perLine: { type: "boolean" }
        },
        required: ["id", "label", "type", "perLine"]
      }
    },
    exportLayout: {
      type: "object",
      properties: {
        rows: exportLayoutEntriesSchema,
        totals: exportLayoutEntriesSchema
      },
      required: ["rows", "totals"]
    },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "name",
    "client",
    "remark",
    "fieldDefinitions",
    "exportLayout",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"]]
};
var exportLayoutEntriesInput = z18.array(
  z18.object({
    key: z18.string().min(1).max(BILLING_EXPORT_KEY_MAX),
    shown: z18.boolean()
  })
).max(BILLING_EXPORT_COLUMNS_MAX).default([]);
var billingCatalogInputSchema = z18.object({
  name: z18.string().trim().min(1, rule("required")).max(NAME_MAX, rule("maxLength", { max: NAME_MAX })),
  client: z18.string().trim().max(NAME_MAX, rule("maxLength", { max: NAME_MAX })).default(""),
  remark: z18.string().trim().max(DESCRIPTION_MAX, rule("maxLength", { max: DESCRIPTION_MAX })).default(""),
  fieldDefinitions: z18.array(
    z18.object({
      id: z18.string().min(1).max(36),
      label: z18.string().trim().min(1, rule("required")).max(BILLING_FIELD_LABEL_MAX),
      type: z18.enum(BILLING_FIELD_TYPES),
      perLine: z18.boolean()
    })
  ).default([]),
  exportLayout: z18.object({
    rows: exportLayoutEntriesInput,
    totals: exportLayoutEntriesInput
  }).default({ rows: [], totals: [] })
});
var billingCatalogEntity = defineEntity({
  name: "billingCatalog",
  apiBase: "/api/billing-catalogs",
  collection: "billing_catalogs",
  rxSchema: billingCatalogSchema,
  fields: {
    name: { maxLength: NAME_MAX, merge: true },
    client: { maxLength: NAME_MAX, merge: true },
    remark: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    fieldDefinitions: { sanitizeValue: clampFieldDefinitions, merge: true },
    exportLayout: { sanitizeValue: clampExportLayout, merge: true }
  },
  migrations: BILLING_CATALOG_MIGRATIONS,
  inputSchema: billingCatalogInputSchema,
  toWire(row) {
    return {
      id: row.id,
      name: row.name || "",
      client: row.client || "",
      remark: row.remark || "",
      fieldDefinitions: clampFieldDefinitions(row.fieldDefinitions),
      exportLayout: clampExportLayout(row.exportLayout),
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      name: doc.name || "",
      client: doc.client || "",
      remark: doc.remark || "",
      // An embedded list, persisted as a JSON-string TEXT column.
      fieldDefinitions: JSON.stringify(
        clampFieldDefinitions(doc.fieldDefinitions)
      ),
      exportLayout: JSON.stringify(clampExportLayout(doc.exportLayout))
    };
  }
});

// src/entities/billingPosition.ts
import { z as z19 } from "zod";
var BILLING_CODE_MAX = 20;
var BILLING_LABEL_MAX = 2e3;
var BILLING_UNITS = [
  "hours",
  "dailyRate",
  "days",
  "pieces",
  "runningMeters",
  "squareMeters",
  "kilometers",
  "kilograms",
  "weeks",
  "lumpSum",
  "heading"
];
var UNIT_MAX = 20;
var BILLING_PRICE_MAX = 1e7;
var BILLING_PERIODS = ["daily", "weekly", "once"];
function billingPeriod(value) {
  return BILLING_PERIODS.includes(value) ? value : "daily";
}
function clampBillingPrice(value) {
  const price = Number(value);
  return Number.isFinite(price) ? Math.round(Math.max(0, Math.min(BILLING_PRICE_MAX, price)) * 100) / 100 : 0;
}
function billingUnit(value) {
  return BILLING_UNITS.includes(value) ? value : "pieces";
}
function isTimeBillingUnit(unit) {
  return unit === "hours" || unit === "dailyRate";
}
var BILLING_POSITION_USES = ["worker", "equipment", "text"];
function clampPositionUses(value) {
  const given = new Set(parseStringArray(value));
  return BILLING_POSITION_USES.filter((use) => given.has(use));
}
function positionFitsUse(position, use) {
  return position.unit !== "heading" && position.billable && position.appliesTo.includes(use);
}
function normalizeBillingCode(value) {
  return value.replace(/\s+/g, "").replace(/\.+$/, "");
}
function compareBillingCodes(leftCode, rightCode) {
  const leftParts = normalizeBillingCode(leftCode).split(".");
  const rightParts = normalizeBillingCode(rightCode).split(".");
  const length = Math.max(leftParts.length, rightParts.length);
  for (let i = 0; i < length; i++) {
    const leftPart = leftParts[i];
    const rightPart = rightParts[i];
    if (leftPart === void 0) {
      return -1;
    }
    if (rightPart === void 0) {
      return 1;
    }
    const order = leftPart.localeCompare(rightPart, "de", {
      numeric: true
    });
    if (order !== 0) {
      return order;
    }
  }
  return 0;
}
var BILLING_POSITION_SCHEMA_VERSION = 0;
var billingPositionSchema = {
  version: BILLING_POSITION_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    catalogId: { type: "string", ref: "billing_catalogs", maxLength: 36 },
    code: { type: "string", maxLength: BILLING_CODE_MAX },
    label: { type: "string", maxLength: BILLING_LABEL_MAX },
    unit: { type: "string", maxLength: UNIT_MAX },
    appliesTo: {
      type: "array",
      items: { type: "string", maxLength: 20 },
      default: [...BILLING_POSITION_USES]
    },
    billable: { type: "boolean", default: true },
    price: { type: "number", default: 0 },
    billingPeriod: { type: "string", maxLength: 10, default: "daily" },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "catalogId",
    "code",
    "label",
    "unit",
    "appliesTo",
    "billable",
    "price",
    "billingPeriod",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "catalogId"]
};
var billingPositionInputSchema = z19.object({
  catalogId: z19.string().min(1, rule("required")).max(ID_MAX),
  code: z19.string().transform(normalizeBillingCode).pipe(
    z19.string().min(1, rule("required")).max(
      BILLING_CODE_MAX,
      rule("maxLength", { max: BILLING_CODE_MAX })
    )
  ),
  label: z19.string().trim().min(1, rule("required")).max(BILLING_LABEL_MAX, rule("maxLength", { max: BILLING_LABEL_MAX })),
  unit: z19.enum(BILLING_UNITS),
  appliesTo: z19.array(z19.enum(BILLING_POSITION_USES)).default([...BILLING_POSITION_USES]),
  billable: z19.boolean().default(true),
  price: z19.number().min(0).max(BILLING_PRICE_MAX).default(0),
  billingPeriod: z19.enum(BILLING_PERIODS).default("daily")
});
var billingPositionEntity = defineEntity({
  name: "billingPosition",
  apiBase: "/api/billing-positions",
  collection: "billing_positions",
  rxSchema: billingPositionSchema,
  fields: {
    catalogId: { maxLength: ID_MAX },
    code: { maxLength: BILLING_CODE_MAX, merge: true },
    label: { kind: "text", maxLength: BILLING_LABEL_MAX, merge: true },
    unit: { sanitizeValue: billingUnit, merge: true },
    appliesTo: {
      kind: "array",
      values: BILLING_POSITION_USES,
      sanitizeValue: clampPositionUses,
      merge: true
    },
    billable: { kind: "boolean", merge: true },
    price: { kind: "number", sanitizeValue: clampBillingPrice, merge: true },
    billingPeriod: { sanitizeValue: billingPeriod, merge: true }
  },
  inputSchema: billingPositionInputSchema,
  toWire(row) {
    return {
      id: row.id,
      catalogId: row.catalogId || "",
      code: row.code || "",
      label: row.label || "",
      unit: billingUnit(row.unit),
      appliesTo: clampPositionUses(row.appliesTo),
      billable: row.billable !== false,
      price: clampBillingPrice(row.price),
      billingPeriod: billingPeriod(row.billingPeriod),
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      catalogId: doc.catalogId || "",
      code: doc.code || "",
      label: doc.label || "",
      unit: billingUnit(doc.unit),
      // A JSON-string TEXT column.
      appliesTo: JSON.stringify(clampPositionUses(doc.appliesTo)),
      billable: doc.billable !== false,
      price: clampBillingPrice(doc.price),
      billingPeriod: billingPeriod(doc.billingPeriod)
    };
  }
});

// src/entities/billingStatement.ts
import { z as z20 } from "zod";
var BILLING_STATEMENT_STATUSES = ["draft", "closed"];
var STATUS_MAX2 = 10;
var WEEK_KEY_MAX = 8;
var WEEK_KEY_PATTERN = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/;
function billingStatementStatus(value) {
  return value === "closed" ? "closed" : "draft";
}
function billingWeekKey(year, week) {
  return `${year}-W${String(week).padStart(2, "0")}`;
}
function parseBillingWeekKey(key) {
  if (!WEEK_KEY_PATTERN.test(key)) {
    return null;
  }
  return { year: Number(key.slice(0, 4)), week: Number(key.slice(6)) };
}
function clampWeekKeys(value) {
  const keys = parseStringArray(value).filter(
    (key) => WEEK_KEY_PATTERN.test(key)
  );
  return [...new Set(keys)].sort();
}
var BILLING_STATEMENT_SCHEMA_VERSION = 0;
var billingStatementSchema = {
  version: BILLING_STATEMENT_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    catalogId: { type: "string", ref: "billing_catalogs", maxLength: 36 },
    title: { type: "string", maxLength: NAME_MAX },
    projectIds: {
      type: "array",
      ref: "projects",
      items: { type: "string", maxLength: 36 }
    },
    weeks: {
      type: "array",
      items: { type: "string", maxLength: WEEK_KEY_MAX }
    },
    fieldValues: {
      type: "array",
      items: {
        type: "object",
        properties: {
          fieldId: { type: "string", maxLength: 36 },
          value: {
            type: "string",
            maxLength: BILLING_FIELD_VALUE_MAX
          },
          userId: { type: "string", maxLength: 36 }
        },
        required: ["fieldId", "value", "userId"]
      }
    },
    remark: { type: "string" },
    status: { type: "string", maxLength: STATUS_MAX2 },
    closedAt: { type: "string", maxLength: 30 },
    closedBy: { type: "string", maxLength: 36 },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "catalogId",
    "title",
    "projectIds",
    "weeks",
    "fieldValues",
    "remark",
    "status",
    "closedAt",
    "closedBy",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"]]
};
var billingFieldValuesInput = z20.array(
  z20.object({
    fieldId: z20.string().min(1).max(ID_MAX),
    value: z20.string().trim().max(BILLING_FIELD_VALUE_MAX),
    userId: z20.string().max(ID_MAX).default("")
  })
).default([]);
var billingStatementInputSchema = z20.object({
  catalogId: z20.string().min(1, rule("required")).max(ID_MAX),
  title: z20.string().trim().min(1, rule("required")).max(NAME_MAX, rule("maxLength", { max: NAME_MAX })),
  projectIds: z20.array(z20.string().max(ID_MAX)).min(1, rule("required")),
  weeks: z20.array(z20.string().regex(WEEK_KEY_PATTERN)).min(1, rule("required")),
  fieldValues: billingFieldValuesInput,
  remark: z20.string().trim().max(DESCRIPTION_MAX, rule("maxLength", { max: DESCRIPTION_MAX })).default("")
});
var billingStatementEntity = defineEntity({
  name: "billingStatement",
  apiBase: "/api/billing-statements",
  collection: "billing_statements",
  rxSchema: billingStatementSchema,
  historyAllFields: true,
  fields: {
    catalogId: { maxLength: ID_MAX, merge: true },
    title: { maxLength: NAME_MAX, merge: true },
    projectIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    weeks: { kind: "array", sanitizeValue: clampWeekKeys, merge: true },
    fieldValues: { sanitizeValue: clampFieldValues, merge: true },
    remark: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    status: { sanitizeValue: billingStatementStatus, merge: true },
    closedAt: { kind: "datetime", merge: true },
    closedBy: { maxLength: ID_MAX, merge: true }
  },
  inputSchema: billingStatementInputSchema,
  toWire(row) {
    const closedAt = row.closedAt;
    return {
      id: row.id,
      catalogId: row.catalogId || "",
      title: row.title || "",
      projectIds: parseStringArray(row.projectIds),
      weeks: clampWeekKeys(row.weeks),
      fieldValues: clampFieldValues(row.fieldValues),
      remark: row.remark || "",
      status: billingStatementStatus(row.status),
      closedAt: closedAt ? closedAt.toISOString() : "",
      closedBy: row.closedBy || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      catalogId: doc.catalogId || "",
      title: doc.title || "",
      // The id and week sets persist as JSON-string TEXT columns.
      projectIds: JSON.stringify(doc.projectIds || []),
      weeks: JSON.stringify(clampWeekKeys(doc.weeks)),
      fieldValues: JSON.stringify(clampFieldValues(doc.fieldValues)),
      remark: doc.remark || "",
      status: billingStatementStatus(doc.status),
      closedAt: doc.closedAt ? new Date(doc.closedAt) : null,
      closedBy: clampString(doc.closedBy, ID_MAX)
    };
  }
});

// src/entities/billingStatementLine.ts
import { z as z21 } from "zod";
var WORKER_NAME_MAX = 150;
var TIME_MAX = 5;
var KIND_MAX2 = 20;
var UNIT_MAX2 = 20;
var PAUSE_MINUTES_MAX = 1440;
var QUANTITY_MAX = 1e6;
var TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
var BILLING_LINE_KINDS = [
  "booking",
  "worker",
  "equipment",
  "text"
];
function billingLineKind(value) {
  return BILLING_LINE_KINDS.includes(value) ? value : "text";
}
function clampTime(value) {
  const time = clampString(value, TIME_MAX);
  return TIME_PATTERN.test(time) ? time : "";
}
function clampPause(value) {
  const minutes = Math.trunc(Number(value));
  return Number.isFinite(minutes) ? Math.max(0, Math.min(PAUSE_MINUTES_MAX, minutes)) : 0;
}
function clampShare(value) {
  const minutes = Math.trunc(Number(value));
  return Number.isFinite(minutes) ? Math.max(0, Math.min(QUANTITY_MAX, minutes)) : 0;
}
function clampAmount(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.max(0, Math.min(QUANTITY_MAX, amount)) : 0;
}
function dateToWire(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString().slice(0, 10);
  }
  return typeof value === "string" ? value.slice(0, 10) : "";
}
function dateToContent(value) {
  const day = clampString(value, 10);
  return DAY_PATTERN.test(day) ? /* @__PURE__ */ new Date(`${day}T00:00:00Z`) : null;
}
var BILLING_STATEMENT_LINE_SCHEMA_VERSION = 0;
var billingStatementLineSchema = {
  version: BILLING_STATEMENT_LINE_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    statementId: {
      type: "string",
      ref: "billing_statements",
      maxLength: 36
    },
    kind: { type: "string", maxLength: KIND_MAX2 },
    timeEntryId: { type: "string", ref: "timeEntries", maxLength: 36 },
    projectId: { type: "string", ref: "projects", maxLength: 36 },
    positionId: {
      type: "string",
      ref: "billing_positions",
      maxLength: 36
    },
    shareMinutes: { type: "number" },
    date: { type: "string", maxLength: 10 },
    workerName: { type: "string", maxLength: WORKER_NAME_MAX },
    userId: { type: "string", maxLength: 36 },
    people: {
      type: "array",
      items: { type: "string", maxLength: PERSON_MAX }
    },
    personIds: {
      type: "array",
      ref: "users",
      items: { type: "string", maxLength: 36 }
    },
    startTime: { type: "string", maxLength: TIME_MAX },
    endTime: { type: "string", maxLength: TIME_MAX },
    pauseMinutes: { type: "number" },
    quantity: { type: "number" },
    fieldValues: {
      type: "array",
      items: {
        type: "object",
        properties: {
          fieldId: { type: "string", maxLength: 36 },
          value: {
            type: "string",
            maxLength: BILLING_FIELD_VALUE_MAX
          },
          userId: { type: "string", maxLength: 36 }
        },
        required: ["fieldId", "value", "userId"]
      }
    },
    positionCode: { type: "string", maxLength: BILLING_CODE_MAX },
    positionLabel: { type: "string", maxLength: BILLING_LABEL_MAX },
    positionUnit: { type: "string", maxLength: UNIT_MAX2 },
    positionPrice: { type: "number", default: 0 },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "statementId",
    "kind",
    "timeEntryId",
    "projectId",
    "positionId",
    "shareMinutes",
    "date",
    "workerName",
    "userId",
    "people",
    "personIds",
    "startTime",
    "endTime",
    "pauseMinutes",
    "quantity",
    "fieldValues",
    "positionCode",
    "positionLabel",
    "positionUnit",
    "positionPrice",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "statementId", "timeEntryId"]
};
var billingStatementLineInputSchema = z21.object({
  statementId: z21.string().min(1, rule("required")).max(ID_MAX),
  kind: z21.enum(BILLING_LINE_KINDS),
  timeEntryId: z21.string().max(ID_MAX).default(""),
  projectId: z21.string().max(ID_MAX).default(""),
  positionId: z21.string().max(ID_MAX).default(""),
  shareMinutes: z21.number().int().min(0).default(0),
  date: z21.string().default(""),
  workerName: z21.string().trim().max(WORKER_NAME_MAX, rule("maxLength", { max: WORKER_NAME_MAX })).default(""),
  userId: z21.string().max(ID_MAX).default(""),
  people: z21.array(z21.string().max(PERSON_MAX)).default([]),
  personIds: z21.array(z21.string().max(ID_MAX)).default([]),
  startTime: z21.string().default(""),
  endTime: z21.string().default(""),
  pauseMinutes: z21.number().int().min(0).max(PAUSE_MINUTES_MAX).default(0),
  quantity: z21.number().min(0).max(QUANTITY_MAX).default(0),
  fieldValues: billingFieldValuesInput
});
var billingStatementLineEntity = defineEntity({
  name: "billingStatementLine",
  apiBase: "/api/billing-statement-lines",
  collection: "billing_statement_lines",
  rxSchema: billingStatementLineSchema,
  fields: {
    statementId: { maxLength: ID_MAX },
    kind: { sanitizeValue: billingLineKind },
    timeEntryId: { maxLength: ID_MAX },
    projectId: { maxLength: ID_MAX, merge: true },
    positionId: { maxLength: ID_MAX, merge: true },
    shareMinutes: {
      kind: "number",
      sanitizeValue: clampShare,
      merge: true
    },
    date: { kind: "date", merge: true },
    workerName: { maxLength: WORKER_NAME_MAX, merge: true },
    userId: { maxLength: ID_MAX, merge: true },
    people: { kind: "list", itemMaxLength: PERSON_MAX, merge: true },
    personIds: { kind: "list", itemMaxLength: ID_MAX, merge: true },
    startTime: { sanitizeValue: clampTime, merge: true },
    endTime: { sanitizeValue: clampTime, merge: true },
    pauseMinutes: {
      kind: "number",
      sanitizeValue: clampPause,
      merge: true
    },
    quantity: {
      kind: "number",
      sanitizeValue: clampAmount,
      merge: true
    },
    fieldValues: { sanitizeValue: clampFieldValues, merge: true },
    positionCode: { maxLength: BILLING_CODE_MAX, merge: true },
    positionLabel: {
      kind: "text",
      maxLength: BILLING_LABEL_MAX,
      merge: true
    },
    positionUnit: { maxLength: UNIT_MAX2, merge: true },
    positionPrice: {
      kind: "number",
      sanitizeValue: clampBillingPrice,
      merge: true
    }
  },
  inputSchema: billingStatementLineInputSchema,
  toWire(row) {
    return {
      id: row.id,
      statementId: row.statementId || "",
      kind: billingLineKind(row.kind),
      timeEntryId: row.timeEntryId || "",
      projectId: row.projectId || "",
      positionId: row.positionId || "",
      shareMinutes: clampShare(row.shareMinutes),
      date: dateToWire(row.date),
      workerName: row.workerName || "",
      userId: row.userId || "",
      people: parseStringArray(row.people),
      personIds: parseStringArray(row.personIds),
      startTime: clampTime(row.startTime),
      endTime: clampTime(row.endTime),
      pauseMinutes: clampPause(row.pauseMinutes),
      quantity: clampAmount(row.quantity),
      fieldValues: clampFieldValues(row.fieldValues),
      positionCode: row.positionCode || "",
      positionLabel: row.positionLabel || "",
      positionUnit: row.positionUnit ? billingUnit(row.positionUnit) : "",
      positionPrice: clampBillingPrice(row.positionPrice),
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      statementId: doc.statementId || "",
      kind: billingLineKind(doc.kind),
      timeEntryId: doc.timeEntryId || "",
      projectId: doc.projectId || "",
      positionId: doc.positionId || "",
      shareMinutes: clampShare(doc.shareMinutes),
      date: dateToContent(doc.date),
      workerName: doc.workerName || "",
      userId: doc.userId || "",
      // Positional pairs, persisted as JSON-string TEXT columns.
      people: JSON.stringify(
        clampStringList(doc.people, { itemMaxLength: PERSON_MAX })
      ),
      personIds: JSON.stringify(
        clampStringList(doc.personIds, { itemMaxLength: ID_MAX })
      ),
      startTime: clampTime(doc.startTime),
      endTime: clampTime(doc.endTime),
      pauseMinutes: clampPause(doc.pauseMinutes),
      quantity: clampAmount(doc.quantity),
      fieldValues: JSON.stringify(clampFieldValues(doc.fieldValues)),
      positionCode: doc.positionCode || "",
      positionLabel: doc.positionLabel || "",
      positionUnit: doc.positionUnit || "",
      positionPrice: clampBillingPrice(doc.positionPrice)
    };
  }
});

// src/entities/mediaGroup.ts
import { z as z22 } from "zod";
var OWNER_TYPE_MAX4 = 30;
var CAPTION_MAX2 = 500;
var LAYOUT_MAX = 2e3;
var MEDIA_GROUP_SCHEMA_VERSION = 2;
var mediaGroupSchema = {
  version: MEDIA_GROUP_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    ownerType: { type: "string", maxLength: OWNER_TYPE_MAX4 },
    ownerId: { type: "string", maxLength: 36 },
    caption: { type: "string", maxLength: CAPTION_MAX2 },
    layout: { type: "string" },
    sortOrder: { type: "number", default: 0 },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "ownerType",
    "ownerId",
    "caption",
    "layout",
    "sortOrder",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId", "ownerId"]
};
var mediaGroupInputSchema = z22.object({
  ownerType: z22.enum(MEDIA_OWNER_TYPES),
  ownerId: z22.string().trim().min(1, rule("required")).max(ID_MAX),
  caption: z22.string().max(CAPTION_MAX2, rule("maxLength", { max: CAPTION_MAX2 })).default(""),
  layout: z22.string().max(LAYOUT_MAX).refine(
    (layout) => parseMediaGroupLayout(layout).length > 0,
    rule("required")
  ),
  sortOrder: z22.number().int().default(0)
});
var mediaGroupEntity = defineEntity({
  name: "mediaGroup",
  apiBase: "/api/media-groups",
  collection: "media_groups",
  rxSchema: mediaGroupSchema,
  migrations: MEDIA_GROUP_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    ownerType: { maxLength: OWNER_TYPE_MAX4 },
    ownerId: { kind: "ref", maxLength: ID_MAX },
    caption: { maxLength: CAPTION_MAX2, merge: true },
    layout: {
      maxLength: LAYOUT_MAX,
      merge: true,
      sanitizeValue: serializeMediaGroupLayout
    },
    sortOrder: { kind: "number", merge: true }
  },
  inputSchema: mediaGroupInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      ownerType: row.ownerType || "",
      ownerId: row.ownerId || "",
      caption: row.caption || "",
      layout: serializeMediaGroupLayout(row.layout),
      sortOrder: Number(row.sortOrder) || 0,
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      ownerType: doc.ownerType || "",
      ownerId: doc.ownerId || "",
      caption: doc.caption || "",
      layout: serializeMediaGroupLayout(doc.layout),
      sortOrder: Math.round(Number(doc.sortOrder)) || 0
    };
  }
});

// src/entities/find.ts
import { z as z23 } from "zod";

// src/migrations/records.ts
function dropPhotoDate(oldDoc) {
  const { photoDate, ...rest } = oldDoc;
  return foldDayIntoRecordedAt(rest, photoDate);
}
var sameText = (left, right) => left === right;
function addDescriptionRefs(oldDoc) {
  return fillMissing(oldDoc, { planumIds: [], profileIds: [] });
}
function photoFindSlipToArray(oldDoc) {
  const { findSlipNumber, ...rest } = oldDoc;
  const single = typeof findSlipNumber === "string" ? findSlipNumber.trim() : "";
  return single ? {
    ...rest,
    findSlipNumbers: withEntry(rest.findSlipNumbers, single, sameText)
  } : fillMissing(rest, { findSlipNumbers: [] });
}
function drawingSheetPlanTypesAndRefs(oldDoc) {
  const { planType, ...rest } = oldDoc;
  const single = typeof planType === "string" ? planType.trim() : "";
  return fillMissing(
    single ? {
      ...rest,
      planTypes: withEntry(rest.planTypes, single, sameText)
    } : rest,
    { planTypes: [], planumIds: [], profileIds: [] }
  );
}
function findFindingIdToRefs(oldDoc) {
  const { findingId, ...rest } = oldDoc;
  const single = typeof findingId === "string" ? findingId.trim() : "";
  return fillMissing(
    single ? {
      ...rest,
      findingIds: withEntry(rest.findingIds, single, sameText)
    } : rest,
    { planumIds: [], profileIds: [], findingIds: [] }
  );
}
var FIND_MIGRATIONS = {
  // v1: added recordedAt (Aufnahmedatum), backfilled from createdAt.
  1: addRecordedAt,
  // v2: findingId -> planum/profile/finding ref arrays.
  2: findFindingIdToRefs,
  // v3: added excavatorId (app-user link), empty until re-saved.
  3: (oldDoc) => fillMissing(oldDoc, { excavatorId: "" }),
  /**
   * v4: dropped findDate — recordedAt is the single date (shown as
   * "Funddatum"), folded from findDate where that was set.
   */
  4: (oldDoc) => {
    const findDate = oldDoc.findDate;
    const next = {
      ...oldDoc,
      recordedAt: findDate || oldDoc.recordedAt
    };
    delete next.findDate;
    return next;
  },
  // v5: added strayFindNumber (Einzelfundnummer), empty until filled in —
  // the server column starts empty the same way.
  5: (oldDoc) => fillMissing(oldDoc, { strayFindNumber: "" }),
  // v6: added internalInformation (Interne Informationen), empty.
  6: (oldDoc) => fillMissing(oldDoc, { internalInformation: "" }),
  // v7: the Bezeichnung may hold 2000 characters. Only the cap moved.
  7: (oldDoc) => oldDoc,
  // v8: added subFindSlipNumber (Unterfundzettelnummer), empty.
  8: (oldDoc) => fillMissing(oldDoc, { subFindSlipNumber: "" }),
  // v9: added sampleType (Probenart), empty.
  9: (oldDoc) => fillMissing(oldDoc, { sampleType: "" })
};
var OVERALL_DESCRIPTION_MIGRATIONS = {
  /**
   * v1: embedded findingIds added (was the overall_description_findings
   * join); existing docs start empty and re-sync.
   */
  1: (oldDoc) => fillMissing(oldDoc, { findingIds: [] }),
  // v2: added recordedAt (Aufnahmedatum), backfilled from createdAt.
  2: addRecordedAt,
  // v3: dropped `date` — recordedAt is the single date field.
  3: dropDescriptionDate,
  // v4: added planumIds/profileIds (specific-description refs).
  4: addDescriptionRefs,
  // v5: added editorId (app-user link), empty until re-saved.
  5: (oldDoc) => fillMissing(oldDoc, { editorId: "" })
};
var DIARY_MIGRATIONS = {
  // v1: weatherCondition widened to a multi-tag string; stored values are
  // unchanged.
  1: (oldDoc) => oldDoc,
  // v2: visitors + parcelIds embedded (were collections); existing docs
  // start empty and re-sync.
  2: (oldDoc) => fillMissing(oldDoc, { visitors: [], parcelIds: [] }),
  // v3: added recordedAt (Aufnahmedatum), backfilled from createdAt.
  3: addRecordedAt,
  // v4: dropped `date` — recordedAt is the single date field.
  4: (oldDoc) => {
    const { date, ...rest } = oldDoc;
    return foldDayIntoRecordedAt(rest, date);
  },
  // v5: added the person app-user links, empty until re-saved.
  5: (oldDoc) => fillMissing(oldDoc, {
    authorId: "",
    excavationLeaderId: "",
    technicianId: "",
    staffIds: []
  }),
  // v6: added the Funde activity text; existing days have none.
  6: (oldDoc) => fillMissing(oldDoc, { finds: "" }),
  /**
   * v7: technicianId -> technicianIds[] (positional to the ", "-split
   * names); the day gained its four "Letzte vergebene Nrn.".
   */
  7: (oldDoc) => {
    const { technicianId, ...rest } = oldDoc;
    return fillMissing(rest, {
      technicianIds: technicianId ? [technicianId] : [],
      lastFindingNumber: "",
      lastProfileNumber: "",
      lastStrayFindNumber: "",
      lastGeoprofileNumber: ""
    });
  },
  /**
   * v8: a visitor's name may hold a whole paragraph (the ExcaBook Besuch
   * column is free text). Only the cap moved.
   */
  8: (oldDoc) => oldDoc,
  /**
   * v9: a visitor gained `note`. It is `required`, so every stored visitor
   * needs the empty string — a missing key fails validation on read.
   */
  9: (oldDoc) => ({
    ...oldDoc,
    visitors: (oldDoc.visitors ?? []).map((visitor) => ({ note: "", ...visitor }))
  }),
  /**
   * v10: two more "Letzte vergebene Nrn." (Sondage, Teilfoto), both
   * `required`, so stored days need the empty string.
   */
  10: (oldDoc) => fillMissing(oldDoc, {
    lastSondageNumber: "",
    lastPartialPhotoNumber: ""
  }),
  /**
   * v11: Sondage/Teilfoto became a list (`sondagePhotos`); what the day
   * already named becomes its first entry. The two numbers stay on the doc
   * as the highest of the list.
   */
  11: (oldDoc) => {
    const sondage = oldDoc.lastSondageNumber || "";
    const partialPhoto = oldDoc.lastPartialPhotoNumber || "";
    return fillMissing(oldDoc, {
      sondagePhotos: sondage || partialPhoto ? [{ sondage, partialPhoto }] : []
    });
  },
  // v12: `weatherExtra` may hold 255 characters. Only the cap moved.
  12: (oldDoc) => oldDoc
};
var TIME_ENTRY_MIGRATIONS = {
  /**
   * v1: entryType discriminates the Mitarbeiter and Geräte Zeiterfassungen
   * sharing this collection; every existing row is a worker entry.
   */
  1: (oldDoc) => fillMissing(oldDoc, { entryType: "worker" }),
  // v2: quantity (Anzahl) — how many of the named thing the row is about.
  // Offered on Geräte only; every existing row is a single unit.
  2: (oldDoc) => fillMissing(oldDoc, { quantity: 1 })
};
var AREA_DESCRIPTION_MIGRATIONS = {
  // v1: added the optional Sondage — part of the Fläche × Planum key.
  1: (oldDoc) => fillMissing(oldDoc, { sondage: "" })
};
var PHOTO_MIGRATIONS = {
  // v1: dropped `photoDate` — recordedAt is the single date field.
  1: dropPhotoDate,
  // v2: added planumIds/profileIds (specific-description refs).
  2: addDescriptionRefs,
  // v3: findSlipNumber (string) -> findSlipNumbers (array).
  3: photoFindSlipToArray,
  // v4: added photographerId (app-user link), empty until re-saved.
  4: (oldDoc) => fillMissing(oldDoc, { photographerId: "" })
};
var DRAWING_SHEET_MIGRATIONS = {
  // v1: planType (string) -> planTypes (array) + description refs.
  1: drawingSheetPlanTypesAndRefs,
  // v2: added drafterId (app-user link), empty until re-saved.
  2: (oldDoc) => fillMissing(oldDoc, { drafterId: "" }),
  // v3: drafter + drafterId became string[]. Flatten-normalize so a
  // single value wraps AND already-array data isn't double-wrapped.
  3: (oldDoc) => cleanPersonList(oldDoc, "drafter", "drafterId"),
  // v4: repair docs a BUGGY v3 already double-wrapped into [["x"]].
  4: (oldDoc) => cleanPersonList(oldDoc, "drafter", "drafterId"),
  // v5: added remark (Bemerkung), empty on existing sheets.
  5: (oldDoc) => fillMissing(oldDoc, { remark: "" })
};
var SAMPLE_MIGRATIONS = {
  // v1: the Probennummer is gone — a Probe is named by type and date.
  1: (oldDoc) => {
    const { sampleNumber: _sampleNumber, ...rest } = oldDoc;
    return rest;
  },
  // v2: an own Lage for a Probe taken from no Planum or Profil.
  2: (oldDoc) => fillMissing(oldDoc, { area: "", trench: "", planum: "", profile: "" })
};

// src/entities/find.ts
var FIND_LABEL_MAX = 2e3;
var VOCAB_MAX = 100;
var SHORT_VOCAB_MAX = 50;
var POSITION_MAX = 30;
var TEXT_FIELD_MAX3 = 255;
var YES_NO_MAX = 10;
var ARTEFACT_FIND_GROUP = "Artefakte";
var SAMPLE_FIND_GROUP = "Proben";
var FIND_GROUP_OPTIONS = [
  "Artefakte",
  "Proben",
  "menschl. \xDCberreste",
  "tierische Reste"
];
var MATERIAL_DETERMINABILITY_OPTIONS = [
  "Material bestimmbar",
  "Material nicht bestimmbar"
];
var MATERIAL_SUBSTANCE_TYPE_OPTIONS = [
  "Anorganisch",
  "Organisch"
];
var MATERIAL_GROUP_OPTIONS = [
  "Kunststoff",
  "Menschlich",
  "Metall/Legierung",
  "Mineralisch",
  "Pflanzlich",
  "Tierisch"
];
var MATERIAL_NAME_OPTIONS = [
  "Aluminium",
  "Bernstein",
  "Beton",
  "Blei",
  "Bodenprobe",
  "Eierschale",
  "Eisen",
  "Elfenbein",
  "Email",
  "Feder",
  "Fell",
  "Gagat/Lignit/Sapropelit",
  "Geweih",
  "Glas",
  "Gold",
  "Gummi oder elastisch",
  "Haar",
  "Hartkunststoff oder plastisch",
  "Haut",
  "Holz",
  "Holzkohle",
  "Horn",
  "Keramik",
  "Knochen/Bein",
  "Koralle",
  "Kupfer",
  "Kupferlegierung",
  "Leder",
  "Muschel",
  "Papier/Pappe",
  "Pech",
  "Perlmutt",
  "Putz/M\xF6rtel",
  "Rinde/Kork",
  "Samen/Fr\xFCchte",
  "Schlacke",
  "Schnecke",
  "Silber",
  "Silex",
  "Stein/Mineral",
  "Ton/Lehm",
  "Zahn",
  "Zink",
  "Zinn"
];
var MATERIAL_PARENT_MAP = {
  Aluminium: "Metall/Legierung",
  Anorganisch: "Material bestimmbar",
  Bernstein: "Pflanzlich",
  Beton: "Mineralisch",
  Blei: "Metall/Legierung",
  Bodenprobe: "Mineralisch",
  Eierschale: "Tierisch",
  Eisen: "Metall/Legierung",
  Elfenbein: "Tierisch",
  Email: "Mineralisch",
  Feder: "Tierisch",
  Fell: "Tierisch",
  "Gagat/Lignit/Sapropelit": "Pflanzlich",
  Geweih: "Tierisch",
  Glas: "Mineralisch",
  Gold: "Metall/Legierung",
  "Gummi oder elastisch": "Kunststoff",
  Haar: "Tierisch",
  "Hartkunststoff oder plastisch": "Kunststoff",
  Haut: "Tierisch",
  Holz: "Pflanzlich",
  Holzkohle: "Pflanzlich",
  Horn: "Tierisch",
  Keramik: "Mineralisch",
  "Knochen/Bein": "Tierisch",
  Koralle: "Tierisch",
  Kunststoff: "Organisch",
  Kupfer: "Metall/Legierung",
  Kupferlegierung: "Metall/Legierung",
  Leder: "Tierisch",
  Menschlich: "Organisch",
  "Metall/Legierung": "Anorganisch",
  Mineralisch: "Anorganisch",
  Muschel: "Tierisch",
  Organisch: "Material bestimmbar",
  "Papier/Pappe": "Pflanzlich",
  Pech: "Pflanzlich",
  Perlmutt: "Tierisch",
  Pflanzlich: "Organisch",
  "Putz/M\xF6rtel": "Mineralisch",
  "Rinde/Kork": "Pflanzlich",
  "Samen/Fr\xFCchte": "Pflanzlich",
  Schlacke: "Mineralisch",
  Schnecke: "Tierisch",
  Silber: "Metall/Legierung",
  Silex: "Mineralisch",
  "Stein/Mineral": "Mineralisch",
  Tierisch: "Organisch",
  "Ton/Lehm": "Mineralisch",
  Zahn: "Tierisch",
  Zink: "Metall/Legierung",
  Zinn: "Metall/Legierung"
};
var MATERIAL_LEVELS = [
  "materialDeterminability",
  "materialSubstanceType",
  "materialGroup",
  "materialName"
];
var MATERIAL_LEVEL_OPTIONS = {
  materialDeterminability: MATERIAL_DETERMINABILITY_OPTIONS,
  materialSubstanceType: MATERIAL_SUBSTANCE_TYPE_OPTIONS,
  materialGroup: MATERIAL_GROUP_OPTIONS,
  materialName: MATERIAL_NAME_OPTIONS
};
function materialLevelOf(value) {
  for (const level of MATERIAL_LEVELS) {
    if (MATERIAL_LEVEL_OPTIONS[level].includes(value)) {
      return level;
    }
  }
  return null;
}
function resolveMaterialLevels(value) {
  const levels = {
    materialDeterminability: "",
    materialSubstanceType: "",
    materialGroup: "",
    materialName: ""
  };
  if (!value) {
    return levels;
  }
  const ownLevel = materialLevelOf(value);
  if (!ownLevel) {
    levels.materialName = value;
    return levels;
  }
  levels[ownLevel] = value;
  let current = MATERIAL_PARENT_MAP[value];
  while (current) {
    const level = materialLevelOf(current);
    if (level && !levels[level]) {
      levels[level] = current;
    }
    current = MATERIAL_PARENT_MAP[current];
  }
  return levels;
}
function materialOptionGroups(level, selected) {
  const levelIndex = MATERIAL_LEVELS.indexOf(level);
  const parentLevel = levelIndex > 0 ? MATERIAL_LEVELS[levelIndex - 1] : null;
  const options = MATERIAL_LEVEL_OPTIONS[level].map((value) => ({
    value,
    group: parentLevel ? MATERIAL_PARENT_MAP[value] ?? "" : ""
  }));
  if (!parentLevel) {
    return options;
  }
  const chosen = selected[parentLevel] ?? "";
  const groups = [...new Set(options.map((option) => option.group))].sort(
    (leftGroup, rightGroup) => Number(rightGroup === chosen) - Number(leftGroup === chosen) || leftGroup.localeCompare(rightGroup, "de")
  );
  return groups.flatMap(
    (group) => options.filter((option) => option.group === group)
  );
}
var POSITION_TO_PLANUM_OPTIONS = ["In", "Unter", "\xDCber"];
var POSITION_TO_PROFILE_OPTIONS = ["Hinter", "In", "Vor"];
var FIND_TREATMENT_OPTIONS = ["Keine", "Nass", "Trocken"];
var YES_NO_OPTIONS = ["Ja", "Nein"];
var STRAY_FIND_NUMBER_PATTERN = /\b(?:Einzelfundnummer|Einzelfund|EF)\s*[-–.]?\s*(?:Nr\.?)?\s*:?\s*(\d+(?:[/-]\d+)*[a-zA-Z]?)\b/i;
var EXCABOOK_STRAY_FIND_PREFIX = "EF-Nr.";
var STRAY_FIND_PART_PATTERN = /\bTeil\s+\d+\s+von\s+\d+\b/i;
var LABEL_EDGE_SEPARATORS = /^[\s,;.\-–/]+|[\s,;.\-–/]+$/g;
function splitStrayFindNumber(bezeichnung) {
  const match = STRAY_FIND_NUMBER_PATTERN.exec(bezeichnung);
  if (!match) {
    return { label: bezeichnung.trim(), strayFindNumber: "" };
  }
  const label = (bezeichnung.slice(0, match.index) + " " + bezeichnung.slice(match.index + match[0].length)).replace(STRAY_FIND_PART_PATTERN, " ").replace(/\s+/g, " ").replace(/\s*([,;])\s*[,;]\s*/g, "$1 ").replace(LABEL_EDGE_SEPARATORS, "").trim();
  return { label, strayFindNumber: match[1] };
}
var EXCABOOK_SAMPLE_TYPE_PREFIX = "Probenart:";
var SAMPLE_TYPE_PATTERN = /\bProbenart\s*:\s*([^,;]+)/i;
function splitSampleType(bezeichnung) {
  const match = SAMPLE_TYPE_PATTERN.exec(bezeichnung);
  if (!match) {
    return { label: bezeichnung.trim(), sampleType: "" };
  }
  const label = (bezeichnung.slice(0, match.index) + " " + bezeichnung.slice(match.index + match[0].length)).replace(/\s+/g, " ").replace(/\s*([,;])\s*[,;]\s*/g, "$1 ").replace(LABEL_EDGE_SEPARATORS, "").trim();
  return { label, sampleType: match[1].trim() };
}
function joinSampleType(label, sampleType) {
  const type = sampleType.trim();
  if (!type) {
    return label;
  }
  const rest = splitSampleType(label).label;
  const prefixed = `${EXCABOOK_SAMPLE_TYPE_PREFIX} ${type}`;
  return rest && rest.toLowerCase() !== type.toLowerCase() ? `${prefixed}, ${rest}` : prefixed;
}
function joinStrayFindNumber(label, strayFindNumber) {
  if (!strayFindNumber.trim()) {
    return label;
  }
  const rest = splitStrayFindNumber(label).label;
  const prefixed = `${EXCABOOK_STRAY_FIND_PREFIX} ${strayFindNumber.trim()}`;
  return rest ? `${prefixed}, ${rest}` : prefixed;
}
var SUB_FIND_SLIP_SEPARATOR = "_";
var SUB_FIND_SLIP_PATTERN = /^(.*\S)\s*_\s*(\d+)$/;
function splitSubFindSlipNumber(value) {
  const trimmed = value.trim();
  const match = SUB_FIND_SLIP_PATTERN.exec(trimmed);
  if (!match) {
    return { findSlipNumber: trimmed, subFindSlipNumber: "" };
  }
  return { findSlipNumber: match[1].trim(), subFindSlipNumber: match[2] };
}
function formatFindSlipNumber(find) {
  const main = find.findSlipNumber.trim();
  const sub = (find.subFindSlipNumber ?? "").trim();
  return sub ? `${main}${SUB_FIND_SLIP_SEPARATOR}${sub}` : main;
}
var FIND_SCHEMA_VERSION = 9;
var findSchema = {
  version: FIND_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    planumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    profileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    findingIds: {
      type: "array",
      ref: "findings",
      items: { type: "string", maxLength: 36 }
    },
    findSlipNumber: { type: "string", maxLength: NUMBER_MAX },
    subFindSlipNumber: { type: "string", maxLength: NUMBER_MAX },
    strayFindNumber: { type: "string", maxLength: NUMBER_MAX },
    findBox: { type: "string", maxLength: NUMBER_MAX },
    area: { type: "string", maxLength: SPATIAL_MAX },
    trench: { type: "string", maxLength: SPATIAL_MAX },
    planum: { type: "string", maxLength: SPATIAL_MAX },
    profile: { type: "string", maxLength: SPATIAL_MAX },
    findGroup: { type: "string", maxLength: SHORT_VOCAB_MAX },
    sampleType: { type: "string", maxLength: VOCAB_MAX },
    label: { type: "string", maxLength: FIND_LABEL_MAX },
    materialDeterminability: { type: "string", maxLength: VOCAB_MAX },
    materialSubstanceType: { type: "string", maxLength: VOCAB_MAX },
    materialGroup: { type: "string", maxLength: VOCAB_MAX },
    materialName: { type: "string", maxLength: VOCAB_MAX },
    positionToPlanum: { type: "string", maxLength: POSITION_MAX },
    positionToProfile: { type: "string", maxLength: POSITION_MAX },
    findDescription: { type: "string" },
    preliminaryDating: { type: "string", maxLength: VOCAB_MAX },
    findTreatment: { type: "string", maxLength: SHORT_VOCAB_MAX },
    excavator: { type: "string", maxLength: VOCAB_MAX },
    excavatorId: { type: "string", maxLength: 36 },
    conservationRemark: { type: "string", maxLength: TEXT_FIELD_MAX3 },
    conservationTakeover: { type: "string", maxLength: YES_NO_MAX },
    macroPhotoLink: { type: "string", maxLength: TEXT_FIELD_MAX3 },
    remark: { type: "string" },
    internalInformation: { type: "string" },
    recordedAt: { type: "string", maxLength: RECORDED_AT_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "planumIds",
    "profileIds",
    "findingIds",
    "findSlipNumber",
    "subFindSlipNumber",
    "strayFindNumber",
    "findBox",
    "area",
    "trench",
    "planum",
    "profile",
    "findGroup",
    "sampleType",
    "label",
    "materialDeterminability",
    "materialSubstanceType",
    "materialGroup",
    "materialName",
    "positionToPlanum",
    "positionToProfile",
    "findDescription",
    "preliminaryDating",
    "findTreatment",
    "excavator",
    "excavatorId",
    "conservationRemark",
    "conservationTakeover",
    "macroPhotoLink",
    "remark",
    "internalInformation",
    "recordedAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId"]
};
var findInputSchema = z23.object({
  planumIds: z23.array(z23.string().max(ID_MAX)).default([]),
  profileIds: z23.array(z23.string().max(ID_MAX)).default([]),
  findingIds: z23.array(z23.string().max(ID_MAX)).default([]),
  findSlipNumber: z23.string().trim().min(1, rule("required")).max(NUMBER_MAX, rule("maxLength", { max: NUMBER_MAX })),
  subFindSlipNumber: z23.string().trim().max(NUMBER_MAX, rule("maxLength", { max: NUMBER_MAX })).regex(/^\d*$/, rule("digits")).default(""),
  strayFindNumber: z23.string().max(NUMBER_MAX).default(""),
  findBox: z23.string().max(NUMBER_MAX).default(""),
  area: z23.string().max(SPATIAL_MAX).default(""),
  trench: z23.string().max(SPATIAL_MAX).default(""),
  planum: z23.string().max(SPATIAL_MAX).default(""),
  profile: z23.string().max(SPATIAL_MAX).default(""),
  findGroup: z23.string().max(SHORT_VOCAB_MAX).default(""),
  sampleType: z23.string().max(VOCAB_MAX).default(""),
  label: z23.string().max(FIND_LABEL_MAX).default(""),
  materialDeterminability: z23.string().max(VOCAB_MAX).default(""),
  materialSubstanceType: z23.string().max(VOCAB_MAX).default(""),
  materialGroup: z23.string().max(VOCAB_MAX).default(""),
  materialName: z23.string().max(VOCAB_MAX).default(""),
  positionToPlanum: z23.string().max(POSITION_MAX).default(""),
  positionToProfile: z23.string().max(POSITION_MAX).default(""),
  findDescription: z23.string().max(DESCRIPTION_MAX).default(""),
  preliminaryDating: z23.string().max(VOCAB_MAX).default(""),
  findTreatment: z23.string().max(SHORT_VOCAB_MAX).default(""),
  excavator: z23.string().max(VOCAB_MAX).default(""),
  excavatorId: z23.string().max(ID_MAX).default(""),
  conservationRemark: z23.string().max(TEXT_FIELD_MAX3).default(""),
  conservationTakeover: z23.string().max(YES_NO_MAX).default(""),
  macroPhotoLink: z23.string().max(TEXT_FIELD_MAX3).default(""),
  remark: z23.string().max(DESCRIPTION_MAX).default(""),
  internalInformation: z23.string().max(DESCRIPTION_MAX).default(""),
  recordedAt: recordedAtField()
});
var findEntity = defineEntity({
  name: "find",
  apiBase: "/api/finds",
  collection: "finds",
  rxSchema: findSchema,
  migrations: FIND_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    // Per-description Befund refs (Planum/Profil + whole-Befund). Array fields
    // merge as a set-union/dedupe on sync conflict.
    planumIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    profileIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    findingIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    findSlipNumber: { maxLength: NUMBER_MAX, merge: true },
    subFindSlipNumber: { maxLength: NUMBER_MAX, merge: true },
    strayFindNumber: { maxLength: NUMBER_MAX, merge: true },
    findBox: { maxLength: NUMBER_MAX, merge: true },
    area: { maxLength: SPATIAL_MAX, merge: true },
    trench: { maxLength: SPATIAL_MAX, merge: true },
    planum: { maxLength: SPATIAL_MAX, merge: true },
    profile: { maxLength: SPATIAL_MAX, merge: true },
    findGroup: { maxLength: SHORT_VOCAB_MAX, merge: true },
    sampleType: { maxLength: VOCAB_MAX, merge: true },
    label: { maxLength: FIND_LABEL_MAX, merge: true },
    materialDeterminability: { maxLength: VOCAB_MAX, merge: true },
    materialSubstanceType: { maxLength: VOCAB_MAX, merge: true },
    materialGroup: { maxLength: VOCAB_MAX, merge: true },
    materialName: { maxLength: VOCAB_MAX, merge: true },
    positionToPlanum: { maxLength: POSITION_MAX, merge: true },
    positionToProfile: { maxLength: POSITION_MAX, merge: true },
    findDescription: {
      kind: "text",
      maxLength: DESCRIPTION_MAX,
      merge: true
    },
    preliminaryDating: { maxLength: VOCAB_MAX, merge: true },
    findTreatment: { maxLength: SHORT_VOCAB_MAX, merge: true },
    excavator: { maxLength: VOCAB_MAX, merge: true },
    excavatorId: { kind: "ref", maxLength: ID_MAX, merge: true },
    conservationRemark: { maxLength: TEXT_FIELD_MAX3, merge: true },
    conservationTakeover: { maxLength: YES_NO_MAX, merge: true },
    macroPhotoLink: { maxLength: TEXT_FIELD_MAX3, merge: true },
    remark: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    internalInformation: {
      kind: "text",
      maxLength: DESCRIPTION_MAX,
      merge: true
    },
    recordedAt: { kind: "datetime", merge: true }
  },
  inputSchema: findInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      planumIds: parseStringArray(row.planumIds),
      profileIds: parseStringArray(row.profileIds),
      findingIds: parseStringArray(row.findingIds),
      findSlipNumber: row.findSlipNumber || "",
      subFindSlipNumber: row.subFindSlipNumber || "",
      strayFindNumber: row.strayFindNumber || "",
      findBox: row.findBox || "",
      area: row.area || "",
      trench: row.trench || "",
      planum: row.planum || "",
      profile: row.profile || "",
      findGroup: row.findGroup || "",
      sampleType: row.sampleType || "",
      label: row.label || "",
      materialDeterminability: row.materialDeterminability || "",
      materialSubstanceType: row.materialSubstanceType || "",
      materialGroup: row.materialGroup || "",
      materialName: row.materialName || "",
      positionToPlanum: row.positionToPlanum || "",
      positionToProfile: row.positionToProfile || "",
      findDescription: row.findDescription || "",
      preliminaryDating: row.preliminaryDating || "",
      findTreatment: row.findTreatment || "",
      excavator: row.excavator || "",
      excavatorId: row.excavatorId || "",
      conservationRemark: row.conservationRemark || "",
      conservationTakeover: row.conservationTakeover || "",
      macroPhotoLink: row.macroPhotoLink || "",
      remark: row.remark || "",
      internalInformation: row.internalInformation || "",
      recordedAt: row.recordedAt || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      planumIds: JSON.stringify(doc.planumIds || []),
      profileIds: JSON.stringify(doc.profileIds || []),
      findingIds: JSON.stringify(doc.findingIds || []),
      findSlipNumber: doc.findSlipNumber || "",
      subFindSlipNumber: doc.subFindSlipNumber || "",
      strayFindNumber: doc.strayFindNumber || "",
      findBox: doc.findBox || "",
      area: doc.area || "",
      trench: doc.trench || "",
      planum: doc.planum || "",
      profile: doc.profile || "",
      findGroup: doc.findGroup || "",
      sampleType: doc.sampleType || "",
      label: doc.label || "",
      materialDeterminability: doc.materialDeterminability || "",
      materialSubstanceType: doc.materialSubstanceType || "",
      materialGroup: doc.materialGroup || "",
      materialName: doc.materialName || "",
      positionToPlanum: doc.positionToPlanum || "",
      positionToProfile: doc.positionToProfile || "",
      findDescription: doc.findDescription || "",
      preliminaryDating: doc.preliminaryDating || "",
      findTreatment: doc.findTreatment || "",
      excavator: doc.excavator || "",
      excavatorId: doc.excavatorId || "",
      conservationRemark: doc.conservationRemark || "",
      conservationTakeover: doc.conservationTakeover || "",
      macroPhotoLink: doc.macroPhotoLink || "",
      remark: doc.remark || "",
      internalInformation: doc.internalInformation || "",
      recordedAt: doc.recordedAt || ""
    };
  }
});

// src/entities/sample.ts
import { z as z24 } from "zod";
var SAMPLE_TYPE_OPTIONS = [
  "Bodenprobe",
  "Brandlehmprobe",
  "Holzkohleprobe",
  "Holzprobe",
  "M\xF6rtelprobe"
];
var SAMPLING_METHOD_OPTIONS = [
  "Blockbergung",
  "Kastenprobe",
  "Stechzylinder"
];
var SAMPLE_SCHEMA_VERSION = 2;
var sampleSchema = {
  version: SAMPLE_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    sampleType: { type: "string", maxLength: SHORT_TEXT_MAX },
    planumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    profileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    samplingReason: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    samplingMethod: { type: "string", maxLength: SHORT_TEXT_MAX },
    description: { type: "string" },
    sampler: { type: "string", maxLength: PERSON_MAX },
    samplerId: { type: "string", maxLength: 36 },
    findIds: {
      type: "array",
      ref: "finds",
      items: { type: "string", maxLength: 36 }
    },
    area: { type: "string", maxLength: SPATIAL_MAX },
    trench: { type: "string", maxLength: SPATIAL_MAX },
    planum: { type: "string", maxLength: SPATIAL_MAX },
    profile: { type: "string", maxLength: SPATIAL_MAX },
    recordedAt: { type: "string", maxLength: RECORDED_AT_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "sampleType",
    "planumIds",
    "profileIds",
    "samplingReason",
    "samplingMethod",
    "description",
    "sampler",
    "samplerId",
    "findIds",
    "area",
    "trench",
    "planum",
    "profile",
    "recordedAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId"]
};
var sampleInputSchema = z24.object({
  sampleType: z24.string().max(SHORT_TEXT_MAX).default(""),
  planumIds: z24.array(z24.string().max(ID_MAX)).default([]),
  profileIds: z24.array(z24.string().max(ID_MAX)).default([]),
  samplingReason: z24.string().max(MEDIUM_TEXT_MAX).default(""),
  samplingMethod: z24.string().max(SHORT_TEXT_MAX).default(""),
  description: z24.string().max(DESCRIPTION_MAX).default(""),
  sampler: z24.string().max(PERSON_MAX).default(""),
  samplerId: z24.string().max(ID_MAX).default(""),
  findIds: z24.array(z24.string().max(ID_MAX)).default([]),
  area: z24.string().max(SPATIAL_MAX).default(""),
  trench: z24.string().max(SPATIAL_MAX).default(""),
  planum: z24.string().max(SPATIAL_MAX).default(""),
  profile: z24.string().max(SPATIAL_MAX).default(""),
  recordedAt: recordedAtField()
});
var sampleEntity = defineEntity({
  name: "sample",
  apiBase: "/api/samples",
  collection: "samples",
  rxSchema: sampleSchema,
  migrations: SAMPLE_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    sampleType: { maxLength: SHORT_TEXT_MAX, merge: true },
    planumIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    profileIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    samplingReason: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    samplingMethod: { maxLength: SHORT_TEXT_MAX, merge: true },
    description: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    sampler: { maxLength: PERSON_MAX, merge: true },
    samplerId: { kind: "ref", maxLength: ID_MAX, merge: true },
    // Two offices converting the same sample at once both keep their Fund.
    findIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    area: { maxLength: SPATIAL_MAX, merge: true },
    trench: { maxLength: SPATIAL_MAX, merge: true },
    planum: { maxLength: SPATIAL_MAX, merge: true },
    profile: { maxLength: SPATIAL_MAX, merge: true },
    recordedAt: { kind: "datetime", merge: true }
  },
  inputSchema: sampleInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      sampleType: row.sampleType || "",
      planumIds: parseStringArray(row.planumIds),
      profileIds: parseStringArray(row.profileIds),
      samplingReason: row.samplingReason || "",
      samplingMethod: row.samplingMethod || "",
      description: row.description || "",
      sampler: row.sampler || "",
      samplerId: row.samplerId || "",
      findIds: parseStringArray(row.findIds),
      area: row.area || "",
      trench: row.trench || "",
      planum: row.planum || "",
      profile: row.profile || "",
      recordedAt: row.recordedAt || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      sampleType: doc.sampleType || "",
      // Ref arrays persist as JSON-string TEXT columns, like the Fund's.
      planumIds: JSON.stringify(doc.planumIds || []),
      profileIds: JSON.stringify(doc.profileIds || []),
      samplingReason: doc.samplingReason || "",
      samplingMethod: doc.samplingMethod || "",
      description: doc.description || "",
      sampler: doc.sampler || "",
      samplerId: doc.samplerId || "",
      findIds: JSON.stringify(doc.findIds || []),
      area: doc.area || "",
      trench: doc.trench || "",
      planum: doc.planum || "",
      profile: doc.profile || "",
      recordedAt: doc.recordedAt || ""
    };
  }
});

// src/entities/overallDescription.ts
import { z as z25 } from "zod";
var DESIGNATION_MAX = 255;
var OVERALL_DESCRIPTION_SCHEMA_VERSION = 5;
var overallDescriptionSchema = {
  version: OVERALL_DESCRIPTION_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    overallDescriptionNumber: { type: "string", maxLength: NUMBER_MAX },
    editor: { type: "string", maxLength: PERSON_MAX },
    editorId: { type: "string", maxLength: 36 },
    designation: { type: "string", maxLength: DESIGNATION_MAX },
    description: { type: "string" },
    remark: { type: "string" },
    area: { type: "string", maxLength: SPATIAL_MAX },
    trench: { type: "string", maxLength: SPATIAL_MAX },
    planum: { type: "string", maxLength: SPATIAL_MAX },
    profile: { type: "string", maxLength: SPATIAL_MAX },
    // The specific descriptions this covers (ref -> the sub-entities), plus
    // whole Befunde with no description yet (ref -> findings).
    planumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    profileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    findingIds: {
      type: "array",
      ref: "findings",
      items: { type: "string", maxLength: 36 }
    },
    recordedAt: { type: "string", maxLength: RECORDED_AT_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "overallDescriptionNumber",
    "editor",
    "editorId",
    "designation",
    "description",
    "remark",
    "area",
    "trench",
    "planum",
    "profile",
    "planumIds",
    "profileIds",
    "findingIds",
    "recordedAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId"]
};
var overallDescriptionInputSchema = z25.object({
  overallDescriptionNumber: z25.string().trim().min(1, rule("required")).max(NUMBER_MAX, rule("maxLength", { max: NUMBER_MAX })),
  editor: z25.string().max(PERSON_MAX).default(""),
  editorId: z25.string().max(ID_MAX).default(""),
  designation: z25.string().max(DESIGNATION_MAX).default(""),
  description: z25.string().max(DESCRIPTION_MAX).default(""),
  remark: z25.string().max(DESCRIPTION_MAX).default(""),
  area: z25.string().max(SPATIAL_MAX).default(""),
  trench: z25.string().max(SPATIAL_MAX).default(""),
  planum: z25.string().max(SPATIAL_MAX).default(""),
  profile: z25.string().max(SPATIAL_MAX).default(""),
  planumIds: z25.array(z25.string().max(ID_MAX)).default([]),
  profileIds: z25.array(z25.string().max(ID_MAX)).default([]),
  findingIds: z25.array(z25.string().max(ID_MAX)).default([]),
  recordedAt: recordedAtField()
});
var overallDescriptionEntity = defineEntity(
  {
    name: "overallDescription",
    apiBase: "/api/overall-descriptions",
    collection: "overall_descriptions",
    rxSchema: overallDescriptionSchema,
    migrations: OVERALL_DESCRIPTION_MIGRATIONS,
    historyAllFields: true,
    fields: {
      projectId: { kind: "ref", maxLength: ID_MAX },
      overallDescriptionNumber: { maxLength: NUMBER_MAX, merge: true },
      editor: { maxLength: PERSON_MAX, merge: true },
      editorId: { kind: "ref", maxLength: ID_MAX, merge: true },
      designation: { maxLength: DESIGNATION_MAX, merge: true },
      description: {
        kind: "text",
        maxLength: DESCRIPTION_MAX,
        merge: true
      },
      remark: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
      area: { maxLength: SPATIAL_MAX, merge: true },
      trench: { maxLength: SPATIAL_MAX, merge: true },
      planum: { maxLength: SPATIAL_MAX, merge: true },
      profile: { maxLength: SPATIAL_MAX, merge: true },
      // Covered-description / Befund id sets — string-array merge
      // (set-union/dedupe).
      planumIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
      profileIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
      findingIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
      recordedAt: { kind: "datetime", merge: true }
    },
    inputSchema: overallDescriptionInputSchema,
    toWire(row) {
      return {
        id: row.id,
        projectId: row.projectId || "",
        overallDescriptionNumber: row.overallDescriptionNumber || "",
        editor: row.editor || "",
        editorId: row.editorId || "",
        designation: row.designation || "",
        description: row.description || "",
        remark: row.remark || "",
        area: row.area || "",
        trench: row.trench || "",
        planum: row.planum || "",
        profile: row.profile || "",
        planumIds: parseStringArray(row.planumIds),
        profileIds: parseStringArray(row.profileIds),
        findingIds: parseStringArray(row.findingIds),
        recordedAt: row.recordedAt || "",
        createdBy: row.createdBy || "",
        updatedBy: row.updatedBy || "",
        updatedAt: row.updatedAt.toISOString(),
        createdAt: row.createdAt.toISOString(),
        _deleted: Boolean(row.deleted)
      };
    },
    wireToContent(doc) {
      return {
        projectId: doc.projectId || "",
        overallDescriptionNumber: doc.overallDescriptionNumber || "",
        editor: doc.editor || "",
        editorId: doc.editorId || "",
        designation: doc.designation || "",
        description: doc.description || "",
        remark: doc.remark || "",
        area: doc.area || "",
        trench: doc.trench || "",
        planum: doc.planum || "",
        profile: doc.profile || "",
        // The covered-description / Befund id sets persist as JSON-string
        // TEXT columns.
        planumIds: JSON.stringify(doc.planumIds || []),
        profileIds: JSON.stringify(doc.profileIds || []),
        findingIds: JSON.stringify(doc.findingIds || []),
        recordedAt: doc.recordedAt || ""
      };
    }
  }
);

// src/entities/areaDescription.ts
import { z as z26 } from "zod";
var AREA_DESCRIPTION_SCHEMA_VERSION = 1;
var areaDescriptionSchema = {
  version: AREA_DESCRIPTION_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    parcelIds: {
      type: "array",
      ref: "parcels",
      items: { type: "string", maxLength: 36 }
    },
    area: { type: "string", maxLength: SPATIAL_MAX },
    planum: { type: "string", maxLength: SPATIAL_MAX },
    sondage: { type: "string", maxLength: SPATIAL_MAX },
    length: { type: "string", maxLength: MEASURE_MAX },
    width: { type: "string", maxLength: MEASURE_MAX },
    conditionAtRecording: { type: "string" },
    naturalSoil: { type: "string" },
    findingsOverview: { type: "string" },
    editor: { type: "string", maxLength: PERSON_MAX },
    editorId: { type: "string", maxLength: 36 },
    recordedAt: { type: "string", maxLength: RECORDED_AT_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "parcelIds",
    "area",
    "planum",
    "sondage",
    "length",
    "width",
    "conditionAtRecording",
    "naturalSoil",
    "findingsOverview",
    "editor",
    "editorId",
    "recordedAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId"]
};
var METRES_PATTERN = /^\d+([.,]\d{1,2})?$/;
function metresField() {
  return z26.string().trim().max(MEASURE_MAX).refine(
    (value) => value === "" || METRES_PATTERN.test(value),
    rule("twoDecimals")
  ).default("");
}
var areaDescriptionInputSchema = z26.object({
  parcelIds: z26.array(z26.string().max(ID_MAX)).default([]),
  area: z26.string().trim().min(1, rule("required")).max(SPATIAL_MAX, rule("maxLength", { max: SPATIAL_MAX })),
  planum: z26.string().trim().min(1, rule("required")).max(SPATIAL_MAX, rule("maxLength", { max: SPATIAL_MAX })),
  sondage: z26.string().trim().max(SPATIAL_MAX, rule("maxLength", { max: SPATIAL_MAX })).default(""),
  length: metresField(),
  width: metresField(),
  conditionAtRecording: z26.string().max(DESCRIPTION_MAX).default(""),
  naturalSoil: z26.string().max(DESCRIPTION_MAX).default(""),
  findingsOverview: z26.string().max(DESCRIPTION_MAX).default(""),
  editor: z26.string().max(PERSON_MAX).default(""),
  editorId: z26.string().max(ID_MAX).default(""),
  recordedAt: recordedAtField()
});
var areaDescriptionEntity = defineEntity({
  name: "areaDescription",
  apiBase: "/api/area-descriptions",
  collection: "area_descriptions",
  rxSchema: areaDescriptionSchema,
  migrations: AREA_DESCRIPTION_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    parcelIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    area: { maxLength: SPATIAL_MAX, merge: true },
    planum: { maxLength: SPATIAL_MAX, merge: true },
    sondage: { maxLength: SPATIAL_MAX, merge: true },
    length: { maxLength: MEASURE_MAX, merge: true },
    width: { maxLength: MEASURE_MAX, merge: true },
    conditionAtRecording: {
      kind: "text",
      maxLength: DESCRIPTION_MAX,
      merge: true
    },
    naturalSoil: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    findingsOverview: {
      kind: "text",
      maxLength: DESCRIPTION_MAX,
      merge: true
    },
    editor: { maxLength: PERSON_MAX, merge: true },
    editorId: { kind: "ref", maxLength: ID_MAX, merge: true },
    recordedAt: { kind: "datetime", merge: true }
  },
  inputSchema: areaDescriptionInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      parcelIds: parseStringArray(row.parcelIds),
      area: row.area || "",
      planum: row.planum || "",
      sondage: row.sondage || "",
      length: row.length || "",
      width: row.width || "",
      conditionAtRecording: row.conditionAtRecording || "",
      naturalSoil: row.naturalSoil || "",
      findingsOverview: row.findingsOverview || "",
      editor: row.editor || "",
      editorId: row.editorId || "",
      recordedAt: row.recordedAt || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      // Persisted as a JSON-string TEXT column, like the Tagebuch's.
      parcelIds: JSON.stringify(doc.parcelIds || []),
      area: doc.area || "",
      planum: doc.planum || "",
      sondage: doc.sondage || "",
      length: doc.length || "",
      width: doc.width || "",
      conditionAtRecording: doc.conditionAtRecording || "",
      naturalSoil: doc.naturalSoil || "",
      findingsOverview: doc.findingsOverview || "",
      editor: doc.editor || "",
      editorId: doc.editorId || "",
      recordedAt: doc.recordedAt || ""
    };
  }
});

// src/entities/diary.ts
import { z as z27 } from "zod";
var TIME_MAX2 = 20;
var WEATHER_MAX = 50;
var WEATHER_CONDITION_MAX = 255;
var COUNT_MAX = 20;
var NUMBER_MAX2 = 20;
var VISITOR_NAME_MAX = DESCRIPTION_MAX;
var VISITOR_POSITION_MAX = 150;
var VISITOR_NOTE_MAX = DESCRIPTION_MAX;
var MAX_VISITORS = 100;
var VISITORS_TOTAL_MAX = 24e3;
function clampVisitors(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  const out = [];
  let budget = VISITORS_TOTAL_MAX;
  for (const item of value) {
    if (out.length >= MAX_VISITORS || budget <= 0) {
      break;
    }
    const obj = item ?? {};
    const position = clampString(obj.position, VISITOR_POSITION_MAX);
    const name = clampString(
      obj.name,
      Math.min(VISITOR_NAME_MAX, Math.max(budget - position.length, 0))
    );
    const note = clampString(
      obj.note,
      Math.min(
        VISITOR_NOTE_MAX,
        Math.max(budget - position.length - name.length, 0)
      )
    );
    if (name === "" && position === "" && note === "") {
      continue;
    }
    budget -= name.length + position.length + note.length;
    out.push({ name, position, note });
  }
  return out;
}
var MAX_SONDAGE_PHOTOS = 100;
function clampSondagePhotos(value) {
  const out = [];
  for (const item of parseJsonArray(value)) {
    if (out.length >= MAX_SONDAGE_PHOTOS) {
      break;
    }
    const obj = item ?? {};
    const sondage = clampString(obj.sondage, NUMBER_MAX2);
    const partialPhoto = clampString(obj.partialPhoto, NUMBER_MAX2);
    if (sondage === "" && partialPhoto === "") {
      continue;
    }
    out.push({ sondage, partialPhoto });
  }
  return out;
}
function parseJsonArray(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}
var WEATHER_CONDITION_OPTIONS = [
  "Sonnig",
  "Heiter",
  "Leicht bew\xF6lkt",
  "Bew\xF6lkt",
  "Bedeckt",
  "Wechselhaft",
  "Neblig",
  "Leichter Regen",
  "Regen",
  "Starkregen",
  "Dauerregen",
  "Leichter Wind",
  "Windig",
  "Sturm",
  "Frost",
  "Leichter Schneefall",
  "Schneefall",
  "Glatteis",
  "Gewitter"
];
var DIARY_SCHEMA_VERSION = 12;
var TIME_PATTERN2 = /^([01]\d|2[0-3]):[0-5]\d$/;
function timeToMinutes(value) {
  if (!TIME_PATTERN2.test(value)) {
    return null;
  }
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}
function breakToMinutes(value) {
  const num = Math.trunc(Number(value));
  return Number.isFinite(num) && num > 0 ? num : 0;
}
var diarySchema = {
  version: DIARY_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    author: { type: "string", maxLength: PERSON_MAX },
    authorId: { type: "string", maxLength: 36 },
    excavationLeader: { type: "string", maxLength: PERSON_MAX },
    excavationLeaderId: { type: "string", maxLength: 36 },
    technician: { type: "string" },
    technicianIds: {
      type: "array",
      items: { type: "string", maxLength: 36 }
    },
    staff: { type: "string" },
    staffIds: { type: "array", items: { type: "string", maxLength: 36 } },
    workStart: { type: "string", maxLength: TIME_MAX2 },
    workEnd: { type: "string", maxLength: TIME_MAX2 },
    breakTime: { type: "string", maxLength: TIME_MAX2 },
    weatherCondition: { type: "string", maxLength: WEATHER_CONDITION_MAX },
    weatherTemperature: { type: "string", maxLength: WEATHER_MAX },
    weatherExtra: { type: "string", maxLength: MEDIUM_TEXT_MAX },
    topsoilRemoval: { type: "string" },
    surveying: { type: "string" },
    cleaning: { type: "string" },
    cuttingFindings: { type: "string" },
    drawing: { type: "string" },
    descriptions: { type: "string" },
    recovery: { type: "string" },
    finds: { type: "string" },
    photos: { type: "string" },
    excavatorType: { type: "string", maxLength: WEATHER_MAX },
    excavatorCount: { type: "string", maxLength: COUNT_MAX },
    excavatorStart: { type: "string", maxLength: TIME_MAX2 },
    excavatorEnd: { type: "string", maxLength: TIME_MAX2 },
    excavatorBreak: { type: "string", maxLength: TIME_MAX2 },
    excavatorNote: { type: "string" },
    dailyCourse: { type: "string" },
    // The last numbers handed out up to this day — carried on the day so the
    // printed Tagebuch stays true to what was known then.
    lastFindingNumber: { type: "string", maxLength: NUMBER_MAX2 },
    lastProfileNumber: { type: "string", maxLength: NUMBER_MAX2 },
    lastStrayFindNumber: { type: "string", maxLength: NUMBER_MAX2 },
    lastGeoprofileNumber: { type: "string", maxLength: NUMBER_MAX2 },
    lastSondageNumber: { type: "string", maxLength: NUMBER_MAX2 },
    lastPartialPhotoNumber: { type: "string", maxLength: NUMBER_MAX2 },
    // Embedded Sondage/Teilfoto list — the pairs the two numbers above are
    // the highest of.
    sondagePhotos: {
      type: "array",
      items: {
        type: "object",
        properties: {
          sondage: { type: "string", maxLength: NUMBER_MAX2 },
          partialPhoto: { type: "string", maxLength: NUMBER_MAX2 }
        },
        required: ["sondage", "partialPhoto"]
      }
    },
    // Embedded visitor list (was the diaryVisitor collection).
    visitors: {
      type: "array",
      items: {
        type: "object",
        properties: {
          name: { type: "string", maxLength: VISITOR_NAME_MAX },
          position: {
            type: "string",
            maxLength: VISITOR_POSITION_MAX
          },
          note: { type: "string", maxLength: VISITOR_NOTE_MAX }
        },
        required: ["name", "position", "note"]
      }
    },
    // Parcel-id references for the day (was the diaryParcel join).
    parcelIds: {
      type: "array",
      ref: "parcels",
      items: { type: "string", maxLength: 36 }
    },
    recordedAt: { type: "string", maxLength: RECORDED_AT_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "author",
    "authorId",
    "excavationLeader",
    "excavationLeaderId",
    "technician",
    "technicianIds",
    "staff",
    "staffIds",
    "workStart",
    "workEnd",
    "breakTime",
    "weatherCondition",
    "weatherTemperature",
    "weatherExtra",
    "topsoilRemoval",
    "surveying",
    "cleaning",
    "cuttingFindings",
    "drawing",
    "descriptions",
    "recovery",
    "finds",
    "photos",
    "excavatorType",
    "excavatorCount",
    "excavatorStart",
    "excavatorEnd",
    "excavatorBreak",
    "excavatorNote",
    "dailyCourse",
    "lastFindingNumber",
    "lastProfileNumber",
    "lastStrayFindNumber",
    "lastGeoprofileNumber",
    "lastSondageNumber",
    "lastPartialPhotoNumber",
    "sondagePhotos",
    "visitors",
    "parcelIds",
    "recordedAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId"]
};
var diaryInputSchema = z27.object({
  author: z27.string().max(PERSON_MAX).default(""),
  authorId: z27.string().max(ID_MAX).default(""),
  excavationLeader: z27.string().max(PERSON_MAX).default(""),
  excavationLeaderId: z27.string().max(ID_MAX).default(""),
  technician: z27.string().max(DESCRIPTION_MAX).default(""),
  technicianIds: z27.array(z27.string().max(ID_MAX)).default([]),
  staff: z27.string().max(DESCRIPTION_MAX).default(""),
  staffIds: z27.array(z27.string().max(ID_MAX)).default([]),
  /**
   * Arbeitszeit (mirrors Zeiterfassung): work times are "HH:mm" and the
   * break is a whole number of minutes; the cross-field rules below apply
   * when both times are set. "Not in the future" is enforced by the form's
   * date/time pickers.
   */
  workStart: z27.string().max(TIME_MAX2).refine(
    (value) => value === "" || TIME_PATTERN2.test(value),
    rule("timeFormat")
  ).default(""),
  workEnd: z27.string().max(TIME_MAX2).refine(
    (value) => value === "" || TIME_PATTERN2.test(value),
    rule("timeFormat")
  ).default(""),
  breakTime: z27.string().max(TIME_MAX2).refine(
    (value) => value === "" || /^\d+$/.test(value.trim()),
    rule("integer")
  ).default(""),
  weatherCondition: z27.string().max(WEATHER_CONDITION_MAX).default(""),
  weatherTemperature: z27.string().max(WEATHER_MAX).default(""),
  weatherExtra: z27.string().max(MEDIUM_TEXT_MAX).default(""),
  topsoilRemoval: z27.string().max(DESCRIPTION_MAX).default(""),
  surveying: z27.string().max(DESCRIPTION_MAX).default(""),
  cleaning: z27.string().max(DESCRIPTION_MAX).default(""),
  cuttingFindings: z27.string().max(DESCRIPTION_MAX).default(""),
  drawing: z27.string().max(DESCRIPTION_MAX).default(""),
  descriptions: z27.string().max(DESCRIPTION_MAX).default(""),
  recovery: z27.string().max(DESCRIPTION_MAX).default(""),
  finds: z27.string().max(DESCRIPTION_MAX).default(""),
  photos: z27.string().max(DESCRIPTION_MAX).default(""),
  excavatorType: z27.string().max(WEATHER_MAX).default(""),
  excavatorCount: z27.string().max(COUNT_MAX).default(""),
  excavatorStart: z27.string().max(TIME_MAX2).default(""),
  excavatorEnd: z27.string().max(TIME_MAX2).default(""),
  excavatorBreak: z27.string().max(TIME_MAX2).default(""),
  excavatorNote: z27.string().max(DESCRIPTION_MAX).default(""),
  dailyCourse: z27.string().max(DESCRIPTION_MAX).default(""),
  lastFindingNumber: z27.string().max(NUMBER_MAX2).default(""),
  lastProfileNumber: z27.string().max(NUMBER_MAX2).default(""),
  lastStrayFindNumber: z27.string().max(NUMBER_MAX2).default(""),
  lastGeoprofileNumber: z27.string().max(NUMBER_MAX2).default(""),
  lastSondageNumber: z27.string().max(NUMBER_MAX2).default(""),
  lastPartialPhotoNumber: z27.string().max(NUMBER_MAX2).default(""),
  sondagePhotos: z27.array(
    z27.object({
      sondage: z27.string().max(NUMBER_MAX2).default(""),
      partialPhoto: z27.string().max(NUMBER_MAX2).default("")
    })
  ).default([]),
  visitors: z27.array(
    z27.object({
      name: z27.string().max(VISITOR_NAME_MAX).default(""),
      position: z27.string().max(VISITOR_POSITION_MAX).default(""),
      note: z27.string().max(VISITOR_NOTE_MAX).default("")
    })
  ).default([]),
  parcelIds: z27.array(z27.string().max(ID_MAX)).default([]),
  /**
   * REQUIRED, like the Befundbeschreibungen': a Tagebuch is the record of
   * one DAY — it is what the entry is filed, sorted, exported and checked
   * for duplicates by (see findDiaryOnDay). A day belonging to no date
   * sorts to the end of every list and matches no duplicate check.
   */
  recordedAt: recordedAtField({ required: true })
}).refine(
  (diary) => {
    const start = timeToMinutes(diary.workStart);
    const end = timeToMinutes(diary.workEnd);
    return start === null || end === null || end > start;
  },
  { message: rule("endBeforeStart"), path: ["workEnd"] }
).refine(
  (diary) => {
    const start = timeToMinutes(diary.workStart);
    const end = timeToMinutes(diary.workEnd);
    if (start === null || end === null || end <= start) {
      return true;
    }
    return breakToMinutes(diary.breakTime) < end - start;
  },
  { message: rule("pauseExceedsWork"), path: ["breakTime"] }
);
var diaryEntity = defineEntity({
  name: "diary",
  apiBase: "/api/diaries",
  collection: "diaries",
  rxSchema: diarySchema,
  migrations: DIARY_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    author: { maxLength: PERSON_MAX, merge: true },
    authorId: { kind: "ref", maxLength: ID_MAX, merge: true },
    excavationLeader: { maxLength: PERSON_MAX, merge: true },
    excavationLeaderId: { kind: "ref", maxLength: ID_MAX, merge: true },
    technician: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    // 'list', not 'array': positional to the ', '-split `technician`.
    technicianIds: { kind: "list", itemMaxLength: ID_MAX, merge: true },
    staff: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    // 'list', not 'array': positional to the ', '-split `staff`.
    staffIds: { kind: "list", itemMaxLength: ID_MAX, merge: true },
    workStart: { maxLength: TIME_MAX2, merge: true },
    workEnd: { maxLength: TIME_MAX2, merge: true },
    breakTime: { maxLength: TIME_MAX2, merge: true },
    weatherCondition: { maxLength: WEATHER_CONDITION_MAX, merge: true },
    weatherTemperature: { maxLength: WEATHER_MAX, merge: true },
    weatherExtra: { maxLength: MEDIUM_TEXT_MAX, merge: true },
    topsoilRemoval: {
      kind: "text",
      maxLength: DESCRIPTION_MAX,
      merge: true
    },
    surveying: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    cleaning: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    cuttingFindings: {
      kind: "text",
      maxLength: DESCRIPTION_MAX,
      merge: true
    },
    drawing: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    descriptions: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    recovery: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    finds: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    photos: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    excavatorType: { maxLength: WEATHER_MAX, merge: true },
    excavatorCount: { maxLength: COUNT_MAX, merge: true },
    excavatorStart: { maxLength: TIME_MAX2, merge: true },
    excavatorEnd: { maxLength: TIME_MAX2, merge: true },
    excavatorBreak: { maxLength: TIME_MAX2, merge: true },
    excavatorNote: {
      kind: "text",
      maxLength: DESCRIPTION_MAX,
      merge: true
    },
    dailyCourse: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    lastFindingNumber: { maxLength: NUMBER_MAX2, merge: true },
    lastProfileNumber: { maxLength: NUMBER_MAX2, merge: true },
    lastStrayFindNumber: { maxLength: NUMBER_MAX2, merge: true },
    lastGeoprofileNumber: { maxLength: NUMBER_MAX2, merge: true },
    lastSondageNumber: { maxLength: NUMBER_MAX2, merge: true },
    lastPartialPhotoNumber: { maxLength: NUMBER_MAX2, merge: true },
    // Embedded object arrays — clamped via sanitizeValue (no built-in kind
    // expresses them); whole-array last-write-wins on conflict.
    sondagePhotos: { sanitizeValue: clampSondagePhotos },
    visitors: { sanitizeValue: clampVisitors },
    // Parcel-id reference set — string-array merge (set-union/dedupe).
    parcelIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    recordedAt: { kind: "datetime", merge: true }
  },
  inputSchema: diaryInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      author: row.author || "",
      authorId: row.authorId || "",
      excavationLeader: row.excavationLeader || "",
      excavationLeaderId: row.excavationLeaderId || "",
      technician: row.technician || "",
      technicianIds: parseStringArray(row.technicianIds),
      staff: row.staff || "",
      staffIds: parseStringArray(row.staffIds),
      workStart: row.workStart || "",
      workEnd: row.workEnd || "",
      breakTime: row.breakTime || "",
      weatherCondition: row.weatherCondition || "",
      weatherTemperature: row.weatherTemperature || "",
      weatherExtra: row.weatherExtra || "",
      topsoilRemoval: row.topsoilRemoval || "",
      surveying: row.surveying || "",
      cleaning: row.cleaning || "",
      cuttingFindings: row.cuttingFindings || "",
      drawing: row.drawing || "",
      descriptions: row.descriptions || "",
      recovery: row.recovery || "",
      finds: row.finds || "",
      photos: row.photos || "",
      excavatorType: row.excavatorType || "",
      excavatorCount: row.excavatorCount || "",
      excavatorStart: row.excavatorStart || "",
      excavatorEnd: row.excavatorEnd || "",
      excavatorBreak: row.excavatorBreak || "",
      excavatorNote: row.excavatorNote || "",
      dailyCourse: row.dailyCourse || "",
      lastFindingNumber: row.lastFindingNumber || "",
      lastProfileNumber: row.lastProfileNumber || "",
      lastStrayFindNumber: row.lastStrayFindNumber || "",
      lastGeoprofileNumber: row.lastGeoprofileNumber || "",
      lastSondageNumber: row.lastSondageNumber || "",
      lastPartialPhotoNumber: row.lastPartialPhotoNumber || "",
      sondagePhotos: clampSondagePhotos(row.sondagePhotos),
      visitors: clampVisitors(parseJsonArray(row.visitors)),
      parcelIds: parseStringArray(row.parcelIds),
      recordedAt: row.recordedAt || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      author: doc.author || "",
      authorId: doc.authorId || "",
      excavationLeader: doc.excavationLeader || "",
      excavationLeaderId: doc.excavationLeaderId || "",
      technician: doc.technician || "",
      technicianIds: JSON.stringify(doc.technicianIds || []),
      staff: doc.staff || "",
      staffIds: JSON.stringify(doc.staffIds || []),
      workStart: doc.workStart || "",
      workEnd: doc.workEnd || "",
      breakTime: doc.breakTime || "",
      weatherCondition: doc.weatherCondition || "",
      weatherTemperature: doc.weatherTemperature || "",
      weatherExtra: doc.weatherExtra || "",
      topsoilRemoval: doc.topsoilRemoval || "",
      surveying: doc.surveying || "",
      cleaning: doc.cleaning || "",
      cuttingFindings: doc.cuttingFindings || "",
      drawing: doc.drawing || "",
      descriptions: doc.descriptions || "",
      recovery: doc.recovery || "",
      finds: doc.finds || "",
      photos: doc.photos || "",
      excavatorType: doc.excavatorType || "",
      excavatorCount: doc.excavatorCount || "",
      excavatorStart: doc.excavatorStart || "",
      excavatorEnd: doc.excavatorEnd || "",
      excavatorBreak: doc.excavatorBreak || "",
      excavatorNote: doc.excavatorNote || "",
      dailyCourse: doc.dailyCourse || "",
      lastFindingNumber: doc.lastFindingNumber || "",
      lastProfileNumber: doc.lastProfileNumber || "",
      lastStrayFindNumber: doc.lastStrayFindNumber || "",
      lastGeoprofileNumber: doc.lastGeoprofileNumber || "",
      lastSondageNumber: doc.lastSondageNumber || "",
      lastPartialPhotoNumber: doc.lastPartialPhotoNumber || "",
      // Embedded arrays persist as JSON-string TEXT columns.
      sondagePhotos: JSON.stringify(
        clampSondagePhotos(doc.sondagePhotos)
      ),
      visitors: JSON.stringify(clampVisitors(doc.visitors)),
      parcelIds: JSON.stringify(doc.parcelIds || []),
      recordedAt: doc.recordedAt || ""
    };
  }
});

// src/entities/timeEntry.ts
import { z as z28 } from "zod";
var WORKER_NAME_MAX2 = 150;
var ENTRY_TYPE_MAX = 20;
var TIME_MAX3 = 5;
var PAUSE_MINUTES_MAX2 = 1440;
var QUANTITY_MAX2 = 9999;
var MAX_YEAR = 9999;
var MAX_WEEK = 53;
var TIME_PATTERN3 = /^([01]\d|2[0-3]):[0-5]\d$/;
function timeToMinutes2(value) {
  if (!TIME_PATTERN3.test(value)) {
    return null;
  }
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}
function clampTime2(value) {
  const str = clampString(value, TIME_MAX3);
  return TIME_PATTERN3.test(str) ? str : "";
}
function clampYear(value) {
  const num = Math.trunc(Number(value));
  return Number.isFinite(num) && num > 0 ? Math.min(num, MAX_YEAR) : 0;
}
function clampWeek(value) {
  const num = Math.trunc(Number(value));
  return Number.isFinite(num) ? Math.max(0, Math.min(MAX_WEEK, num)) : 0;
}
var TIME_ENTRY_TYPES = ["worker", "equipment"];
var DEFAULT_TIME_ENTRY_TYPE = "worker";
function clampTimeEntryType(value) {
  const str = clampString(value, ENTRY_TYPE_MAX);
  return TIME_ENTRY_TYPES.includes(str) ? str : DEFAULT_TIME_ENTRY_TYPE;
}
function clampPauseMinutes(value) {
  const num = Math.trunc(Number(value));
  return Number.isFinite(num) ? Math.max(0, Math.min(PAUSE_MINUTES_MAX2, num)) : 0;
}
function clampQuantity(value) {
  const num = Math.trunc(Number(value));
  return Number.isFinite(num) ? Math.max(1, Math.min(QUANTITY_MAX2, num)) : 1;
}
function timeEntryTimesRequired(entryType) {
  return clampTimeEntryType(entryType) === "worker";
}
function dateToWire2(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString().slice(0, 10);
  }
  if (typeof value === "string") {
    return value.slice(0, 10);
  }
  return "";
}
function timeToWire(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return "";
    }
    const hh = String(value.getUTCHours()).padStart(2, "0");
    const mm = String(value.getUTCMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }
  if (typeof value === "string") {
    return clampTime2(value);
  }
  return "";
}
function dateToContent2(value) {
  const day = clampString(value, 10);
  return DAY_PATTERN.test(day) ? /* @__PURE__ */ new Date(`${day}T00:00:00Z`) : null;
}
function timeToContent(value) {
  const time = clampTime2(value);
  return time ? /* @__PURE__ */ new Date(`1970-01-01T${time}:00Z`) : null;
}
var TIME_ENTRY_SCHEMA_VERSION = 2;
var timeEntrySchema = {
  version: TIME_ENTRY_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    entryType: { type: "string", maxLength: ENTRY_TYPE_MAX },
    year: { type: "number" },
    week: { type: "number" },
    date: { type: "string", maxLength: 10 },
    workerName: { type: "string", maxLength: WORKER_NAME_MAX2 },
    quantity: { type: "number" },
    userId: { type: "string", maxLength: 36 },
    startTime: { type: "string", maxLength: TIME_MAX3 },
    endTime: { type: "string", maxLength: TIME_MAX3 },
    pauseMinutes: { type: "number" },
    note: { type: "string" },
    phaseNumber: { type: "string", maxLength: PHASE_NUMBER_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "entryType",
    "year",
    "week",
    "date",
    "workerName",
    "quantity",
    "userId",
    "startTime",
    "endTime",
    "pauseMinutes",
    "note",
    "phaseNumber",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  /**
   * `entryType` leads its compound index: every view queries one variant, the
   * office ones by type alone (the index prefix), the field ones by type +
   * project + day.
   */
  indexes: [["updatedAt", "id"], "projectId", ["entryType", "projectId"]]
};
var timeEntryInputSchema = z28.object({
  // Which Zeiterfassung the row belongs to. Not user-editable — the view
  // that creates the row fixes it — so it just defaults when omitted.
  entryType: z28.enum(TIME_ENTRY_TYPES).default(DEFAULT_TIME_ENTRY_TYPE),
  year: z28.number().int().min(0).max(MAX_YEAR, rule("maxValue", { max: MAX_YEAR })).default(0),
  week: z28.number().int().min(0).max(MAX_WEEK, rule("maxValue", { max: MAX_WEEK })).default(0),
  date: z28.string().trim().refine(
    (value) => value === "" || DAY_PATTERN.test(value),
    rule("dateFormat")
  ).refine(hasPlausibleYearIfDay, rule("implausibleYear")).default(""),
  /**
   * A time entry is only valid once filled in: a name (the worker, or the
   * Gerät on an equipment row) is always required, the times only on a
   * worker row — see timeEntryTimesRequired and the superRefine below.
   * Whatever IS filled in must be a well-formed HH:mm. Pause stays
   * optional (0 = no break, and always 0 on equipment — machines take
   * none). Mirrors the field grid's isRowComplete.
   */
  workerName: z28.string().trim().min(1, rule("required")).max(WORKER_NAME_MAX2, rule("maxLength", { max: WORKER_NAME_MAX2 })),
  /**
   * How many of them the row is about. Not offered on a worker row (a
   * person is one), so it defaults to the 1 that keeps the arithmetic
   * below unchanged for them.
   */
  quantity: z28.number().int().min(1, rule("minValue", { min: 1 })).max(QUANTITY_MAX2, rule("maxValue", { max: QUANTITY_MAX2 })).default(1),
  userId: z28.string().max(ID_MAX).default(""),
  startTime: z28.string().trim().refine(
    (value) => value === "" || TIME_PATTERN3.test(value),
    rule("timeFormat")
  ).default(""),
  endTime: z28.string().trim().refine(
    (value) => value === "" || TIME_PATTERN3.test(value),
    rule("timeFormat")
  ).default(""),
  pauseMinutes: z28.number().int().min(0).max(PAUSE_MINUTES_MAX2).default(0),
  note: z28.string().max(DESCRIPTION_MAX).default(""),
  phaseNumber: z28.string().max(PHASE_NUMBER_MAX).default("")
}).superRefine((entry, ctx) => {
  const missing = ["startTime", "endTime"].filter(
    (field) => !entry[field]
  );
  if (missing.length === 0) {
    return;
  }
  if (!timeEntryTimesRequired(entry.entryType) && missing.length === 2) {
    return;
  }
  for (const path of missing) {
    ctx.addIssue({
      code: "custom",
      message: rule("required"),
      path: [path]
    });
  }
}).refine(
  (entry) => {
    const start = timeToMinutes2(entry.startTime);
    const end = timeToMinutes2(entry.endTime);
    return start === null || end === null || end > start;
  },
  { message: rule("endBeforeStart"), path: ["endTime"] }
).refine(
  (entry) => {
    const start = timeToMinutes2(entry.startTime);
    const end = timeToMinutes2(entry.endTime);
    if (start === null || end === null || end <= start) {
      return true;
    }
    return entry.pauseMinutes < end - start;
  },
  { message: rule("pauseExceedsWork"), path: ["pauseMinutes"] }
);
var timeEntryEntity = defineEntity({
  name: "timeEntry",
  apiBase: "/api/time-entries",
  collection: "timeEntries",
  rxSchema: timeEntrySchema,
  migrations: TIME_ENTRY_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    // Fixed when the row is created (which Zeiterfassung it belongs to), so
    // it never merges — like media's `kind`.
    entryType: {
      maxLength: ENTRY_TYPE_MAX,
      sanitizeValue: clampTimeEntryType
    },
    year: { kind: "number", merge: true, sanitizeValue: clampYear },
    week: { kind: "number", merge: true, sanitizeValue: clampWeek },
    date: { kind: "date", merge: true },
    workerName: { maxLength: WORKER_NAME_MAX2, merge: true },
    quantity: { kind: "number", merge: true, sanitizeValue: clampQuantity },
    userId: { kind: "ref", maxLength: ID_MAX, merge: true },
    startTime: {
      maxLength: TIME_MAX3,
      merge: true,
      sanitizeValue: clampTime2
    },
    endTime: { maxLength: TIME_MAX3, merge: true, sanitizeValue: clampTime2 },
    pauseMinutes: {
      kind: "number",
      merge: true,
      sanitizeValue: clampPauseMinutes
    },
    note: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    phaseNumber: { maxLength: PHASE_NUMBER_MAX, merge: true }
  },
  inputSchema: timeEntryInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      entryType: clampTimeEntryType(row.entryType),
      year: clampYear(row.year),
      week: clampWeek(row.week),
      date: dateToWire2(row.date),
      workerName: row.workerName || "",
      quantity: clampQuantity(row.quantity),
      userId: row.userId || "",
      startTime: timeToWire(row.startTime),
      endTime: timeToWire(row.endTime),
      pauseMinutes: clampPauseMinutes(row.pauseMinutes),
      note: row.note || "",
      phaseNumber: row.phaseNumber || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      entryType: clampTimeEntryType(doc.entryType),
      year: clampYear(doc.year),
      week: clampWeek(doc.week),
      date: dateToContent2(doc.date),
      workerName: doc.workerName || "",
      quantity: clampQuantity(doc.quantity),
      userId: doc.userId || "",
      startTime: timeToContent(doc.startTime),
      endTime: timeToContent(doc.endTime),
      pauseMinutes: clampPauseMinutes(doc.pauseMinutes),
      note: doc.note || "",
      phaseNumber: doc.phaseNumber || ""
    };
  }
});
function normalizedWorkerName(name) {
  return (name || "").trim().toLowerCase();
}
function sameTimeEntryPerson(first, second) {
  const userA = (first.userId || "").trim();
  const userB = (second.userId || "").trim();
  if (userA && userB) {
    return userA === userB;
  }
  const nameA = normalizedWorkerName(first.workerName);
  return nameA !== "" && nameA === normalizedWorkerName(second.workerName);
}
function timeEntryPersonResolver(entries) {
  const accountByName = /* @__PURE__ */ new Map();
  for (const entry of entries) {
    const userId = (entry.userId || "").trim();
    const name = normalizedWorkerName(entry.workerName);
    if (!userId || !name) {
      continue;
    }
    const known = accountByName.get(name);
    if (known === void 0) {
      accountByName.set(name, userId);
      continue;
    }
    if (known !== userId) {
      accountByName.set(name, "");
    }
  }
  return (entry) => {
    const userId = (entry.userId || "").trim();
    if (userId) {
      return `u:${userId}`;
    }
    const name = normalizedWorkerName(entry.workerName);
    if (!name) {
      return "";
    }
    const account = accountByName.get(name);
    return account ? `u:${account}` : `n:${name}`;
  };
}
function timeEntriesOverlap(first, second) {
  if (!first.date || first.date !== second.date) {
    return false;
  }
  if (clampTimeEntryType(first.entryType) !== clampTimeEntryType(second.entryType)) {
    return false;
  }
  if (!sameTimeEntryPerson(first, second)) {
    return false;
  }
  const aStart = timeToMinutes2(first.startTime);
  const aEnd = timeToMinutes2(first.endTime);
  const bStart = timeToMinutes2(second.startTime);
  const bEnd = timeToMinutes2(second.endTime);
  if (aStart === null || aEnd === null || bStart === null || bEnd === null || aEnd <= aStart || bEnd <= bStart) {
    return false;
  }
  return aStart < bEnd && bStart < aEnd;
}
function findTimeEntryOverlaps(candidate, others) {
  return others.filter(
    (other) => other.id !== candidate.id && !other._deleted && timeEntriesOverlap(candidate, other)
  );
}
var MAX_WORK_MINUTES = 10 * 60;
var MIN_REST_MINUTES = 11 * 60;
function requiredBreakMinutes(netWorkMinutes) {
  if (netWorkMinutes > 9 * 60) {
    return 45;
  }
  if (netWorkMinutes > 6 * 60) {
    return 30;
  }
  return 0;
}
function epochDay(date) {
  if (!DAY_PATTERN.test(date)) {
    return null;
  }
  const [year, month, day] = date.split("-").map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / 864e5);
}
function computeTimeEntryWarningDetails(entries) {
  const flag = /* @__PURE__ */ new Map();
  const add = (id, code, entryIds) => {
    const list = flag.get(id);
    if (!list) {
      flag.set(id, [{ code, entryIds: [...entryIds] }]);
      return;
    }
    const existing = list.find((detail) => detail.code === code);
    if (!existing) {
      list.push({ code, entryIds: [...entryIds] });
      return;
    }
    for (const entryId of entryIds) {
      if (!existing.entryIds.includes(entryId)) {
        existing.entryIds.push(entryId);
      }
    }
  };
  const byPerson = /* @__PURE__ */ new Map();
  const personKey = timeEntryPersonResolver(entries);
  for (const entry of entries) {
    if (entry._deleted || clampTimeEntryType(entry.entryType) !== "worker") {
      continue;
    }
    const key = personKey(entry);
    const day = epochDay(entry.date);
    const start = timeToMinutes2(entry.startTime);
    const end = timeToMinutes2(entry.endTime);
    if (!key || day === null || start === null || end === null || end <= start) {
      continue;
    }
    let days = byPerson.get(key);
    if (!days) {
      days = /* @__PURE__ */ new Map();
      byPerson.set(key, days);
    }
    const existing = days.get(day);
    const pause = Math.max(0, entry.pauseMinutes || 0);
    if (existing) {
      existing.start = Math.min(existing.start, start);
      existing.end = Math.max(existing.end, end);
      existing.span += end - start;
      existing.pause += pause;
      existing.ids.push(entry.id);
    } else {
      days.set(day, {
        day,
        start,
        end,
        span: end - start,
        pause,
        ids: [entry.id]
      });
    }
  }
  for (const days of byPerson.values()) {
    const sorted = [...days.values()].sort(
      (firstDay, secondDay) => firstDay.day - secondDay.day
    );
    for (const workerDay of sorted) {
      const netWork = workerDay.span - workerDay.pause;
      if (netWork > MAX_WORK_MINUTES) {
        for (const id of workerDay.ids) {
          add(id, "longDay", workerDay.ids);
        }
      }
      const required = requiredBreakMinutes(netWork);
      if (required > 0 && workerDay.pause < required) {
        const code = required >= 45 ? "shortBreak45" : "shortBreak30";
        for (const id of workerDay.ids) {
          add(id, code, workerDay.ids);
        }
      }
    }
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const cur = sorted[i];
      const rest = cur.day * 1440 + cur.start - (prev.day * 1440 + prev.end);
      if (rest < MIN_REST_MINUTES) {
        const both = [...prev.ids, ...cur.ids];
        for (const id of both) {
          add(id, "shortRest", both);
        }
      }
    }
  }
  return flag;
}
function computeTimeEntryWarnings(entries) {
  const codes = /* @__PURE__ */ new Map();
  for (const [id, details] of computeTimeEntryWarningDetails(entries)) {
    codes.set(
      id,
      details.map((detail) => detail.code)
    );
  }
  return codes;
}

// src/entities/photo.ts
import { z as z29 } from "zod";
var NAME_MAX3 = 255;
var VOCAB_MAX2 = 100;
var DIRECTION_MAX2 = 30;
var MOTIF_TYPE_OPTIONS = [
  "Befund\xFCbersicht",
  "Detailaufnahme",
  "Arbeitsfoto/Sonst",
  "Planum",
  "Profil",
  "Fund",
  "Gesamt\xFCbersicht"
];
var VIEW_DIRECTION_OPTIONS = [
  "N",
  "NNO",
  "NNW",
  "NO",
  "NW",
  "O",
  "ONO",
  "OSO",
  "S",
  "SO",
  "SSO",
  "SSW",
  "SW",
  "W",
  "WNW",
  "WSW",
  "unbekannt",
  "von oben",
  "von unten"
];
var PHOTO_SCHEMA_VERSION = 4;
var photoSchema = {
  version: PHOTO_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    fileName: { type: "string", maxLength: NAME_MAX3 },
    motifType: { type: "string", maxLength: VOCAB_MAX2 },
    area: { type: "string", maxLength: SPATIAL_MAX },
    trench: { type: "string", maxLength: SPATIAL_MAX },
    planum: { type: "string", maxLength: SPATIAL_MAX },
    profile: { type: "string", maxLength: SPATIAL_MAX },
    planumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    profileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    findingIds: {
      type: "array",
      ref: "findings",
      items: { type: "string", maxLength: 36 }
    },
    viewDirection: { type: "string", maxLength: DIRECTION_MAX2 },
    motif: { type: "string" },
    photographer: { type: "string", maxLength: PERSON_MAX },
    photographerId: { type: "string", maxLength: 36 },
    remarks: { type: "string" },
    publishable: { type: "boolean", default: false },
    findSlipNumbers: {
      type: "array",
      items: { type: "string", maxLength: NUMBER_MAX }
    },
    filmNumber: { type: "string", maxLength: DIRECTION_MAX2 },
    imageNumber: { type: "string", maxLength: DIRECTION_MAX2 },
    recordedAt: { type: "string", maxLength: RECORDED_AT_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "fileName",
    "motifType",
    "area",
    "trench",
    "planum",
    "profile",
    "planumIds",
    "profileIds",
    "findingIds",
    "viewDirection",
    "motif",
    "photographer",
    "photographerId",
    "remarks",
    "publishable",
    "findSlipNumbers",
    "filmNumber",
    "imageNumber",
    "recordedAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId"]
};
var photoInputSchema = z29.object({
  fileName: z29.string().trim().min(1, rule("required")).max(NAME_MAX3, rule("maxLength", { max: NAME_MAX3 })),
  // The Aufnahme fields are required: Dateiname, Motivtyp, Fotograf,
  // Aufnahmedatum (recordedAt is always set).
  motifType: z29.string().trim().min(1, rule("required")).max(VOCAB_MAX2, rule("maxLength", { max: VOCAB_MAX2 })),
  area: z29.string().max(SPATIAL_MAX).default(""),
  trench: z29.string().max(SPATIAL_MAX).default(""),
  planum: z29.string().max(SPATIAL_MAX).default(""),
  profile: z29.string().max(SPATIAL_MAX).default(""),
  planumIds: z29.array(z29.string().max(ID_MAX)).default([]),
  profileIds: z29.array(z29.string().max(ID_MAX)).default([]),
  findingIds: z29.array(z29.string().max(ID_MAX)).default([]),
  viewDirection: z29.string().max(DIRECTION_MAX2).default(""),
  motif: z29.string().max(DESCRIPTION_MAX).default(""),
  photographer: z29.string().trim().min(1, rule("required")).max(PERSON_MAX, rule("maxLength", { max: PERSON_MAX })),
  photographerId: z29.string().max(ID_MAX).default(""),
  remarks: z29.string().max(DESCRIPTION_MAX).default(""),
  publishable: z29.boolean().default(false),
  findSlipNumbers: z29.array(z29.string().max(NUMBER_MAX)).default([]),
  filmNumber: z29.string().max(DIRECTION_MAX2).default(""),
  imageNumber: z29.string().max(DIRECTION_MAX2).default(""),
  recordedAt: recordedAtField()
});
var photoEntity = defineEntity({
  name: "photo",
  apiBase: "/api/photos",
  collection: "photos",
  rxSchema: photoSchema,
  migrations: PHOTO_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    fileName: { maxLength: NAME_MAX3, merge: true },
    motifType: { maxLength: VOCAB_MAX2, merge: true },
    area: { maxLength: SPATIAL_MAX, merge: true },
    trench: { maxLength: SPATIAL_MAX, merge: true },
    planum: { maxLength: SPATIAL_MAX, merge: true },
    profile: { maxLength: SPATIAL_MAX, merge: true },
    // Depicted-description / Befund id sets — string-array merge
    // (set-union/dedupe).
    planumIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    profileIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    findingIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    viewDirection: { maxLength: DIRECTION_MAX2, merge: true },
    motif: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    photographer: { maxLength: PERSON_MAX, merge: true },
    photographerId: { kind: "ref", maxLength: ID_MAX, merge: true },
    remarks: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    publishable: { kind: "boolean", merge: true },
    // Fundzettelnummern — string-array merge (set-union/dedupe).
    findSlipNumbers: {
      kind: "array",
      itemMaxLength: NUMBER_MAX,
      merge: true
    },
    filmNumber: { maxLength: DIRECTION_MAX2, merge: true },
    imageNumber: { maxLength: DIRECTION_MAX2, merge: true },
    recordedAt: { kind: "datetime", merge: true }
  },
  inputSchema: photoInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      fileName: row.fileName || "",
      motifType: row.motifType || "",
      area: row.area || "",
      trench: row.trench || "",
      planum: row.planum || "",
      profile: row.profile || "",
      planumIds: parseStringArray(row.planumIds),
      profileIds: parseStringArray(row.profileIds),
      findingIds: parseStringArray(row.findingIds),
      viewDirection: row.viewDirection || "",
      motif: row.motif || "",
      photographer: row.photographer || "",
      photographerId: row.photographerId || "",
      remarks: row.remarks || "",
      publishable: row.publishable == null ? false : Boolean(row.publishable),
      findSlipNumbers: parseStringArray(row.findSlipNumbers),
      filmNumber: row.filmNumber || "",
      imageNumber: row.imageNumber || "",
      recordedAt: row.recordedAt || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      fileName: doc.fileName || "",
      motifType: doc.motifType || "",
      area: doc.area || "",
      trench: doc.trench || "",
      planum: doc.planum || "",
      profile: doc.profile || "",
      // The depicted-description / Befund id sets persist as JSON-string
      // TEXT columns.
      planumIds: JSON.stringify(doc.planumIds || []),
      profileIds: JSON.stringify(doc.profileIds || []),
      findingIds: JSON.stringify(doc.findingIds || []),
      viewDirection: doc.viewDirection || "",
      motif: doc.motif || "",
      photographer: doc.photographer || "",
      photographerId: doc.photographerId || "",
      remarks: doc.remarks || "",
      publishable: doc.publishable,
      findSlipNumbers: JSON.stringify(doc.findSlipNumbers || []),
      filmNumber: doc.filmNumber || "",
      imageNumber: doc.imageNumber || "",
      recordedAt: doc.recordedAt || ""
    };
  }
});

// src/entities/drawingSheet.ts
import { z as z30 } from "zod";
var NAME_MAX4 = 255;
var VOCAB_MAX3 = 100;
var FORMAT_MAX = 30;
var PLAN_TYPE_OPTIONS = [
  "Befundplan",
  "Befund\xFCbersicht",
  "Blattschnittplan",
  "Phasenplan",
  "Schematischer Plan",
  "Technischer Gesamtplan",
  "Topografische \xDCbersicht",
  "Vermessungsplan",
  "\xDCbersicht der Fl\xE4chen, Schnitte und Hauptprofile",
  "\xDCbersicht der Teilma\xDFnahmen"
];
var SHEET_FORMAT_OPTIONS = ["A0", "A1", "A2", "A3", "A4"];
var SCALE_PATTERN = /^M 1:\d+$/;
var SCALE_DENOMINATOR_OPTIONS = [
  "10",
  "20",
  "25",
  "50",
  "100"
];
function parseScaleDenominator(value) {
  const text3 = value == null ? "" : String(value);
  const tail = text3.slice(text3.lastIndexOf(":") + 1);
  return tail.replace(/\D/g, "").replace(/^0+/, "");
}
function formatScale(value) {
  const denominator = parseScaleDenominator(value);
  return denominator ? `M 1:${denominator}` : "";
}
var DRAWING_SHEET_SCHEMA_VERSION = 5;
function nameArray(value) {
  const items = value == null ? [] : Array.isArray(value) ? value : [value];
  return items.flat(Infinity).map((item) => typeof item === "string" ? item : String(item ?? "")).map((item) => item.trim()).filter((item) => item !== "");
}
var drawingSheetSchema = {
  version: DRAWING_SHEET_SCHEMA_VERSION,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 36 },
    projectId: { type: "string", maxLength: 36 },
    drawingSheetNumber: { type: "string", maxLength: NUMBER_MAX },
    fileName: { type: "string", maxLength: NAME_MAX4 },
    planTypes: {
      type: "array",
      items: { type: "string", maxLength: VOCAB_MAX3 }
    },
    // The specific descriptions this sheet shows (ref -> the sub-entities),
    // plus whole Befunde with no description yet (ref -> findings).
    planumIds: {
      type: "array",
      ref: "finding_planums",
      items: { type: "string", maxLength: 36 }
    },
    profileIds: {
      type: "array",
      ref: "finding_profiles",
      items: { type: "string", maxLength: 36 }
    },
    findingIds: {
      type: "array",
      ref: "findings",
      items: { type: "string", maxLength: 36 }
    },
    area: { type: "string", maxLength: SPATIAL_MAX },
    trench: { type: "string", maxLength: SPATIAL_MAX },
    planum: { type: "string", maxLength: SPATIAL_MAX },
    profile: { type: "string", maxLength: SPATIAL_MAX },
    sheetFormat: { type: "string", maxLength: FORMAT_MAX },
    scale: { type: "string", maxLength: NUMBER_MAX },
    period: { type: "string", maxLength: PERSON_MAX },
    drafter: {
      type: "array",
      items: { type: "string", maxLength: PERSON_MAX }
    },
    drafterId: {
      type: "array",
      ref: "users",
      items: { type: "string", maxLength: 36 }
    },
    remark: { type: "string" },
    recordedAt: { type: "string", maxLength: RECORDED_AT_MAX },
    createdBy: { type: "string" },
    updatedBy: { type: "string" },
    updatedAt: { type: "string", maxLength: 24 },
    createdAt: { type: "string", maxLength: 24 }
  },
  required: [
    "id",
    "projectId",
    "drawingSheetNumber",
    "fileName",
    "planTypes",
    "planumIds",
    "profileIds",
    "findingIds",
    "area",
    "trench",
    "planum",
    "profile",
    "sheetFormat",
    "scale",
    "period",
    "drafter",
    "drafterId",
    "remark",
    "recordedAt",
    "createdBy",
    "updatedBy",
    "updatedAt",
    "createdAt"
  ],
  indexes: [["updatedAt", "id"], "projectId"]
};
var drawingSheetInputSchema = z30.object({
  drawingSheetNumber: z30.string().trim().min(1, rule("required")).max(NUMBER_MAX, rule("maxLength", { max: NUMBER_MAX })),
  /**
   * Every field except Zeitraum (period) is required. The Zuordnung (a Befund
   * ref OR a manual Lage) is enforced in the form, since the description refs
   * live outside these form values.
   */
  fileName: z30.string().trim().min(1, rule("required")).max(NAME_MAX4, rule("maxLength", { max: NAME_MAX4 })),
  planTypes: z30.array(z30.string().max(VOCAB_MAX3)).min(1, rule("required")).default([]),
  planumIds: z30.array(z30.string().max(ID_MAX)).default([]),
  profileIds: z30.array(z30.string().max(ID_MAX)).default([]),
  findingIds: z30.array(z30.string().max(ID_MAX)).default([]),
  area: z30.string().max(SPATIAL_MAX).default(""),
  trench: z30.string().max(SPATIAL_MAX).default(""),
  planum: z30.string().max(SPATIAL_MAX).default(""),
  profile: z30.string().max(SPATIAL_MAX).default(""),
  sheetFormat: z30.string().trim().min(1, rule("required")).max(FORMAT_MAX, rule("maxLength", { max: FORMAT_MAX })),
  // Only the canonical `M 1:X` — the form enters X and composes the value,
  // so this bites for hand-edited or imported data, not for typing.
  scale: z30.string().trim().min(1, rule("required")).max(NUMBER_MAX, rule("maxLength", { max: NUMBER_MAX })).regex(SCALE_PATTERN, rule("scaleFormat")),
  period: z30.string().max(PERSON_MAX).default(""),
  drafter: z30.array(z30.string().max(PERSON_MAX)).min(1, rule("required")).default([]),
  drafterId: z30.array(z30.string().max(ID_MAX)).default([]),
  remark: z30.string().max(DESCRIPTION_MAX).default(""),
  recordedAt: recordedAtField()
});
var drawingSheetEntity = defineEntity({
  name: "drawingSheet",
  apiBase: "/api/drawing-sheets",
  collection: "drawing_sheets",
  rxSchema: drawingSheetSchema,
  migrations: DRAWING_SHEET_MIGRATIONS,
  historyAllFields: true,
  fields: {
    projectId: { kind: "ref", maxLength: ID_MAX },
    drawingSheetNumber: { maxLength: NUMBER_MAX, merge: true },
    fileName: { maxLength: NAME_MAX4, merge: true },
    // Plantypen — string-array merge (set-union/dedupe).
    planTypes: { kind: "array", itemMaxLength: VOCAB_MAX3, merge: true },
    planumIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    profileIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    findingIds: { kind: "array", itemMaxLength: ID_MAX, merge: true },
    area: { maxLength: SPATIAL_MAX, merge: true },
    trench: { maxLength: SPATIAL_MAX, merge: true },
    planum: { maxLength: SPATIAL_MAX, merge: true },
    profile: { maxLength: SPATIAL_MAX, merge: true },
    sheetFormat: { maxLength: FORMAT_MAX, merge: true },
    scale: { maxLength: NUMBER_MAX, merge: true },
    period: { maxLength: PERSON_MAX, merge: true },
    /**
     * Both 'list', not 'array': the id at index n belongs to the Zeichner
     * name at index n, so neither half may drop a blank or a repeat.
     */
    drafter: { kind: "list", itemMaxLength: PERSON_MAX, merge: true },
    drafterId: { kind: "list", itemMaxLength: ID_MAX, merge: true },
    remark: { kind: "text", maxLength: DESCRIPTION_MAX, merge: true },
    recordedAt: { kind: "datetime", merge: true }
  },
  inputSchema: drawingSheetInputSchema,
  toWire(row) {
    return {
      id: row.id,
      projectId: row.projectId || "",
      drawingSheetNumber: row.drawingSheetNumber || "",
      fileName: row.fileName || "",
      planTypes: parseStringArray(row.planTypes),
      planumIds: parseStringArray(row.planumIds),
      profileIds: parseStringArray(row.profileIds),
      findingIds: parseStringArray(row.findingIds),
      area: row.area || "",
      trench: row.trench || "",
      planum: row.planum || "",
      profile: row.profile || "",
      sheetFormat: row.sheetFormat || "",
      scale: row.scale || "",
      period: row.period || "",
      drafter: parseStringArray(row.drafter),
      drafterId: parseStringArray(row.drafterId),
      remark: row.remark || "",
      recordedAt: row.recordedAt || "",
      createdBy: row.createdBy || "",
      updatedBy: row.updatedBy || "",
      updatedAt: row.updatedAt.toISOString(),
      createdAt: row.createdAt.toISOString(),
      _deleted: Boolean(row.deleted)
    };
  },
  wireToContent(doc) {
    return {
      projectId: doc.projectId || "",
      drawingSheetNumber: doc.drawingSheetNumber || "",
      fileName: doc.fileName || "",
      // Arrays persist as JSON-string TEXT columns.
      planTypes: JSON.stringify(doc.planTypes || []),
      planumIds: JSON.stringify(doc.planumIds || []),
      profileIds: JSON.stringify(doc.profileIds || []),
      findingIds: JSON.stringify(doc.findingIds || []),
      area: doc.area || "",
      trench: doc.trench || "",
      planum: doc.planum || "",
      profile: doc.profile || "",
      sheetFormat: doc.sheetFormat || "",
      scale: doc.scale || "",
      period: doc.period || "",
      drafter: JSON.stringify(nameArray(doc.drafter)),
      drafterId: JSON.stringify(nameArray(doc.drafterId)),
      remark: doc.remark || "",
      recordedAt: doc.recordedAt || ""
    };
  }
});

// src/entities/descriptionPhrases.ts
var DESCRIPTION_PHRASES = [
  {
    key: "form",
    label: "Form",
    phrases: [
      "Der Befund hat eine ovale Form.",
      "Der Befund hat eine unregelm\xE4\xDFige Form.",
      "Die Form ist oval.",
      "Die Form ist kreisrund.",
      "Die Form ist langovale.",
      "Die Form ist rechteckig.",
      "Die Form ist trapezf\xF6rmig.",
      "Der Befund ist rundlich bis oval.",
      "Die Form ist nicht eindeutig erkennbar.",
      "Die rechte Ecke ist abgerundet.",
      "Die linke Ecke ist abgerundet.",
      "Der Befund zeigt eine langovale Form.",
      "Der Befund hat eine runde Form."
    ]
  },
  {
    key: "verfuellung",
    label: "Verf\xFCllung",
    phrases: [
      "Die Verf\xFCllung ist homogen.",
      "Die Verf\xFCllung ist heterogen.",
      "Die Verf\xFCllung ist leicht heterogen.",
      "Die Verf\xFCllung ist \xFCberwiegend homogen.",
      "Die Verf\xFCllung ist \xFCberwiegend heterogen.",
      "Die Verf\xFCllung ist \xFCberwiegend heterogen und leicht fleckig.",
      "Die Verf\xFCllung ist dunkelgrau, leicht heterogen.",
      "Die Verf\xFCllung hat eine dunkel graue Verf\xE4rbung.",
      "Die Verf\xFCllung hat eine dunkel grau-braune Verf\xE4rbung.",
      "Die Verf\xFCllung hat eine homogene graue Verf\xE4rbung.",
      "Die Verf\xFCllung hat eine graue Verf\xE4rbung.",
      "Die Verf\xFCllung ist insgesamt dunkel und homogen.",
      "Die Verf\xFCllung ist insgesamt dunkel und homogen, zur Kontur hin w\xE4scht sie nur schwach flie\xDFend aus.",
      "Die Verf\xFCllung ist insgesamt dunkel und homogen, zur Kontur hin w\xE4scht sie kaum aus.",
      "Die Verf\xFCllung aus schluffigem Lehm enth\xE4lt keine sichtbaren Beimengungen.",
      "Verf\xFCllung in zwei Schichten (A, B).",
      "Verf\xFCllung in drei Schichten (A, B, C).",
      "Die Verf\xFCllung zeigt eine mehrphasige Schichtung.",
      "Es handelt sich um eine einphasige Verf\xFCllung."
    ]
  },
  {
    key: "abgrenzung",
    label: "Abgrenzung",
    phrases: [
      "Die Abgrenzbarkeit zum anstehenden Boden ist gut.",
      "Die Abgrenzbarkeit zum anstehenden Boden ist mittel.",
      "Die Abgrenzbarkeit zum anstehenden Boden ist schlecht.",
      "Die Abgrenzbarkeit ist gut und die Kontur verl\xE4uft diffus.",
      "Die Abgrenzbarkeit ist gut und die Kontur verl\xE4uft deutlich.",
      "Die Abgrenzbarkeit ist mittel und die Kontur verl\xE4uft diffus.",
      "Die Abgrenzbarkeit zum anstehenden Boden ist gut und die Kontur verl\xE4uft diffus.",
      "Die Abgrenzbarkeit zum anstehenden Boden ist gut und die Kontur verl\xE4uft deutlich.",
      "Die Abgrenzbarkeit zum anstehenden Boden ist mittel und die Kontur verl\xE4uft diffus.",
      "Die Kontur verl\xE4uft diffus.",
      "Die Kontur verl\xE4uft deutlich.",
      "Der Konturverlauf ist deutlich erkennbar.",
      "Der Konturverlauf ist verwaschen und daher nur diffus erkennbar.",
      "Die Befundgrenzen sind leicht verwaschen.",
      "Die Befundgrenzen sind verwaschen.",
      "Die Befundgrenzen sind stark verwaschen.",
      "Die Befundgrenzen sind etwas verwaschen.",
      "Der Befund ist gut abgrenzbar mit deutlicher Kontur.",
      "Der Befund ist gut abgrenzbar vom anstehenden Boden.",
      "Die Abgrenzung erfolgt aufgrund der Farbe.",
      "Die Abgrenzung erfolgt aufgrund von Materialunterschieden."
    ]
  },
  {
    key: "wandung_profil",
    label: "Wandung/Profil",
    phrases: [
      "Beide Wandungen verlaufen steilschr\xE4g.",
      "Beide Wandungen verlaufen steil-schr\xE4g.",
      "Beide Wandungen verlaufen steil.",
      "Beide Wandungen verlaufen flachschr\xE4g.",
      "Beide Wandungen verlaufen steilschr\xE4g und gehen flie\xDFend in die konvexe Sohle \xFCber.",
      "Beide Wandungen verlaufen steil-schr\xE4g und gehen flie\xDFend in die leicht konvexe Sohle \xFCber.",
      "Beide Wandungen verlaufen steil und gehen flie\xDFend in die konvexe Sohle \xFCber.",
      "Die linke Wandung verl\xE4uft steilschr\xE4g und geht flie\xDFend in die konvexe Sohle \xFCber.",
      "Die linke Wandung verl\xE4uft steil-schr\xE4g und geht flie\xDFend in die konvexe Sohle \xFCber.",
      "Die rechte Wandung verl\xE4uft steilschr\xE4g und geht flie\xDFend in die konvexe Sohle \xFCber.",
      "Die Sohle geht flie\xDFend in die schr\xE4ge rechte Wandung \xFCber.",
      "Die Sohle geht flie\xDFend in die steilschr\xE4ge rechte Wandung \xFCber.",
      "Die Sohle geht flie\xDFend in die steil-schr\xE4ge rechte Wandung \xFCber.",
      "Die Sohle ist konvex gebogen.",
      "Die Sohle ist konvex.",
      "Die Sohle ist leicht konvex.",
      "Die Sohle ist flach.",
      "Schr\xE4ge Wandungen gehen flie\xDFend in eine konvexe Sohle \xFCber.",
      "Flachschr\xE4ge Wandungen gehen flie\xDFend in eine konvexe Sohle \xFCber.",
      "Wannenf\xF6rmiger Befund im Profil.",
      "Muldenf\xF6rmiger Befund im Profil."
    ]
  },
  {
    key: "farbe_beschreibung",
    label: "Farbe",
    phrases: [
      "Der Befund l\xE4sst sich durch seine dunkelbraune Farbe vom anstehenden Boden unterscheiden.",
      "Der Befund l\xE4sst sich durch seine dunkelbraune Farbe gut vom anstehenden Boden unterscheiden.",
      "Der Befund l\xE4sst sich durch seine graubraune Farbe vom anstehenden Boden unterscheiden.",
      "Der Befund l\xE4sst sich durch seine dunkle Verf\xE4rbung gut vom anstehenden Boden unterscheiden.",
      "Befund auf Grund der dunklen Verf\xE4rbung erkennbar.",
      "Befund auf Grund der dunklen Verf\xE4rbung und der Holzkohle erkennbar."
    ]
  },
  {
    key: "einschluesse",
    label: "Einschl\xFCsse",
    phrases: [
      "Die Verf\xFCllung enth\xE4lt Holzkohle (<1%).",
      "Die Verf\xFCllung enth\xE4lt Holzkohle (ca. 1%).",
      "Die Verf\xFCllung enth\xE4lt Steine (<1%).",
      "Die Verf\xFCllung enth\xE4lt Wurzeln (<1%).",
      "Die Verf\xFCllung enth\xE4lt Holzkohle und Steine (<1%).",
      "Die Verf\xFCllung enth\xE4lt zahlreiche Holzkohleflitter.",
      "Die Verf\xFCllung enth\xE4lt vereinzelt Holzkohle.",
      "Die Verf\xFCllung enth\xE4lt vereinzelt Steine.",
      "Die Verf\xFCllung enth\xE4lt keine sichtbaren Beimengungen.",
      "\xDCber den ganzen Befund sind Wurzeln und Holzkohleflitter verteilt.",
      "Die Verf\xFCllung enth\xE4lt Brandlehm und Holzkohle.",
      "Die Verf\xFCllung enth\xE4lt Ziegelfragmente.",
      "Die Verf\xFCllung enth\xE4lt M\xF6rtelreste.",
      "Die Verf\xFCllung enth\xE4lt Keramikfragmente."
    ]
  },
  {
    key: "stratigraphie",
    label: "Stratigraphie",
    phrases: [
      "Der Befund schneidet Befund [Nummer].",
      "Der Befund wird geschnitten von Befund [Nummer].",
      "Keine stratigraphischen Beziehungen erkennbar.",
      "Der Befund ist Teil von Befund [Nummer].",
      "Der Befund liegt \xFCber Befund [Nummer].",
      "Der Befund liegt unter Befund [Nummer].",
      "Stratigraphisch j\xFCnger als Befund [Nummer].",
      "Stratigraphisch \xE4lter als Befund [Nummer].",
      "Der Befund ist in Profil [Nummer] dokumentiert.",
      "In Profil mit Befund [Nummer]."
    ]
  },
  {
    key: "erhaltung",
    label: "Erhaltung",
    phrases: [
      "Der Befund ist vollst\xE4ndig erhalten.",
      "Der Befund ist nur teilweise erhalten.",
      "Der Befund ist unvollst\xE4ndig.",
      "Der Befund wird im Ostteil von einer St\xF6rung unterbrochen.",
      "Der Befund wird von modernen St\xF6rungen beeintr\xE4chtigt.",
      "Der Erhaltungszustand ist gut.",
      "Der Erhaltungszustand ist mittel.",
      "Der Erhaltungszustand ist schlecht."
    ]
  },
  {
    key: "allgemein",
    label: "Allgemein",
    phrases: [
      "Der Befund wurde vollst\xE4ndig dokumentiert.",
      "Die Dokumentation erfolgte fotografisch und zeichnerisch.",
      "Der Befund wurde im Planum freigelegt.",
      "Eine Profilzeichnung wurde angefertigt.",
      "Der Befund wurde vollst\xE4ndig ausgegraben.",
      "Es erfolgte eine Beprobung der Verf\xFCllung.",
      "Der Befund wurde halbiert.",
      "Es wurden Proben entnommen.",
      "Fundleer.",
      "Keine Funde geborgen."
    ]
  },
  {
    key: "konsistenz",
    label: "Konsistenz",
    phrases: [
      "Konsistenz: locker",
      "Konsistenz: plastisch",
      "Konsistenz: dicht",
      "Konsistenz: hart",
      "Konsistenz: locker-plastisch",
      "Konsistenz: locker bis plastisch",
      "Die Konsistenz ist locker.",
      "Die Konsistenz ist plastisch.",
      "Die Konsistenz ist dicht.",
      "Die Konsistenz ist hart."
    ]
  },
  {
    key: "funde",
    label: "Funde",
    phrases: [
      "Im Befund wurden Keramikfragmente gefunden.",
      "Es wurden keine Funde geborgen.",
      "Der Befund war fundleer.",
      "Der Befund enth\xE4lt vereinzelt Tierknochen.",
      "Zahlreiche Ziegelfragmente wurden dokumentiert.",
      "Es fanden sich Eisenn\xE4gel in der Verf\xFCllung.",
      "Wenige unbestimmbare Fragmente wurden geborgen.",
      "Es wurden Glasfragmente entdeckt.",
      "Holzkohlest\xFCcke wurden entnommen.",
      "An Beimengungen sind Keramikfragmente zu erkennen.",
      "An Beimengungen sind Holzkohleflitter und Keramikfragmente zu erkennen.",
      "Beim Anlegen des Profils wurden Keramikfragmente gefunden.",
      "Es wurden Schlackereste gefunden.",
      "Der Befund enth\xE4lt M\xF6rtelreste."
    ]
  },
  {
    key: "proben",
    label: "Proben",
    phrases: [
      "Eine Bodenprobe wurde entnommen.",
      "Es wurden Proben entnommen.",
      "Es erfolgte eine Beprobung der Verf\xFCllung.",
      "Proben wurden zur Analyse entnommen.",
      "Eine Probe wurde f\xFCr weitere Untersuchungen gesichert."
    ]
  },
  {
    key: "stoerungen",
    label: "St\xF6rungen",
    phrases: [
      "Der Befund wird von einer modernen St\xF6rung durchzogen.",
      "Der Befund wird von einer Pflugst\xF6rung durchzogen.",
      "Im NO wird der Befund von einer St\xF6rung geschnitten (Pflug).",
      "Der Befund wird von einer Drainage gest\xF6rt.",
      "Eine moderne St\xF6rung beeintr\xE4chtigt den Befund.",
      "Der Befund enth\xE4lt eine pflugbedingte St\xF6rung.",
      "Pflugbedingte St\xF6rungen sind erkennbar.",
      "Im Zentrum ist eine St\xF6rung durch eine Drainage erkennbar.",
      "Der Befund wird im Ostteil von einer St\xF6rung unterbrochen.",
      "Moderne St\xF6rungen beeintr\xE4chtigen den Befund.",
      "Der Befund ist von Bioturbation betroffen."
    ]
  },
  {
    key: "verfaerbung",
    label: "Verf\xE4rbung",
    phrases: [
      "Der Befund l\xE4sst sich durch seine dunkle Verf\xE4rbung vom anstehenden Boden unterscheiden.",
      "Befund auf Grund der dunklen Verf\xE4rbung erkennbar.",
      "Befund auf Grund der dunklen Verf\xE4rbung und der Holzkohle erkennbar.",
      "Die Verf\xFCllung hat eine schwarze relative homogene Verf\xE4rbung.",
      "Die Verf\xE4rbung ist homogen dunkelgrau-braun.",
      "Die Verf\xFCllung hat eine dunkle grau-braune Verf\xE4rbung.",
      "Die Verf\xFCllung hat eine homogene dunkel graue Verf\xE4rbung.",
      "Die Verf\xFCllung hat eine hellgrau-braune homogene Verf\xE4rbung.",
      "Die Verf\xE4rbung ist heterogen und weist hellere graue Flecken auf.",
      "Die Verf\xE4rbung wird im Westen heller."
    ]
  },
  {
    key: "beimengungen",
    label: "Beimengungen",
    phrases: [
      "\xDCber den ganzen Befund sind Wurzeln und Holzkohleflitter verteilt.",
      "An Beimengungen sind kleine Wurzeln (<1%) zu erkennen.",
      "An Beimengungen sind kleine Wurzeln (<1%) und Holzkohleflitter zu erkennen.",
      "An Beimengungen sind kleine Wurzeln, Steine und Holzkohleflitter zu erkennen.",
      "An Beimengungen sind Holzkohleflitter (1-2cm) zu erkennen.",
      "An Beimengungen sind Schlackereste zu erkennen.",
      "Die Verf\xFCllung enth\xE4lt ca. 1% Holzkohle.",
      "Die Verf\xFCllung enth\xE4lt < 1% Holzkohle.",
      "Die Verf\xFCllung enth\xE4lt vereinzelt Holzkohle.",
      "Die Verf\xFCllung enth\xE4lt zahlreiche Holzkohleflitter.",
      "Einzelne Steine sind eingelagert.",
      "Vereinzelt kleine Steine (< 5 cm)."
    ]
  },
  {
    key: "schichten",
    label: "Schichten",
    phrases: [
      "Der Befund besteht aus zwei Schichten (A und B).",
      "Der Befund besteht aus drei Schichten (A, B und C).",
      "Schicht A liegt \xFCber Schicht B.",
      "Die obere Schicht ist dunkler als die untere.",
      "Die Schichten sind deutlich voneinander abgrenzbar.",
      "Die Schichtgrenzen sind diffus.",
      "Die Schichten gehen flie\xDFend ineinander \xFCber.",
      "Eine klare Schichtung ist erkennbar.",
      "Die Schichtung ist nur schwach ausgepr\xE4gt."
    ]
  },
  {
    key: "interpretation",
    label: "Interpretation",
    phrases: [
      "Der Befund wird als Grube interpretiert.",
      "Der Befund wird als Abfallgrube interpretiert.",
      "Der Befund wird als Vorratgrube interpretiert.",
      "Der Befund wird als Grabgrube interpretiert.",
      "Der Befund wird als Pfostengrube interpretiert.",
      "Der Befund wird als Feuerstelle interpretiert.",
      "Der Befund wird als Herdstelle interpretiert.",
      "Der Befund wird als Graben interpretiert.",
      "Der Befund wird als Grubenhaus interpretiert.",
      "Der Befund wird als Bestattung interpretiert.",
      "Der Befund wird als Siedlungsgrube interpretiert.",
      "Der Befund wird als K\xF6rpergrab interpretiert.",
      "Der Befund wird als Brandgrab interpretiert.",
      "Der Befund k\xF6nnte eine Pfostengrube sein.",
      "Der Befund k\xF6nnte eine Abfallgrube sein.",
      "Eine eindeutige Interpretation ist nicht m\xF6glich.",
      "Die Funktion des Befundes ist unklar.",
      "M\xF6glicherweise handelt es sich um einen nat\xFCrlichen Befund.",
      "Der Befund ist anthropogenen Ursprungs.",
      "Wahrscheinlich handelt es sich um eine Siedlungsgrube.",
      "Der Befund ist als neuzeitliche St\xF6rung zu werten."
    ]
  },
  {
    key: "masse_tiefe",
    label: "Ma\xDFe/Tiefe",
    phrases: [
      "Tiefe: [x] cm.",
      "Erhaltene Tiefe: [x] cm.",
      "Maximale Tiefe: [x] cm.",
      "Durchmesser im Planum: ca. [x] cm.",
      "Ma\xDFe im Planum: [x] \xD7 [y] cm.",
      "Wandungsneigung: ca. [x]\xB0.",
      "Der Befund ist [x] cm tief erhalten.",
      "Die Sohle liegt auf [x] m \xFC. NN.",
      "OK Befund: [x] m \xFC. NN.",
      "UK Befund: [x] m \xFC. NN.",
      "Profiltiefe: [x] cm.",
      "Die erhaltene Tiefe betr\xE4gt [x] cm.",
      "Im Schnitt ist der Befund [x] cm tief.",
      "Der Befund ist flach erhalten (< 10 cm).",
      "Der Befund ist gut erhalten (> 30 cm tief)."
    ]
  },
  {
    key: "bodenart",
    label: "Bodenart",
    phrases: [
      "Bodenart: sandiger Lehm (sL).",
      "Bodenart: lehmiger Sand (lS).",
      "Bodenart: Schluff (U).",
      "Bodenart: toniger Lehm (tL).",
      "Bodenart: sandiger Schluff (sU).",
      "Bodenart: Lehm (L).",
      "Bodenart: Ton (T).",
      "Bodenart: Sand (S).",
      "Bodenart: Kies (G).",
      "K\xF6rnung: fein- bis mittelsandig.",
      "K\xF6rnung: grobsandig mit Kiesanteilen.",
      "K\xF6rnung: schluffig-tonig.",
      "Die Bodenart entspricht dem anstehenden Boden.",
      "Die Bodenart weicht vom anstehenden Boden ab.",
      "Substrat: umgelagerter L\xF6ss.",
      "Substrat: verlehmter Sand.",
      "Substrat: humoser Oberboden."
    ]
  },
  {
    key: "bioturbation",
    label: "Bioturbation",
    phrases: [
      "Der Befund ist von Bioturbation betroffen.",
      "Wurzeln durchziehen die Verf\xFCllung.",
      "Tierg\xE4nge sind erkennbar.",
      "Die Verf\xFCllung ist durch Wurzeln stark gest\xF6rt.",
      "Einzelne Tierg\xE4nge durchziehen den Befund.",
      "Maulwurfg\xE4nge sind erkennbar.",
      "Die Schichtung ist durch Bioturbation teilweise gest\xF6rt.",
      "Wurzelg\xE4nge sind \xFCber den gesamten Befund verteilt.",
      "Die Bioturbation ist gering.",
      "Die Bioturbation ist stark ausgepr\xE4gt."
    ]
  },
  {
    key: "datierung",
    label: "Datierung",
    phrases: [
      "Zeitstellung: unbestimmt.",
      "Zeitstellung: vorgeschichtlich.",
      "Zeitstellung: neolithisch.",
      "Zeitstellung: bronzezeitlich.",
      "Zeitstellung: eisenzeitlich.",
      "Zeitstellung: r\xF6merzeitlich.",
      "Zeitstellung: fr\xFChmittelalterlich.",
      "Zeitstellung: mittelalterlich.",
      "Zeitstellung: neuzeitlich.",
      "Eine Datierung ist anhand der Funde nicht m\xF6glich.",
      "Die Funde deuten auf eine [Periode]-zeitliche Datierung hin.",
      "Der Befund ist aufgrund der Keramik in die [Periode] zu datieren.",
      "Eine genauere Datierung ist nicht m\xF6glich.",
      "Der Befund ist fundleer; eine Datierung ist nicht m\xF6glich.",
      "Relative Datierung: j\xFCnger als Befund [Nummer].",
      "Relative Datierung: \xE4lter als Befund [Nummer]."
    ]
  }
];

// src/entities/index.ts
var entityConfigs = [
  projectEntity,
  parcelEntity,
  userEntity,
  groupEntity,
  projectGroupEntity,
  userProfileEntity,
  findingEntity,
  findingPlanumEntity,
  findingProfileEntity,
  mediaEntity,
  mediaLinkEntity,
  mediaGroupEntity,
  findEntity,
  overallDescriptionEntity,
  areaDescriptionEntity,
  sampleEntity,
  diaryEntity,
  timeEntryEntity,
  photoEntity,
  drawingSheetEntity,
  translationEntity,
  glossaryTermEntity,
  billingCatalogEntity,
  billingPositionEntity,
  billingStatementEntity,
  billingStatementLineEntity
];

// src/befundator.ts
function stringField(blob, key) {
  const value = blob[key];
  if (typeof value === "string") {
    return value.trim();
  }
  return value == null ? "" : String(value).trim();
}
function text(blob, key) {
  const value = stringField(blob, key);
  return /^[-–—]+$/.test(value) ? "" : value;
}
function withFreeText(blob, key, freeKey) {
  return text(blob, key) || text(blob, freeKey);
}
function joinPages(blob, ...keys) {
  return keys.map((key) => text(blob, key)).filter((value) => value !== "").join("\n");
}
function bareFindingNumbers(value) {
  const numbers = value.match(/\d+/g);
  return numbers ? numbers.join(", ") : value;
}
function metresToCentimetres(value) {
  const metres = Number(value.replace(",", "."));
  if (value.trim() === "" || !Number.isFinite(metres)) {
    return value;
  }
  return String(Math.round(metres * 100 * 100) / 100);
}
function inclusionContent(line) {
  const parts = [];
  let depth = 0;
  let current = "";
  for (const character of line) {
    if (character === "(") {
      depth += 1;
    } else if (character === ")") {
      depth = Math.max(0, depth - 1);
    }
    if (character === "," && depth === 0) {
      parts.push(current);
      current = "";
      continue;
    }
    current += character;
  }
  parts.push(current);
  return parts.flatMap((part) => parseInclusions(part.trim()));
}
function boundaryBasis(blob) {
  const parts = [];
  if (blob.aufgrundFarbe === true) {
    parts.push("Farbe");
  }
  if (blob.aufgrundMaterial === true) {
    parts.push("Material");
  }
  const free = text(blob, "aufgrundFreitext");
  if (free !== "") {
    parts.push(free);
  }
  return parts.join(", ");
}
function datumContent(blob, key) {
  if (!(key in blob)) {
    return {};
  }
  const day = stringField(blob, key);
  return { recordedAt: day ? `${day}T00:00:00.000Z` : "" };
}
function northContent(blob) {
  const raw = blob.northAngle;
  if (raw === void 0 || raw === null || String(raw).trim() === "") {
    return {};
  }
  const angle = Number(raw);
  if (!Number.isFinite(angle)) {
    return {};
  }
  return { northAngle: angle, northArrow: true };
}
function blobToFindingContent(blob) {
  return {
    findingNumber: stringField(blob, "befundnr"),
    interpretation: text(blob, "interpretation"),
    remark: joinPages(blob, "bemerkungen1", "bemerkungen2", "bemerkungen3")
  };
}
function spatialContent(blob) {
  const content = {
    area: text(blob, "flaeche"),
    sondage: text(blob, "sondage"),
    overliesOrCuts: bareFindingNumbers(text(blob, "schneidet")),
    underliesOrIsCut: bareFindingNumbers(text(blob, "geschnitten"))
  };
  const parcelId = stringField(blob, "parcelId");
  return parcelId ? { ...content, parcelId } : content;
}
function soilContent(blob) {
  return {
    // befundator edits a single colour; ours is a list, so it is the one
    // entry — or none, when neither half was filled.
    colors: text(blob, "farbeMunsell") || text(blob, "munsellCode") ? [
      {
        color: text(blob, "farbeMunsell"),
        munsellCode: text(blob, "munsellCode")
      }
    ] : [],
    soilType: text(blob, "bodenart"),
    inclusions: inclusionContent(text(blob, "beimengPl")),
    consistency: withFreeText(blob, "gefuege", "gefuegeFreitext"),
    boundaryClarity: withFreeText(blob, "abgrenz", "abgrenzFreitext"),
    boundaryBasis: boundaryBasis(blob),
    contour: withFreeText(blob, "kontur", "konturFreitext"),
    humusContent: withFreeText(blob, "humus", "humusFreitext")
  };
}
function blobToPlanumContent(blob) {
  return {
    ...spatialContent(blob),
    ...soilContent(blob),
    planumNumber: stringField(blob, "plNr"),
    ...datumContent(blob, "datumPl"),
    editor: text(blob, "verfasserPl"),
    orientation: text(blob, "plAus"),
    form: text(blob, "plForm"),
    length: metresToCentimetres(text(blob, "maxL")),
    width: metresToCentimetres(text(blob, "maxBr")),
    diameter: metresToCentimetres(text(blob, "diameter")),
    description: joinPages(blob, "plText1", "plText2", "plText3"),
    ...northContent(blob)
  };
}
function blobToProfileContent(blob) {
  return {
    ...spatialContent(blob),
    profileNumber: stringField(blob, "prNr"),
    ...datumContent(blob, "datumPr"),
    editor: text(blob, "verfasserPr"),
    orientation: text(blob, "prAus"),
    form: text(blob, "prForm"),
    width: metresToCentimetres(text(blob, "maxBrPr")),
    depth: metresToCentimetres(text(blob, "maxT")),
    description: joinPages(blob, "prText1", "prText2", "prText3")
  };
}
function blobToStratigraphyRefs(blob) {
  const numbers = (key) => {
    const value = bareFindingNumbers(text(blob, key));
    return value === "" ? [] : value.match(/\d+/g) ?? [];
  };
  return {
    inProfileWith: numbers("profilmit"),
    partOf: numbers("teilvon")
  };
}
function parseEntryKey(key) {
  const marker = "_Gesamtbeschreibung_";
  const markerAt = key.indexOf(marker);
  if (markerAt !== -1) {
    return {
      befundnr: "",
      plNr: "",
      prNr: "",
      isGesb: true,
      gesbNr: key.slice(markerAt + marker.length)
    };
  }
  const match = key.match(/^(\d+)(?:_Pl(\d+))?(?:_Pr(\d+))?$/);
  if (!match) {
    return { befundnr: key, plNr: "", prNr: "", isGesb: false, gesbNr: "" };
  }
  return {
    befundnr: match[1],
    plNr: match[2] ?? "",
    prNr: match[3] ?? "",
    isGesb: false,
    gesbNr: ""
  };
}

// src/historyLabel.ts
var LABELS = {
  project: { fields: ["measureNumber", "shortTitle"] },
  parcel: { fields: ["parcelNumber", "cadastralDistrict"] },
  finding: { fields: ["findingNumber", "shortDescription"] },
  findingPlanum: { fields: ["planumNumber"] },
  findingProfile: { fields: ["profileNumber"] },
  overallDescription: { fields: ["overallDescriptionNumber", "designation"] },
  areaDescription: { fields: ["area", "planum", "sondage"] },
  find: { fields: ["findSlipNumber", "findBox"] },
  sample: { fields: ["sampleType"], dateField: "recordedAt" },
  photo: { fields: ["fileName"] },
  drawingSheet: { fields: ["drawingSheetNumber", "fileName"] },
  media: { fields: ["caption", "originalName"] },
  diary: { fields: [], dateField: "recordedAt" },
  timeEntry: { fields: ["workerName"], dateField: "date" },
  group: { fields: ["name"] },
  projectGroup: { fields: ["name"] },
  user: { fields: ["username", "displayName"] },
  userProfile: { fields: ["customName"] },
  billingStatement: { fields: ["title"] },
  glossaryTerm: { fields: ["de", "en"] },
  // The legacy free-text Entry, still in the table from before the entities.
  entry: { fields: ["name"] }
};
var HISTORY_LABEL_MAX = 255;
function text2(value) {
  return typeof value === "string" ? value.trim() : "";
}
function fieldText(entityType, field, snapshot) {
  if (entityType === "find" && field === "findSlipNumber") {
    return formatFindSlipNumber({
      findSlipNumber: text2(snapshot.findSlipNumber),
      subFindSlipNumber: text2(snapshot.subFindSlipNumber)
    });
  }
  return text2(snapshot[field]);
}
function historyLabel(entityType, snapshot) {
  const spec = LABELS[entityType];
  if (!spec) {
    return "";
  }
  return spec.fields.map((field) => fieldText(entityType, field, snapshot)).filter(Boolean).slice(0, 2).join(" \xB7 ").slice(0, HISTORY_LABEL_MAX);
}
function historyLabelDate(entityType, snapshot) {
  const field = LABELS[entityType]?.dateField;
  return field ? text2(snapshot[field]).slice(0, 10) : "";
}

// src/migrations/upgrade.ts
function upgradeDocument(strategies, fromVersion, toVersion, doc) {
  let upgraded = { ...doc };
  for (let version = fromVersion + 1; version <= toVersion; version++) {
    const step = strategies[version];
    if (!step) {
      throw new Error(`no migration step to v${version}`);
    }
    upgraded = step(upgraded);
  }
  return upgraded;
}
export {
  APP_ADMIN_ROLE,
  APP_ROLES,
  AREA_DESCRIPTION_SCHEMA_VERSION,
  ARTEFACT_FIND_GROUP,
  BASE_MEASURE_NUMBER_MAX,
  BASE_MEASURE_NUMBER_PATTERN,
  BILLING_CATALOG_SCHEMA_VERSION,
  BILLING_CODE_MAX,
  BILLING_EXPORT_TABLES,
  BILLING_FIELD_LABEL_MAX,
  BILLING_FIELD_TYPES,
  BILLING_FIELD_VALUE_MAX,
  BILLING_LABEL_MAX,
  BILLING_LINE_KINDS,
  BILLING_PERIODS,
  BILLING_POSITION_SCHEMA_VERSION,
  BILLING_POSITION_USES,
  BILLING_STATEMENT_LINE_SCHEMA_VERSION,
  BILLING_STATEMENT_SCHEMA_VERSION,
  BILLING_STATEMENT_STATUSES,
  BILLING_UNITS,
  BOUNDARY_BASIS_OPTIONS,
  BOUNDARY_CLARITY_OPTIONS,
  CIRCULAR_FORM,
  COLOR_OPTIONS,
  CONSISTENCY_OPTIONS,
  CONTOUR_OPTIONS,
  COURSE_MEASURE_FIELDS,
  CREW_KEY_MAX,
  CREW_NAME_MAX,
  CREW_ROLE_MAX,
  CREW_ROLE_PRESETS,
  DATING_BASIS_OPTIONS,
  DEFAULT_EMPLOYEE_TYPE,
  DEFAULT_FINDING_TYPE,
  DEFAULT_MEDIA_KIND,
  DEFAULT_PROJECT_STATUS,
  DEFAULT_TIME_ENTRY_TYPE,
  DESCRIPTION_MAX,
  DESCRIPTION_PHRASES,
  DIARY_SCHEMA_VERSION,
  DRAWING_SHEET_SCHEMA_VERSION,
  EMPLOYEE_TYPES,
  EXCABOOK_SAMPLE_TYPE_PREFIX,
  EXCABOOK_STRAY_FIND_PREFIX,
  EXCAVATION_METHOD_OPTIONS,
  FINDING_PLANUM_SCHEMA_VERSION,
  FINDING_PROFILE_SCHEMA_VERSION,
  FINDING_SCHEMA_VERSION,
  FINDING_TYPE_OPTIONS,
  FIND_GROUP_OPTIONS,
  FIND_LABEL_MAX,
  FIND_SCHEMA_VERSION,
  FIND_TREATMENT_OPTIONS,
  FORM_OPTIONS,
  GLOSSARY_DIRECTIONS,
  GLOSSARY_REMARK_MAX,
  GLOSSARY_TERM_MAX,
  GLOSSARY_TERM_SCHEMA_VERSION,
  GROUP_SCHEMA_VERSION,
  HISTORY_LABEL_MAX,
  HUMUS_CONTENT_OPTIONS,
  ID_MAX,
  INCLUSION_OPTIONS,
  INTERPRETATION_OPTIONS,
  MATERIAL_DETERMINABILITY_OPTIONS,
  MATERIAL_GROUP_OPTIONS,
  MATERIAL_LEVELS,
  MATERIAL_LEVEL_OPTIONS,
  MATERIAL_NAME_OPTIONS,
  MATERIAL_PARENT_MAP,
  MATERIAL_SUBSTANCE_TYPE_OPTIONS,
  MAX_COURSES,
  MAX_WORK_MINUTES,
  MEASURE_VALUE_PATTERN,
  MEDIA_GROUP_MAX_PER_ROW,
  MEDIA_GROUP_MAX_ROWS,
  MEDIA_GROUP_SCHEMA_VERSION,
  MEDIA_KINDS,
  MEDIA_LINK_SCHEMA_VERSION,
  MEDIA_OWNER_TYPES,
  MEDIA_SCHEMA_VERSION,
  MIN_PLAUSIBLE_YEAR,
  MIN_REST_MINUTES,
  MOTIF_TYPE_OPTIONS,
  MUNSELL_CODE_LABELS,
  MUNSELL_CODE_OPTIONS,
  NAME_MAX,
  NO_ORIENTATION,
  ORIENTATION_OPTIONS,
  OVERALL_DESCRIPTION_SCHEMA_VERSION,
  PARCEL_SCHEMA_VERSION,
  PART_LABEL_MAX,
  PERSON_FIELDS,
  PHASE_NAME_MAX,
  PHASE_NUMBER_MAX,
  PHASE_NUMBER_MAX_VALUE,
  PHASE_PRESETS,
  PHASE_SHORT_TITLE_MAX,
  PHOTO_SCHEMA_VERSION,
  PLANUM_PART_OPTIONS,
  PLAN_TYPE_OPTIONS,
  PLAUSIBLE_YEARS_AHEAD,
  POSITION_TO_PLANUM_OPTIONS,
  POSITION_TO_PROFILE_OPTIONS,
  PRELIMINARY_DATING_OPTIONS,
  PRIVILEGED_ROLES,
  PROFILE_FORM_OPTIONS,
  PROFILE_PART_OPTIONS,
  PROJECT_GROUP_SCHEMA_VERSION,
  PROJECT_SCHEMA_VERSION,
  PROJECT_STATUSES,
  QUOTA_LABEL_MAX,
  QUOTA_NUMBER_MAX,
  SAMPLE_FIND_GROUP,
  SAMPLE_SCHEMA_VERSION,
  SAMPLE_TYPE_OPTIONS,
  SAMPLING_METHOD_OPTIONS,
  SCALE_DENOMINATOR_OPTIONS,
  SCALE_PATTERN,
  SHEET_FORMAT_OPTIONS,
  SHORT_DESCRIPTION_OPTIONS,
  SOIL_TYPE_OPTIONS,
  SUB_FIND_SLIP_SEPARATOR,
  SUB_MEASURE_MAX,
  SYSTEM_USER_ID,
  TEXT_MAX,
  TIME_ENTRY_SCHEMA_VERSION,
  TIME_ENTRY_TYPES,
  TRANSLATION_INSTRUCTIONS_MAX,
  TRANSLATION_INSTRUCTION_MAX_LENGTH,
  TRANSLATION_LANGUAGES,
  TRANSLATION_ORIGINS,
  TRANSLATION_OWNER_SUBJECTS,
  TRANSLATION_OWNER_TYPES,
  TRANSLATION_SCHEMA_VERSION,
  USER_PROFILE_SCHEMA_VERSION,
  USER_SCHEMA_VERSION,
  VIEW_DIRECTION_OPTIONS,
  VOICE_FALLBACK_LANGUAGES,
  WEATHER_CONDITION_OPTIONS,
  YES_NO_OPTIONS,
  accountLabel,
  areaDescriptionEntity,
  areaDescriptionInputSchema,
  areaDescriptionSchema,
  bareFindingNumbers,
  billingCatalogEntity,
  billingCatalogInputSchema,
  billingCatalogSchema,
  billingFieldType,
  billingLineKind,
  billingPeriod,
  billingPositionEntity,
  billingPositionInputSchema,
  billingPositionSchema,
  billingStatementEntity,
  billingStatementInputSchema,
  billingStatementLineEntity,
  billingStatementLineInputSchema,
  billingStatementLineSchema,
  billingStatementSchema,
  billingStatementStatus,
  billingUnit,
  billingWeekKey,
  blobToFindingContent,
  blobToPlanumContent,
  blobToProfileContent,
  blobToStratigraphyRefs,
  buildAccountResolver,
  buildTieredAccountResolver,
  clampBillingPrice,
  clampColors,
  clampCourses,
  clampEmployeeType,
  clampExportLayout,
  clampFieldDefinitions,
  clampFieldValues,
  clampFindingType,
  clampInclusions,
  clampPositionUses,
  clampSondagePhotos,
  clampStrata,
  clampTimeEntryType,
  compareBillingCodes,
  compatibleStoredLink,
  composeMeasureNumber,
  computeTimeEntryWarningDetails,
  computeTimeEntryWarnings,
  crewPersonKey,
  defineAbilityFor,
  defineEntity,
  diaryEntity,
  diaryInputSchema,
  diarySchema,
  distinctPeople,
  drawingSheetEntity,
  drawingSheetInputSchema,
  drawingSheetSchema,
  effectivePhaseNumber,
  emptyCourse,
  emptyExportLayout,
  entityConfigs,
  fieldLevelMerge,
  fieldValueOf,
  findEntity,
  findInputSchema,
  findSchema,
  findTimeEntryOverlaps,
  findingEntity,
  findingInputSchema,
  findingPlanumEntity,
  findingPlanumInputSchema,
  findingPlanumSchema,
  findingProfileEntity,
  findingProfileInputSchema,
  findingProfileSchema,
  findingSchema,
  findingTypeChanged,
  fitsShape,
  formatCourses,
  formatFindSlipNumber,
  formatInclusionShare,
  formatInclusions,
  formatQuotaRange,
  formatQuotaRanges,
  formatScale,
  formatStrata,
  fullMeasureNumber,
  glossaryDirection,
  glossaryTermEntity,
  glossaryTermInputSchema,
  glossaryTermSchema,
  groupEntity,
  groupInputSchema,
  groupSchema,
  hasPlausibleYearIfDateTime,
  hasPlausibleYearIfDay,
  historyLabel,
  historyLabelDate,
  isAppAdmin,
  isCircularForm,
  isDelegatableRoleChange,
  isMeasureValue,
  isPhaseNumberInRange,
  isPlainMachineTranslation,
  isPlausibleDateTime,
  isPlausibleDay,
  isPrivilegedRole,
  isRecordNumberAllowed,
  isShortFormOf,
  isStructureFinding,
  isTimeBillingUnit,
  isValidPartLabel,
  joinSampleType,
  joinStrayFindNumber,
  matchableAccount,
  materialOptionGroups,
  mediaEntity,
  mediaGroupEntity,
  mediaGroupInputSchema,
  mediaGroupSchema,
  mediaInputSchema,
  mediaLinkEntity,
  mediaLinkInputSchema,
  mediaLinkSchema,
  mediaSchema,
  metresToCentimetres,
  migrationProblems,
  namesAreCompatible,
  normalizeBillingCode,
  normalizeGlossaryTerm,
  normalizePersonName,
  numberQuotaSchema,
  overallDescriptionEntity,
  overallDescriptionInputSchema,
  overallDescriptionSchema,
  overlapsOtherQuota,
  parcelEntity,
  parcelInputSchema,
  parcelSchema,
  parseBillingWeekKey,
  parseCrewArray,
  parseEntryKey,
  parseInclusionShare,
  parseInclusions,
  parseMediaGroupLayout,
  parsePhaseArray,
  parseQuotaArray,
  parseRecordNumber,
  parseScaleDenominator,
  parseStringArray,
  partLabelField,
  personFieldIds,
  personFieldNames,
  personFieldValue,
  personNameWords,
  phaseEntrySchema,
  phaseForDate,
  photoEntity,
  photoInputSchema,
  photoSchema,
  positionFitsUse,
  projectEntity,
  projectGroupEntity,
  projectGroupInputSchema,
  projectGroupSchema,
  projectInputSchema,
  projectMeasureParts,
  projectSchema,
  quotaCovers,
  quotasOverlap,
  recordMeasureNumber,
  recordMeasureParts,
  relinkPersonFields,
  requiredBreakMinutes,
  resolveAccessibleProjectIds,
  resolveKeepingStored,
  resolveMaterialLevels,
  resolvePersonUserId,
  resolvePersonUserIdAsSelf,
  resolvePersonUserIds,
  resolveWithKept,
  rule,
  sameTimeEntryPerson,
  sampleEntity,
  sampleInputSchema,
  sampleSchema,
  sanitizeCrew,
  sanitizePhases,
  sanitizeQuotas,
  serializeMediaGroupLayout,
  splitMeasureNumber,
  splitSampleType,
  splitStrayFindNumber,
  splitSubFindSlipNumber,
  storedPersonLinkList,
  storedPersonLinks,
  subject,
  timeEntriesOverlap,
  timeEntryEntity,
  timeEntryInputSchema,
  timeEntryPersonResolver,
  timeEntrySchema,
  timeEntryTimesRequired,
  translationEntity,
  translationInputSchema,
  translationSchema,
  translationWriteNeedsOwnerRight,
  uncoveredNumbers,
  upgradeDocument,
  userEntity,
  userInputSchema,
  userProfileEntity,
  userProfileInputSchema,
  userProfileSchema,
  userSchema,
  validateQuotaEndpoint,
  valuesEqual,
  withFieldValue,
  withPersonLabels,
  worksDeToEn,
  worksEnToDe
};
